import { useCallback, useEffect, useRef, useState } from "react";
// ─────────────────────────────────────────────────────────────────────────
// THE IFRAME BOUNDARY — read this before touching rendering, input or TTS.
//
// epub.js renders the book into an IFRAME inside the stage div (viewerRef).
// That iframe's geometry and its event scope are both different from the
// document around it, and on 2026-10-06 three separate user-visible bugs all
// turned out to be the same mistake in different clothes. The rules:
//
// 1. COORDINATES — the STAGE is authoritative, not the iframe.
//    In continuous+paginated mode the iframe is as wide as every column in
//    the section at once (measured: 9216x768 inside a 1024x768 stage), so
//    "is this element on screen" tested against the iframe's own viewport
//    answers YES for the entire chapter. Translate into outer-document
//    coordinates — iframeRect.left + elRect.left — and compare against the
//    stage's rect. Also: doc.defaultView is NULL on some views, and
//    pre-rendered neighbour views report zero rects for everything, so walk
//    the stage's iframes and use the one where the element is laid out.
//    (Bug: read-aloud never turned the page.)
//
// 2. EVENTS — bind on BOTH sides, or accept a dead zone.
//    Touches and keys that start inside the iframe never reach the outer
//    window, and vice versa. A tap on the text is an iframe event; a tap on
//    an edge strip or margin is an outer event. Anything input-driven needs
//    binding in both places. (Bugs: a reveal control that could not be
//    tapped; swipe dead in the 64px bands at each edge.)
//
// 3. HIGHLIGHTS MAY ONLY CHANGE COLOURS.
//    Background, foreground and box-shadow paint outside layout. Anything
//    touching font metrics or box geometry — font-size, font-weight,
//    font-family, letter-spacing, line-height, padding, margin, display —
//    reflows the column and shifts the page under the reader, once per
//    highlighted word. (Bug: "changing the size of the text to highlight it
//    and moving it around".) The rule is restated at both copies of the
//    highlight CSS: src/index.css and services/epubService.ts. Note there
//    are TWO copies and the epubService one is the one injected into the
//    iframe, i.e. the one that actually reaches the text.
//
// 4. AN ABSENCE IS ONLY EVIDENCE ONCE YOU KNOW WHAT IT COVERS.
//    Three times in one day we each concluded something from a missing
//    signal without first checking the signal could have appeared: a
//    user-agent grep that was too narrow, nginx beacons that were never
//    instrumented on the path being used, and an install prompt whose
//    event listener did not exist. Before "it never happened", establish
//    that it COULD have been recorded.
//
// 5. DURING A PANIC ROLLBACK, REVERT THE BUILT BUNDLE — NOT SOURCE FILES.
//    `git checkout -- BookCard.tsx` during an emergency rollback silently
//    destroyed an earlier session's uncommitted "never show 0%" fix, and
//    Thomas had to ask for it a second time. Uncommitted work does not
//    survive a revert, and a revert aimed at one file takes with it
//    anything else living uncommitted in that file. Roll back dist/ and
//    deploy that; leave the working tree alone.
//
// Corollary for all three: verify by measuring in a real epub.js render,
// not by reasoning. Each of these looked obviously correct in source.
// ─────────────────────────────────────────────────────────────────────────
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import type { Book as EpubBook, Rendition, NavItem, Contents } from "epubjs";
import { EpubCFI } from "epubjs";
import {
  getBook,
  getBookBlob,
  getPosition,
  markOpened,
  readLastCfiBySha,
  flushPositionSync,
  savePosition,
} from "@/services/bookStorageService";
import { fetchCloudPosition, sha256Hex } from "@/services/cloudLibraryService";
import { remoteUrl } from "@/services/remoteConfig";
import { applyReaderTheme, openEpub } from "@/services/epubService";
import { waitForStage, type StageWaitResult } from "@/services/layoutService";
import { pageTurnFromKeyEvent } from "@/services/keyService";
import { markOpenFailed, clearOpenFailed } from "@/services/openFailureService";
import { useReaderStore } from "@/store/readerStore";
import {
  buildSegments,
  findNearestSegment,
  wrapWords,
  type WordSpan,
} from "@/services/segmentService";
import {
  addBookmark,
  addHighlight,
  deleteBookmark,
  listHighlights,
  syncHighlightsForBook,
} from "@/services/notesService";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/services/db";
import {
  loadPretranslationForBook,
  buildImportedPretranslation,
} from "@/services/pretranslateService";
import { HIGHLIGHT_COLORS, type HighlightColor, type Bookmark } from "@/types/highlight";
import {
  fadeVolume,
  canReadAloud,
  isTTSSupported,
  listVoices,
  providerByName,
  serverProvider,
  stopAllProviders,
} from "@/services/ttsService";
import { ReaderToolbar, PlayPauseGlyph } from "./ReaderToolbar";
import { SettingsPanel } from "./SettingsPanel";
import { TOCPanel } from "./TOCPanel";
import { NotesPanel } from "./NotesPanel";
import { MarksPanel } from "./MarksPanel";
import { BookInsights } from "./BookInsights";
import { SearchPanel } from "./SearchPanel";
import {
  TranslatePanel,
  fetchTranslation,
  readTranslationCache,
  readTranslationCacheAsync,
  type TxNav,
} from "./TranslatePanel";
import {
  estimateRemaining,
  formatDuration,
  getBookPrefs,
  setBookPrefs,
  startSession,
  tickSession,
} from "@/services/statsService";
import type { TextSegment } from "@/types/tts";

/** How long a book gets to paint its first section before we call the open
 *  failed. Generous: a 55 MB EPUB on a phone legitimately takes a while, and
 *  a false positive here would break a book that was merely slow. */
const OPEN_WATCHDOG_MS = 45000;

// 2026-09-28 — ABANDONED EXPERIMENT, recorded so it isn't retried blind.
//
// Rock* and Future Boy open onto `cover.xhtml` / `xhtml/cover.xhtml`: an
// image-only page with no text. The existing skip only recognises nav/TOC
// filenames, so it never fires and the reader sits on a wordless cover —
// which is what "books wouldn't open" actually is. Verified: both EPUBs are
// valid, un-DRMed, fully downloaded (byte-exact), and epub.js opens both
// cleanly in isolation. A standalone harness running the intended selection
// (walk the spine, pick the first section whose SOURCE has >200 chars)
// picked copyright.xhtml for Rock* and rendered 1721 chars, and
// copyrightnotice.xhtml for Future Boy at 340 chars.
//
// Inside the app it made things WORSE every way it was tried, measured
// against all 19 live books: 14/19 opened instead of 17/19, because probing
// sections with section.load() during boot disturbs the rendition epub.js is
// building (dropping the section.unload() call did not fix it). Three books
// that had always worked went blank. Reverted to the original nav-only skip.
//
// If picked up again: do the prose-scan BEFORE creating the rendition, off a
// throwaway epub.js Book instance, and hand the chosen href to the first
// display() — don't probe a live rendition mid-boot.

// ---------------------------------------------------------------------------
// Newest-wins resume support (2026-09-28).
// ---------------------------------------------------------------------------

/** Normalise a stored timestamp to epoch MILLISECONDS, or null if it can't
 *  be trusted.
 *
 *  THE UNITS GENUINELY DIFFER between tiers and always have:
 *    - cloud   — server writes `int(time.time())`, i.e. epoch SECONDS
 *    - durable — localStorage writes `Date.now()`, i.e. MILLISECONDS
 *  Comparing them raw would make any cloud position look ~1970 and lose
 *  every contest, which would be the old bug with the sign flipped.
 *
 *  1e12 ms is 2001-09-09 and 1e12 seconds is the year 33658, so no real
 *  value is ambiguous: anything below the cut is seconds, anything above
 *  is milliseconds. */
function normaliseStamp(raw: unknown): number | null {
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw <= 0) return null;
  const ms = raw < 1e12 ? raw * 1000 : raw;
  // Reject implausible values rather than letting them dominate forever.
  // A position stamped in the future — a device with a badly wrong clock, or
  // a server/client clock disagreement — would otherwise win every merge on
  // every device permanently, and the user could never read past it. One day
  // of slack absorbs ordinary timezone/NTP skew; beyond that we stop
  // believing the number and treat the entry as untimestamped (see
  // pickNewestPosition — it still counts, it just can't outrank real data).
  const DAY = 86_400_000;
  if (ms > Date.now() + DAY) return null;
  // Before 2001 means it isn't a real reading position timestamp.
  if (ms < 1e12) return null;
  return ms;
}

/** Choose the most recently written position among the available tiers.
 *
 *  MISSING TIMESTAMPS ARE HANDLED EXPLICITLY, not silently either way:
 *  positions written before savedAt/updatedAt existed (and any whose stamp
 *  normaliseStamp rejected) carry `at: null`. Such an entry can never
 *  outrank a timestamped one — it is by definition older than anything
 *  written since timestamping arrived — but it is still used when nothing
 *  timestamped exists, so an old position is never silently discarded and
 *  the user doesn't get thrown back to page one. Among several untimed
 *  candidates we fall back to the historical tier order (cloud, then Dexie,
 *  then localStorage), which is the behaviour those entries were written
 *  under.
 *
 *  Exact ties go to the local tier: if the cloud and this device agree to
 *  the millisecond it's the same write echoing back, and preferring local
 *  avoids a round-trip race nudging the reader backwards. */
function pickNewestPosition<
  T extends { tier: "cloud" | "dexie" | "durable"; cfi: string; at: number | null },
>(candidates: T[]): T | undefined {
  if (!candidates.length) return undefined;
  const timed = candidates.filter((c) => c.at !== null);
  if (timed.length) {
    const tieOrder: Record<string, number> = { dexie: 0, durable: 1, cloud: 2 };
    return timed.reduce((best, c) =>
      (c.at as number) > (best.at as number) ||
      ((c.at as number) === (best.at as number) &&
        tieOrder[c.tier] < tieOrder[best.tier])
        ? c
        : best,
    );
  }
  const legacyOrder: Record<string, number> = { cloud: 0, dexie: 1, durable: 2 };
  return candidates.reduce((best, c) =>
    legacyOrder[c.tier] < legacyOrder[best.tier] ? c : best,
  );
}

// TTS debug toast is developer tooling only (2026-07-10, per Thomas:
// "remove the debug screen" — production UX fails quietly). Enable it by
// loading the app with ?debug=1 in the URL, e.g.
// https://1n2.org/pagevoice/?debug=1#/reader/<id>
const TTS_DEBUG_ENABLED =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("debug");

/** Walk every spine section of an opened epub and pull its block-level
 *  paragraphs, in reading order, deduped. Used to seed a background whole-book
 *  pre-translation for an imported non-English book. Best-effort and defensive:
 *  a section that won't load is simply skipped.
 *
 *  `limit` stops the spine walk early. The pre-translation binder only needs a
 *  few hundred paragraphs to score a candidate artifact, and walking an entire
 *  novel during boot competes with the rendition for the main thread. */
async function extractAllParagraphs(book: EpubBook, limit = Infinity): Promise<string[]> {
  const out: string[] = [];
  const seen = new Set<string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = (book as any).spine?.spineItems ?? [];
  for (const item of items) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc: any = await item.load((book as any).load.bind(book));
      const root: Element | Document | null =
        doc?.querySelectorAll ? doc : doc?.ownerDocument ?? null;
      const nodes = root?.querySelectorAll?.(
        "p, h1, h2, h3, h4, h5, h6, blockquote, li",
      );
      nodes?.forEach((n: Element) => {
        const t = (n.textContent || "").replace(/\s+/g, " ").trim();
        if (t.length >= 2 && /[^\W\d_]/u.test(t) && !seen.has(t)) {
          seen.add(t);
          out.push(t);
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (item as any).unload?.();
    } catch {
      /* skip a section that won't load */
    }
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * ReaderView wires:
 *   - epub.js Book + Rendition into a div
 *   - keyboard / click navigation
 *   - per-chapter segment building (used by TTS)
 *   - TTS playback loop with paragraph highlighting
 *   - position persistence (CFI saved on every relocation)
 */
// ── On-screen gesture debug HUD (enabled only on the /pagevoice-test/ build) ──
const PV_DEBUG =
  typeof location !== "undefined" && /[?&]debug=1/.test(location.search);
function pvDebug(msg: string) {
  if (!PV_DEBUG || typeof document === "undefined") return;
  let hud = document.getElementById("pv-debug-hud");
  if (!hud) {
    hud = document.createElement("div");
    hud.id = "pv-debug-hud";
    hud.style.cssText =
      // 2026-10-07: lifted clear of the bottom bar (bar + caret strip ~175px),
      // so the debug overlay never sits on top of the controls.
      "position:fixed;left:6px;bottom:190px;z-index:2147483647;max-width:74vw;" +
      "font:11px/1.35 ui-monospace,monospace;color:#9effc4;background:rgba(0,0,0,.74);" +
      "padding:6px 8px;border-radius:8px;pointer-events:none;white-space:pre-wrap;";
    document.body.appendChild(hud);
  }
  const t = new Date().toISOString().slice(17, 23);
  hud.textContent = (`${t} ${msg}\n` + hud.textContent).split("\n").slice(0, 9).join("\n");
}
// ── SWIPE PAGING (2026-10-06) ─────────────────────────────────────────────
// Thomas on build 1347: "Page sliding by finger seems off. Only using push
// right side or push left side" — i.e. swipe is unreliable so he has fallen
// back to the edge strips.
//
// The old gate was `dt < 600 && |dx| > 50 && |dx| > |dy| * 1.5`, which is a
// phone gesture. A BOOX reports touch coarsely and with latency, and a
// deliberate drag across a slow panel very easily takes longer than 600 ms —
// so the gesture that FEELS most like turning a page was exactly the one
// being rejected, while a quick flick sometimes got through. That reads as
// "sometimes works, mostly doesn't", which is what he is describing.
//
// Retuned for e-ink:
//   • no upper time limit at all. There is no reason a slow drag is not a
//     swipe; the distance and axis tests already separate it from a tap and
//     from a scroll. This is the single change most likely to fix it.
//   • distance 50 -> 36 px, still far above TAP_SLOP so a tap can't become
//     a page turn by accident.
//   • axis ratio 1.5 -> 1.2, so a slightly diagonal drag (a thumb arcs) is
//     still a swipe.
//   • touchcancel resets the tracker. Without it a cancelled gesture left
//     `tracking` true and the NEXT touchend computed dx from a stale start
//     point — a page turn from nowhere, which is its own kind of "off".
//
// Deliberately NOT added: any finger-following / drag-follow animation. On a
// 16-level greyscale panel a transform that tracks the finger smears and
// stutters; a clean discrete turn on release is better. slidePageTurn() just
// calls rendition.next()/prev() and that stays true.
//
// Direction is fixed: drag left = next, drag right = prev. It does NOT
// honour buttonOrder, matching the arrows and the edge strips above —
// that setting describes the physical upper/lower button pair only.
const SWIPE_MIN_PX = 36;
const SWIPE_AXIS_RATIO = 1.2;
/** Movement beyond this from pointerdown means the gesture was a drag, so an
 *  edge strip must NOT treat the release as a tap. */
const EDGE_TAP_SLOP_PX = 16;

// ── ZERO-HEIGHT STAGE (2026-10-06) ────────────────────────────────────────
// MEASURED, from /__pvdiag/resume2 across 13 real opens. Every open that
// reported a stage with a size resumed correctly (match=1, locD=1, locS=1).
// Every failure looked identical:
//
//   stage@display=0x0/c0x0/f10   stage@settle=0x0/c0x0/f0
//   locD=0  locS=0  range=unknown  match=0
//
// Ten iframes mount into a zero-height box and then collapse to none, and
// currentLocation() returns null outright. Opens 4 and 5 in that sample are
// the SAME book, once healthy and once collapsed, so it is timing, not
// content. This is the ".epub-container present, no iframe" signature that
// reaches Thomas as "won't open books" / "won't save my place" — with no
// location there is nothing to save, and the open is wasted.
//
// WHY IT MEASURES ZERO. The chain is `html,body,#root { height: 100dvh }`
// -> `.pv-fg.relative.flex.h-full.flex-col` -> `.relative.flex-1.min-h-0`
// -> the stage at `absolute inset-0`. Every link takes its height from its
// parent; none has an intrinsic height of its own. So the stage is exactly
// as tall as 100dvh resolves to at that instant, and 100dvh is the DYNAMIC
// viewport — on an Android WebView it is re-resolved as browser chrome
// shows and hides and is not reliably settled when a mount-time effect
// runs. A desktop browser settles it before the effect; a slower device
// need not. That is why this is intermittent and why it shows up on his
// panel and not in a harness.
//
// So: never hand epub.js a zero box. Wait for the stage to measure, with a
// hard cap so a genuinely hidden reader can never hang — on timeout we
// render anyway, which is the existing behaviour, rather than a blank
// screen. Degrade to something, never to nothing.
/** Attach swipe-to-turn to a document or window. Returns a cleanup fn.
 *  Bound to BOTH the epub.js iframe document and the outer window: a swipe
 *  that starts on the text is an iframe touch and never reaches the parent,
 *  while a swipe that starts on an edge strip or a margin is an outer touch
 *  and never reaches the iframe. Binding one alone leaves dead regions, and
 *  touch events do not cross the iframe boundary, so there is no double
 *  fire. */
function attachSwipePaging(
  target: Document | Window,
  getSelectionText: () => string,
  onTurn: (dir: "next" | "prev") => void,
): () => void {
  let sx = 0,
    sy = 0,
    tracking = false;
  const onStart = (e: Event) => {
    const te = e as TouchEvent;
    if (te.touches.length !== 1) {
      tracking = false;
      return;
    }
    sx = te.touches[0].clientX;
    sy = te.touches[0].clientY;
    tracking = true;
  };
  const onCancel = () => {
    tracking = false;
  };
  const onEnd = (e: Event) => {
    const te = e as TouchEvent;
    if (!tracking) return;
    tracking = false;
    const t = te.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - sx;
    const dy = t.clientY - sy;
    // Selecting text is a highlight, not a swipe.
    if (getSelectionText().length > 0) return;
    if (
      Math.abs(dx) > SWIPE_MIN_PX &&
      Math.abs(dx) > Math.abs(dy) * SWIPE_AXIS_RATIO
    ) {
      onTurn(dx < 0 ? "next" : "prev");
    }
  };
  const opts = { passive: true } as AddEventListenerOptions;
  target.addEventListener("touchstart", onStart, opts);
  target.addEventListener("touchend", onEnd, opts);
  target.addEventListener("touchcancel", onCancel, opts);
  return () => {
    target.removeEventListener("touchstart", onStart, opts);
    target.removeEventListener("touchend", onEnd, opts);
    target.removeEventListener("touchcancel", onCancel, opts);
  };
}

export function ReaderView() {
  const { bookId = "" } = useParams();
  const navigate = useNavigate();

  // iOS Safari's left-edge "swipe-back" gesture would pop the reader route and
  // dump the reader back to the library mid-read (reported as "left swipe
  // exits the book"). Absorb an accidental back with a sentinel history entry;
  // the in-app Back button (a push to /library) still exits normally.
  useEffect(() => {
    const stay = () =>
      window.history.pushState(null, "", window.location.href);
    stay();
    window.addEventListener("popstate", stay);
    return () => window.removeEventListener("popstate", stay);
  }, []);

  // Seed the physical page-button order ONCE, based on device. BOOX/Onyx gets
  // the Kindle default (upper→next / lower→prev); everyone else gets the
  // browser standard. After this the user's manual choice persists and we
  // never re-seed — so the reversal is applied exactly once.
  useEffect(() => {
    if (useReaderStore.getState().reader.buttonOrder == null) {
      const boox = /\b(boox|onyx)\b/i.test(navigator.userAgent);
      useReaderStore.getState().setReader({ buttonOrder: boox ? "kindle" : "reversed" });
    }
  }, []);

  const reader = useReaderStore((s) => s.reader);
  const tts = useReaderStore((s) => s.tts);
  const initStore = useReaderStore((s) => s.init);
  // Resolve which TTS provider to use based on settings (auto / server /
  // kokoro / browser). Derived every render; the ref below mirrors it
  // so the play loop's await always speaks through the CURRENT engine
  // even when the user switched mid-session. Without the ref, the
  // useCallback for the loop captured the engine at first render and
  // ignored later switches — which is how the "Piper → Kokoro and now
  // it won't stop" bug came up: pause/stop were hitting Kokoro while
  // the loop was still calling Piper's speakSegment.
  const activeProvider = providerByName(tts.provider);
  const activeProviderRef = useRef(activeProvider);
  // Track the previous provider so we can detect a mid-session engine
  // change and stop the OLD provider's audio explicitly. Without this,
  // switching Piper → Kokoro while a Piper segment is mid-playback
  // leaves the Piper audio element playing until it finishes naturally
  // — and any subsequent pause/stop hits the new Kokoro provider, which
  // can't reach the Piper audio. That was the visible "won't stop"
  // symptom after the engine switch.
  const prevProviderRef = useRef(activeProvider);
  if (prevProviderRef.current !== activeProvider) {
    try {
      prevProviderRef.current.stop();
    } catch {
      /* defensive */
    }
    prevProviderRef.current = activeProvider;
  }
  activeProviderRef.current = activeProvider;

  const viewerRef = useRef<HTMLDivElement | null>(null);
  /** How long the boot had to wait for the stage to measure non-zero, and
   *  whether it gave up. Reported through /__pvdiag/resume2 — this is the
   *  number that says whether the zero-stage fix is working. */
  const stageWaitRef = useRef<StageWaitResult | null>(null);
  /** Count of resize(0,0) calls suppressed by the ResizeObserver zero guard.
   *  If this is > 0 on an open that still succeeded, the guard caught a
   *  collapse that would previously have torn the views down. */
  const zeroResizeCountRef = useRef(0);
  /** Armed only when the boot gave up waiting and rendered into a 0x0 box.
   *  One-shot: the first non-zero resize consumes it. */
  const recoverFromZeroBootRef = useRef(false);
  /** Caps /__pvdiag/turn beacons per mount so a long read can't flood the
   *  nginx log. Reset when a play session starts. */
  const turnDiagCountRef = useRef(0);
  /** Caps /__pvdiag/key beacons per mount. */
  const keyDiagCountRef = useRef(0);
  // ── FULLSCREEN RE-ENTRY (2026-10-06) ───────────────────────────────────
  // Thomas: "On boox it sometimes goes back to show android buttons at the
  // bottom. Would be better if after a few pages it auto sets to full page."
  //
  // The manifest already declares display:"fullscreen" with a
  // display_override chain, so this is NOT a manifest problem and there is
  // no reinstall caveat — something drops out of fullscreen at runtime and
  // the Android navigation bar comes back over the foot of the page.
  //
  // The Fullscreen API needs a user gesture, and a page turn IS one, so
  // re-requesting on a turn is both legal and exactly the hook he suggested.
  //
  // Two refusals to be naive about:
  //  * Do not fight a deliberate exit. If he swiped the bar up on purpose,
  //    snapping back on the very next turn makes the app feel broken. So we
  //    wait for a few turns (his own instinct, and the right shape) before
  //    re-asking, which leaves him a window to do whatever he brought the
  //    bar up for.
  //  * Do not retry forever. Some WebViews refuse the request outright;
  //    firing on every turn for the rest of the session would be pure cost.
  //    Back off after two failures and report it instead of guessing.
  const turnsOutOfFsRef = useRef(0);
  const fsFailuresRef = useRef(0);
  const fsDiagCountRef = useRef(0);
  /** Turns to tolerate out of fullscreen before re-asking. */
  const FS_GRACE_TURNS = 3;
  const FS_MAX_FAILURES = 2;
  /** True once that recovery has actually run — reported in telemetry. */
  const zeroBootRecoveredRef = useRef(false);
  /** The CFI this open asked to resume to, kept so the zero-boot recovery
   *  can re-display it rather than leaving the reader at the wrong place. */
  const bootStartAtRef = useRef<string | undefined>(undefined);
  /** The bottom bar element. Measured so every panel can anchor itself
   *  directly above the bar instead of each one hard-coding a guess. */
  const barRef = useRef<HTMLDivElement | null>(null);
  const bookRef = useRef<EpubBook | null>(null);
  const renditionRef = useRef<Rendition | null>(null);

  const [bookTitle, setBookTitle] = useState<string>("");
  const [chapterLabel, setChapterLabel] = useState<string>("");
  const [toc, setToc] = useState<NavItem[]>([]);
  const [tocOpen, setTocOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Auto-hide bottom bar after a page flip; a small chevron pill at the
  // bottom-centre brings it back. Idea: maximum reading area after the user
  // commits to a page, but never lost — the pill is always discoverable.
  // Bottom bar stays visible all the time now — the user keeps Back +
  // Play down there. barHidden is kept in the source as a constant so the
  // existing setBarHidden(false) callbacks in TOC/Search/etc. compile
  // without rewiring every callsite, but it's never actually toggled on.
  const barHidden = false;
  const setBarHidden = (_: boolean) => { /* no-op — bar stays visible */ };
  const [notesOpen, setNotesOpen] = useState(false);
  // 2026-10-06, "Bookmarks don't have a menu." Bookmarks have been written
  // to Dexie since schema v2 with no way to read them back, and NotesPanel
  // (highlights) had no call site that ever opened it. MarksPanel lists
  // both and is reachable from the bottom bar.
  const [marksOpen, setMarksOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Snapshot of the current visible page's text and start CFI, fed to
  // TranslatePanel as controlled props. Updates each time the rendition
  // relocates (initial display, page flip, TOC jump, font reflow)
  // AND when reader.translateOn flips so the very first frame after
  // turning translation on already has fresh data.
  const [translatePage, setTranslatePage] = useState<{
    text: string;
    cfi: string | undefined;
    paras?: string[];
  }>({ text: "", cfi: undefined });
  // Latest translation produced by TranslatePanel for the current page —
  // surfaced here so the Play button can hand it to TTS verbatim. Keyed
  // by CFI so a stale translation from the previous page can't bleed
  // into the next one.
  const [currentTranslation, setCurrentTranslation] = useState<{
    cfi: string | undefined;
    text: string;
  }>({ cfi: undefined, text: "" });
  // Live mirror of translatePage for the translation read-aloud loop.
  // The loop is a single long-running async function that outlives many
  // page flips — any state captured in its closure freezes at the value
  // it had when Play was pressed, so after the first auto-advance the
  // loop would keep re-reading page 1's translation forever. Reading
  // through this ref always sees the latest relocate. (2026-07-10)
  const translatePageRef = useRef(translatePage);
  translatePageRef.current = translatePage;
  // Caption shown at the bottom of the TranslatePanel, driven by the
  // look-ahead loop below. Examples: "Ready · 3 pages ahead cached" /
  // "Translating page 2 ahead…" / "Caught up to end of section".
  const [aheadStatus, setAheadStatus] = useState<string>("");
  const aheadAbortRef = useRef<AbortController | null>(null);
  // Start in hidden-chrome mode: the top toolbar is gone, the bottom bar
  // carries Back / Theme / Bookmark / Contents / Search / Fonts / Play.
  // Tap the centre of the reading area to bring the title bar back if
  // wanted; the bottom bar is always visible regardless.
  const [chromeVisible, setChromeVisible] = useState(false);
  // 2026-09-29 — "Interface not disappearing."
  //
  // The top toolbar already started hidden and toggled on a centre tap, but
  // the BOTTOM bar was rendered unconditionally ("always visible now"), so
  // the reading chrome never actually got out of the way. There was no idle
  // timer anywhere — hiding was purely a manual toggle.
  //
  // Now both bars retract after a few seconds of no interaction and come
  // straight back on any tap, key or page turn.
  //
  // 2026-10-06 — SUPERSEDED, see barOpen below. This flag now governs only
  // the top running head, and is permanently false: the book opens full
  // screen with the text filling the panel, per "Default to open full
  // screen no button". It is deliberately never set back to true, because
  // doing so changes the flex layout and forces epub.js to repaginate —
  // slow on e-ink, and it re-renders the iframe (which is what was
  // stacking the keydown listeners). The bottom bar is now an OVERLAY
  // driven by barOpen, so showing it costs no reflow at all.
  const [barsVisible] = useState(false);
  // Bottom bar visibility, user-controlled via the two carets and nothing
  // else. Starts closed (full screen). There is NO idle auto-hide and no
  // tap-to-reveal: the only way in or out is an explicit caret press, which
  // is the whole point of the 2026-10-06 BOOX feedback ("Have hide menu
  // carrot and show menu carrot for bottom bar"). Not persisted across
  // opens — "default to open full screen" means every open, not just the
  // first.
  const [barOpen, setBarOpen] = useState(false);
  const idleTimerRef = useRef<number | null>(null);
  // Taps land inside the epub.js iframe, which is a separate document — a
  // window-level listener never sees them. The boot effect wires the
  // iframe's own click handler and calls through this ref so it always hits
  // the current closure.
  const wakeChromeRef = useRef<(() => void) | null>(null);
  // True while we are deliberately repaginating after the chrome's layout
  // footprint changed. epub.js tears down and rebuilds its views during a
  // resize, which fires relocate events at positions that are an artifact of
  // the rebuild, not of the reader moving. Those must not reach
  // savePosition — that file has been damaged twice.
  const suppressPositionSaveRef = useRef(false);
  const [progressPercent, setProgressPercent] = useState(0);
  // Real whole-book text size, measured at import by countBookText and
  // stored on the Book row. Powers the "left in book" / "left to hear"
  // footer. Undefined means we genuinely don't know yet — the footer then
  // shows nothing rather than a made-up figure. (The comment here used to
  // claim these came from epub.js's locations index; in fact nothing ever
  // assigned them, which is why every book showed the same estimate.)
  const [bookWordCount, setBookWordCount] = useState<number | undefined>(
    undefined
  );
  const [bookCharCount, setBookCharCount] = useState<number | undefined>(
    undefined
  );
  const [error, setError] = useState<string | null>(null);
  // CFI of the start of the currently visible page, kept in state so the
  // bookmark button can toggle the bookmark for THIS page (filled/outline
  // state) and not just blind-add a new one each tap.
  const [currentPageCfi, setCurrentPageCfi] = useState<string | undefined>();
  // Deferred auto-translate intent — set during boot from the EPUB's
  // declared metadata.language, consumed once the first relocate fires
  // so the iframe is actually rendered before the overlay can cover it.
  const autoTranslateIntentRef = useRef<"on" | "off" | "skip">("skip");
  // % complete + ETA shows on the first opened page only — after the user
  // flips a page it stays hidden for the rest of the session, so the
  // bottom bar reads as quiet reading chrome instead of a constantly
  // updating dashboard.
  const [hasFlippedPage, setHasFlippedPage] = useState(false);
  // Centred 3-2-1 countdown shown when TTS audio takes >400ms to start
  // (Kokoro cold start is ~3s, Piper warm ~1s, prefetched segments are
  // near-instant). The number disappears the moment the first audio
  // boundary fires, so a fast load just briefly flashes nothing. UX
  // request 2026-06-26: surface the wait visually so it doesn't feel
  // like nothing happened after pressing Play.
  const [countdownNum, setCountdownNum] = useState<number | null>(null);
  const audioStartedRef = useRef(false);
  const countdownIntervalRef = useRef<number | null>(null);
  // Temporary debug strip that flashes when Read-aloud starts. Surfaces
  // the section the rendition is on, the picked segment text, the trim
  // offset, and what the trimmed (about-to-be-spoken) text starts with.
  // Lets us see on-device whether a wrong start is from segment picking,
  // trim-offset math, or something downstream. Will be removed once the
  // start-at-top-of-page bug is closed.
  const [ttsDebugInfo, setTtsDebugInfo] = useState<{
    href?: string;
    pickedTag?: string;
    skipped: number;
    picked: string;
    trim: number;
    spoken: string;
    idx?: number;
    path?: string;
    cfi?: string;
    preSkipIdx?: number;
    preSkipText?: string;
    wrapCount?: number;
    boundaryFired?: boolean;
  } | null>(null);
  // Ref so the play loop can patch the live toast with "wrap: N | bnd: yes/no"
  // without each segment triggering a full re-render of the surrounding tree.
  const ttsDebugInfoRef = useRef<typeof ttsDebugInfo>(null);
  ttsDebugInfoRef.current = ttsDebugInfo;
  // Small transient user-facing notice for quiet TTS failures (replaces the
  // old full-screen error for the "no text on this page" case, 2026-07-10).
  const [ttsNotice, setTtsNotice] = useState<string | null>(null);
  const ttsNoticeTimerRef = useRef<number | null>(null);
  // Counts relocated events so we can distinguish the very first one
  // (which fires from the initial display() during boot) from every
  // subsequent one (which is some kind of navigation — tap, key, font
  // change, TOC jump, anything). The state above flips on event #2.
  const relocateCountRef = useRef(0);

  // ── Session-recap tracking ─────────────────────────────────────────
  //
  // On book open we start a session timestamp and accumulate the text of
  // every page we render. When the user pauses > 5 minutes, closes the
  // tab, or hits "Finish reading", the accumulated text is POSTed to
  // /lib/recap which returns a 3-sentence Gemma summary that we surface
  // in a modal. The modal is styled to match the dark palette and has a
  // Copy button.
  const sessionStartRef = useRef<number>(Date.now());
  const sessionTextRef = useRef<string[]>([]);
  const pageCountRef = useRef<number>(0);
  const lastPausedAtRef = useRef<number | null>(null);
  const recapPostedRef = useRef<boolean>(false);
  const bookShaRef = useRef<string | null>(null);
  const [recapText, setRecapText] = useState<string | null>(null);
  const [recapBusy, setRecapBusy] = useState(false);

  // Live bookmark set for this book. Cheap index lookup; re-renders the
  // bookmark button whenever a bookmark is added or removed so the
  // on/off state stays in sync.
  const bookmarks: Bookmark[] = useLiveQuery(
    () =>
      bookId
        ? db.bookmarks.where("bookId").equals(bookId).toArray()
        : Promise.resolve([] as Bookmark[]),
    [bookId],
    [] as Bookmark[]
  );
  const bookmarkOnThisPage = bookmarks.find(
    (b) => currentPageCfi && b.cfi === currentPageCfi
  );
  const isPageBookmarked = !!bookmarkOnThisPage;

  /** Toggle a bookmark on the page currently displayed. ONE implementation,
   *  shared by the top-right button and the row inside the Marks panel.
   *
   *  Two controls for one action is safe here, and deliberately so: both
   *  read `isPageBookmarked`, which derives from `bookmarks`, which is a
   *  useLiveQuery over Dexie. That is a single reactive source, so the two
   *  cannot disagree about whether this page is bookmarked — acting on
   *  either repaints both. (The Marks panel's own list is separate local
   *  state and only reloads after its own action, so bookmarking from the
   *  corner while that panel happens to be open leaves its list one item
   *  stale until reopened. The panel sits under its own scrim at that
   *  moment, so this is hard to reach and cosmetic when it happens.) */
  const toggleBookmarkOnThisPage = useCallback(async () => {
    if (!bookId) return;
    if (bookmarkOnThisPage) {
      await deleteBookmark(bookmarkOnThisPage.id);
      return;
    }
    const rendition = renditionRef.current;
    const loc = rendition?.currentLocation() as
      | { start?: { cfi?: string; href?: string } }
      | undefined;
    const cfi = loc?.start?.cfi;
    if (!cfi) return;
    const href = loc?.start?.href;
    let chapter: string | undefined;
    if (href) {
      const found = findInToc(toc, href);
      if (found) chapter = found.label.trim();
    }
    await addBookmark({ bookId, cfi, href, chapterLabel: chapter });
  }, [bookId, bookmarkOnThisPage, toc]);

  // How far up from the bottom the page-flip edge strips must stop.
  // Bar open: clear the whole overlay (its 48px caret row + progress +
  // icon row), hence 104px as before. Bar closed: clear only the 56px
  // reveal caret, so the strips reach much further down and the full-
  // screen page keeps nearly its whole edge tappable.
  /** Any bar-hosted panel open. Drives the panel slot inside the bar and,
   *  with it, hiding the progress row. */
  const anyPanelOpen = settingsOpen || searchOpen || marksOpen || tocOpen;

  /** 2026-10-06, Thomas: "Only one button bar open at a time. Opening next
   *  closes last."
   *
   *  One entry point for every panel toggle, so exclusivity cannot drift as
   *  panels are added. Tapping the open panel's own tile closes it.
   *
   *  Closing by opening something else must not leave stale state behind —
   *  a half-typed search, a scroll position, an armed delete confirmation.
   *  Each panel is UNMOUNTED when its flag goes false (they are rendered
   *  behind `&&`), so their internal state is discarded by React rather
   *  than needing to be reset by hand. The one thing that outlives a
   *  remount is the search query, which SearchPanel keeps in its own
   *  useState — also discarded on unmount. Verified by the panels being
   *  conditionally rendered rather than hidden with CSS. */
  const openPanel = useCallback(
    (which: "settings" | "search" | "marks" | "toc" | null) => {
      setSettingsOpen(which === "settings");
      setSearchOpen(which === "search");
      setMarksOpen(which === "marks");
      setTocOpen(which === "toc");
    },
    [],
  );

  const flipZoneBottomInset = barOpen
    ? "calc(env(safe-area-inset-bottom, 0px) + 104px)"
    : "calc(env(safe-area-inset-bottom, 0px) + 60px)";

  // TTS playback state
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const segmentsRef = useRef<TextSegment[]>([]);
  const currentIdxRef = useRef<number>(0);
  // Remembers the trim offset for the FIRST segment of a play session so we
  // can start mid-paragraph at the word visible on the current page rather
  // than the start of the paragraph (which often sits on the previous page).
  const firstSegmentRef = useRef<{
    idx: number;
    segId: string;
    charOffset: number;
  }>({ idx: -1, segId: "", charOffset: 0 });
  const playingRef = useRef(false);
  const pausedRef = useRef(false);
  const contentsRef = useRef<Contents | null>(null);
  // Documents (epub.js section iframes) that already have our page-turn
  // keydown listener. A WeakSet so torn-down iframes are collectable.
  const keyBoundDocsRef = useRef<WeakSet<Document>>(new WeakSet());
  // Resume telemetry (2026-10-06) — see the __pvdiag block after display().
  const resumeTiersRef = useRef<string>("");
  const resumeChoiceRef = useRef<string>("");
  // FIRST-RELOCATE REGRESSION GUARD (2026-10-06).
  //
  // Where we ASKED the resume to put us, resolved to a spine index, captured
  // BEFORE rendition.display() runs — the relocated handler fires *during*
  // display(), so anything set afterwards would be too late to be seen.
  //
  // null means "no intent to compare against": a default open (nothing
  // stored), an explicit bookmark jump (the user's own choice outranks any
  // stored position), or a CFI we could not resolve to a spine index. In
  // every one of those cases the guard stays inert and the save proceeds —
  // same fail-open polarity as the wordless check below.
  const resumeIntentRef = useRef<{
    cfi: string;
    spineIndex: number;
  } | null>(null);
  // Last position we are confident about, kept for the pagehide flush
  // below. Written by the relocated handler on every real page.
  const lastGoodPosRef = useRef<{
    cfi: string;
    href?: string;
    progressPercent?: number;
  } | null>(null);

  // Initialize store (loads settings from IDB)
  useEffect(() => {
    initStore();
  }, [initStore]);

  // Whenever the bookId changes — opening a fresh book — drop any
  // translation state inherited from the previous book BEFORE the new
  // book starts loading. This prevents the "black screen on second
  // book" failure mode hit 2026-06-28: navigating from a
  // German (translateOn=true) book to an English book left the
  // TranslatePanel rendering with the prior book's stale text on top
  // of the new book's loading iframe, and if the new book's
  // metadata.language never resolved, translateOn never flipped off.
  // Reset translateOn to false too — auto-detect will turn it back on
  // (via autoTranslateIntent on the first relocated event) if the new
  // book is actually non-English.
  useEffect(() => {
    setTranslatePage({ text: "", cfi: undefined });
    setCurrentTranslation({ cfi: undefined, text: "" });
    setAheadStatus("");
    aheadAbortRef.current?.abort();
    useReaderStore.getState().setReader({ translateOn: false });
    autoTranslateIntentRef.current = "skip";
    // intentionally NOT a dependency of `reader.translateOn` —
    // we want this to fire ONLY on bookId change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // Boot the epub
  useEffect(() => {
    let cancelled = false;
    let createdObjectUrl: string | undefined;
    // Open watchdog. 2026-09-28: two books (a 40 MB and a 55 MB EPUB) opened
    // to a cover image and a progress bar and then simply stopped — no
    // rejection, so the error screen below never fired and the app looked
    // dead. epub.js was throwing an UNHANDLED TypeError from its own
    // replacements() chain, which nothing here could observe. A book that
    // hasn't painted a single section within the budget is a failed open,
    // whether or not anything rejected. Cleared by the first "rendered".
    let openedOk = false;
    const openWatchdog = window.setTimeout(() => {
      if (cancelled || openedOk) return;
      markOpenFailed(bookId);
      setError(
        "This book didn't finish opening. It may be a large or unusual EPUB.",
      );
    }, OPEN_WATCHDOG_MS);
    async function boot() {
      if (!bookId) return;
      const meta = await getBook(bookId);
      const blob = await getBookBlob(bookId);
      if (!meta || !blob) {
        setError("Book not found.");
        return;
      }
      setBookTitle(meta.title);
      // Real text size, measured at import (or filled in by the library
      // backfill for books that predate it). Left undefined when unknown,
      // which makes the footer render no estimate at all.
      setBookWordCount(meta.wordCount);
      setBookCharCount(meta.charCount);
      // Content SHA for this boot, resolved ONCE and shared. Two independent
      // consumers need it — the pre-translation binder (an artifact for an
      // imported book is named <sha>.en.json) and the durable position-resume
      // below — and they used to compute/read it separately, so whichever ran
      // first on a book with no stored SHA saw `undefined`. Backfills the Dexie
      // row as a side effect, exactly as the resume path always did.
      const shaPromise: Promise<string | undefined> = (async () => {
        if (meta.cloudSha256) return meta.cloudSha256;
        try {
          const h = await sha256Hex(blob);
          await db.books.put({ ...meta, cloudSha256: h });
          return h;
        } catch {
          return undefined; /* sha compute failure — callers degrade gracefully */
        }
      })();
      try {
        const book = await openEpub(blob);
        if (cancelled) {
          book.destroy();
          return;
        }
        bookRef.current = book;
        const navObj = await book.loaded.navigation;
        setToc(navObj.toc ?? []);

        // Auto-translate intent — checked here, applied AFTER the first
        // relocate so the iframe has actually rendered before any
        // overlay covers it. v260627-1740 set this immediately during
        // boot which caused the German-book black-screen bug:
        // translateOn flipped on before the rendition mounted, so the
        // TranslatePanel covered an empty iframe area with no source
        // text to show. autoTranslateIntent is consumed in the first
        // relocate handler (once per book open) and then cleared.
        let autoTranslateIntent: "on" | "off" | "skip" = "skip";
        let bookLang = "";
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const meta = await (book.loaded as any).metadata;
          const rawLang = meta?.language;
          // epub.js sometimes hands metadata.language as an object —
          // pluck the inner string when that happens.
          const declared = String(
            (typeof rawLang === "object" && rawLang
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ? ((rawLang as any).value ?? (rawLang as any)._ ?? "")
              : rawLang ?? "")
          )
            .trim()
            .toLowerCase();
          const isEnglish =
            declared.startsWith("en") || declared === "english";
          bookLang = declared;
          // Default-off when metadata.language is missing: most EPUBs in
          // the wild are English even when they don't say so, and an
          // accidental translation mode that survives from the previous
          // book is the actual harm we're protecting against — an
          // English-book Play failed silently 2026-06-27 because the
          // previous (German) book had left translateOn=true and the
          // current book's silent metadata didn't override it.
          if (isEnglish) {
            autoTranslateIntent = "off";
          } else if (!declared) {
            autoTranslateIntent = "off";
          } else {
            autoTranslateIntent = "on";
          }
        } catch {
          /* metadata.language missing — leave translateOn as-is */
        }
        autoTranslateIntentRef.current = autoTranslateIntent;
        // PRE-TRANSLATION. Load a whole-book English map so a foreign book
        // opens instantly with working audio instead of translating page by
        // page. Classics ship an artifact; imported books get one built in the
        // background on first open. All best-effort — failure just falls back
        // to the live per-page translator.
        void (async () => {
          try {
            const rec = await db.books.get(bookId).catch(() => undefined);
            // Await the SHARED backfill promise rather than reading the Dexie
            // row directly. The row's cloudSha256 is undefined on books
            // imported before we tracked it, and the code that computes it runs
            // further down this same boot on a different async path — so this
            // loader used to lose the race, see `undefined`, skip the sha
            // candidate and give up for the whole session. That's how a book
            // with a complete artifact (QualityLand 2.0) ended up live-
            // translating every page at ~50s. 2026-07-19.
            const sha = await shaPromise;
            const loaded = await loadPretranslationForBook(bookId, rec?.fileName, sha, {
              title: rec?.title ?? meta.title,
              author: rec?.author ?? meta.author,
              // Lazy + capped: only pulled if a candidate artifact actually
              // downloads, and only enough paragraphs to score the binding.
              sampleParas: () => extractAllParagraphs(book, 400),
            });
            // NOTE: do NOT force translateOn here just because a map loaded.
            // Turning translation on puts the translated overlay over the book;
            // if that overlay has no text yet the reader goes BLACK. Auto-enable
            // stays driven by metadata.language via autoTranslateIntent below.
            if (!loaded && autoTranslateIntent === "on") {
              const paras = await extractAllParagraphs(book);
              if (paras.length) {
                void buildImportedPretranslation(bookId, bookLang || "auto", paras);
              }
            }
          } catch {
            /* pre-translation is best-effort */
          }
        })();
        // Build a locations index in the background. epub.js's "relocated"
        // event only includes location.start.percentage once locations have
        // been generated; without this the progress bar stays at 0%.
        // 1024 chars/location is the upstream default and works well.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (book.locations as any)
          .generate?.(1024)
          ?.then?.(() => {
            // Estimate whole-book word count for the "time left" footer.
            try {
              const n = (book.locations as any).length?.();
              if (typeof n === "number" && n > 0) {
                setBookWordCount(Math.round((n * 1024) / 5.5));
              }
            } catch {
              /* estimate stays on the conservative default */
            }
            // ── REPAIR progressPercent (2026-10-07) ────────────────────
            // MEASURED on the server: 11 of 13 stored positions have
            // progressPercent 0.0, including one sitting on chapter5.xhtml
            // at spine 11 — plainly not the start of the book.
            //
            // Why it sticks at zero: the relocated handler reads
            // location.start.percentage, which epub.js only fills in once
            // THIS index has generated. Until then it passes undefined so
            // savePosition's `??` keeps the stored value — and the stored
            // value was already 0, so 0 is preserved forever. The guard
            // that stopped a real figure being overwritten by a premature
            // zero also stopped the real figure ever arriving.
            //
            // The index is ready at exactly this point, so ask it directly
            // what the current CFI is worth and write that. One correction
            // per open; a book repairs itself the first time he opens it.
            try {
              const loc = renditionRef.current?.currentLocation() as
                | { start?: { cfi?: string } }
                | undefined;
              const cfi = loc?.start?.cfi;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const pct = cfi
                ? ((book.locations as any).percentageFromCfi?.(cfi) as
                    | number
                    | undefined)
                : undefined;
              if (
                typeof pct === "number" &&
                Number.isFinite(pct) &&
                pct > 0 &&
                pct <= 1
              ) {
                setProgressPercent(pct);
                savePosition({
                  bookId,
                  cfi,
                  progressPercent: pct,
                  updatedAt: new Date().toISOString(),
                });
              }
            } catch {
              /* best effort — the bar simply stays where it was */
            }
          })
          ?.catch?.(() => {
            /* best effort — bar will just stay at 0% */
          });
        const viewer = viewerRef.current!;
        // Do not hand epub.js a zero box — see the waitForStage block above.
        // This is awaited BEFORE renderTo, not after, because renderTo is
        // what mounts the section views: measuring zero here is what
        // produced stage=0x0/f10 collapsing to f0. Bounded at 3s, and on
        // timeout we proceed exactly as before rather than showing nothing.
        const stageWait = await waitForStage(viewer);
        stageWaitRef.current = stageWait;
        if (stageWait.timedOut) {
          // Arm the recovery: we are about to lay out against a zero box,
          // so the first non-zero measurement must re-display, not just
          // resize. See the ResizeObserver block.
          recoverFromZeroBootRef.current = true;
          // eslint-disable-next-line no-console
          console.warn(
            `[pagevoice] stage never measured (${stageWait.w}x${stageWait.h} after ${stageWait.waitedMs}ms) — rendering anyway, will re-display when it measures`,
          );
        }
        const rendition = book.renderTo(viewer, {
          width: "100%",
          height: "100%",
          flow: "paginated",
          // Single-page rendering at all widths. "auto" gives a two-page
          // spread on desktop, and spread-driven relocations restart the TTS
          // session (countdown re-arms after every paragraph).
          spread: "none",
          // 2026-09-29 — "Tried in safari save to Home Screen but won't open
          // books", and the reading area rendering blank at phone widths.
          //
          // The 2026-08-11 experiment set manager:"continuous" + snap:true for
          // Kindle-style swipe paging. epub.js only instantiates its Snap
          // helper when the device reports touch (snap.js supportsTouch() ->
          // 'ontouchstart' in window), and Snap seizes the stage's scrolling
          // (WebkitOverflowScrolling, scrollLeft, scroll listeners).
          //
          // Measured on the live build against the same book (Alice, 185 KB):
          //   768px, touch=false -> .epub-container present, 1363 chars, fine
          //   375px, touch=true  -> NO .epub-container, no iframe at all, the
          //                         45s open watchdog fires and the user gets
          //                         "This book didn't open."
          // Touch is the only input to this stack that differs, and Snap is
          // the only thing in it gated on touch. A phone is a touch device,
          // which is why this reads as "won't open books" on the Home Screen
          // while the same book opens on a desktop browser.
          //
          // Snap's only benefit is the swipe gesture, on exactly the devices
          // where it breaks rendering, so it goes. The continuous manager is
          // kept (it still pre-renders neighbours, no blank frame mid-turn)
          // and our own edge tap-zones handle paging.
          manager: "continuous",
          snap: false,
          allowScriptedContent: false,
        });
        renditionRef.current = rendition;
        applyReaderTheme(rendition, reader);

        rendition.on("relocated", (location: any) => {
          if (!location) return;
          // Apply the deferred auto-translate intent on the very first
          // relocate of this book open. By now the iframe has actually
          // rendered, so flipping translateOn won't blank the screen.
          if (autoTranslateIntentRef.current === "on") {
            autoTranslateIntentRef.current = "skip";
            useReaderStore.getState().setReader({ translateOn: true });
          } else if (autoTranslateIntentRef.current === "off") {
            autoTranslateIntentRef.current = "skip";
            useReaderStore.getState().setReader({ translateOn: false });
          }
          const cfi = location.start?.cfi;
          const spineIndex = location.start?.index ?? 0;
          const href = location.start?.href;
          // epub.js only fills in location.start.percentage once the
          // locations index has finished generating — see the
          // book.locations.generate(1024) call above, which is async and
          // deliberately backgrounded. Until it resolves, percentage is
          // the NUMBER 0, which is indistinguishable from "genuinely at
          // the start of the book" unless we ask the index itself.
          //
          // VERIFIED LIVE 2026-10-05 against build 260928-2203: reopening
          // a book fires this handler before the index is ready, so pct
          // was 0 and the savePosition() below wrote progressPercent: 0
          // straight over the real stored value. Measured on Alice:
          // 0.1617 -> 0 on every single reload, reproduced 2/2. The CFI
          // itself survived (the reader did land on the right page), but
          // the recorded progress was destroyed each time — and because
          // the footer hides the readout at zero, the app then looked as
          // though it had forgotten the place even though it hadn't.
          //
          // Treat "0 while the index is still building" as UNKNOWN rather
          // than as zero: pass undefined, so savePosition()'s
          // `?? book.progressPercent` keeps whatever is already stored.
          // A real 0 (> 0 check fails but index IS ready) still writes 0.
          const rawPct = location.start?.percentage;
          let locationsReady = false;
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const n = (book.locations as any)?.length?.();
            locationsReady = typeof n === "number" && n > 0;
          } catch {
            /* index not built yet — treat as not ready */
          }
          const pct =
            typeof rawPct === "number" && (rawPct > 0 || locationsReady)
              ? rawPct
              : undefined;
          // Only move the on-screen readout when we actually know the
          // figure. Setting 0 here made the bar flash empty on every
          // reopen before the index landed.
          if (pct !== undefined) setProgressPercent(pct);
          // Capture the new page-start CFI so the bookmark button can flip
          // between "this page IS bookmarked" and "this page is NOT" without
          // duplicating bookmarks every time the user taps.
          if (cfi) setCurrentPageCfi(cfi);
          // First relocated event = initial boot display; from the second
          // onwards we've moved the page (tap, key, TOC jump, font reflow,
          // anything) and we want the % readout to vanish for the rest of
          // the session.
          relocateCountRef.current += 1;
          if (relocateCountRef.current >= 2 && !hasFlippedPage) {
            setHasFlippedPage(true);
          }
          // Session-recap accumulator — grab the visible text on every
          // relocate so we have the whole session's material ready when
          // the user pauses > 5 minutes / closes the tab. Cap the buffer
          // at 32 KB so a long session doesn't balloon memory. Best-
          // effort — if the iframe hasn't rendered yet, skip this tick.
          try {
            const iframe = document.querySelector("iframe");
            const doc = (iframe as HTMLIFrameElement | null)?.contentDocument;
            const t = doc?.body?.innerText?.trim();
            if (t && t.length > 40) {
              sessionTextRef.current.push(t);
              pageCountRef.current += 1;
              // Trim buffer to the last ~32 KB — keep only the tail so
              // the recap covers the most recent reading.
              let total = 0;
              const buf = sessionTextRef.current;
              for (let i = buf.length - 1; i >= 0; i--) {
                total += buf[i].length;
                if (total > 32_000) {
                  buf.splice(0, i + 1);
                  break;
                }
              }
            }
          } catch { /* iframe not ready — next relocate will pick it up */ }
          // Update chapter label from TOC
          if (href) {
            const found = findInToc(navObj.toc ?? [], href);
            if (found) setChapterLabel(found.label.trim());
          }
          // Persist — unless this page has no words on it.
          //
          // WHY (2026-09-28): this handler fires on the FIRST relocate of
          // every open, which for a cover-first book is the wordless cover.
          // savePosition writes through to Dexie, localStorage AND the cloud,
          // so merely opening a book promoted its cover to the highest-
          // priority resume tier. That is how all 20 stored positions in the
          // library came to point at covers and title pages this morning —
          // the genuine positions (Future Boy was on chapter 4) were
          // overwritten by automated opens.
          //
          // POLARITY MATTERS: we suppress the save only when we have
          // positively read the page and found it wordless. If the iframe
          // isn't readable yet (cross-origin, not painted, thrown), textLen
          // is null and we SAVE — a guard that fails closed would silently
          // stop recording progress, which is worse than the bug it fixes.
          let visibleLen: number | null = null;
          try {
            const frame = viewerRef.current?.querySelector("iframe");
            const body = (frame as HTMLIFrameElement | null)?.contentDocument
              ?.body;
            if (body) {
              visibleLen = (body.innerText || "").replace(/\s+/g, " ").trim()
                .length;
            }
          } catch {
            visibleLen = null; // unreadable — fall through and save
          }
          // 250 chars: the same threshold findFirstContentHref uses. Across
          // the 19-book library the wordless plates measured <= 161 chars and
          // the shortest genuine text page 405, so the cut sits in open space
          // rather than near a real page.
          const isWordless = visibleLen !== null && visibleLen < 250;

          // ── FIRST-RELOCATE REGRESSION GUARD (2026-10-06) ──────────────
          //
          // The wordless check above catches covers and plates. It cannot
          // catch an epigraph, a dedication or a title page that has words
          // on it — and that is where the library actually ended up: 10 of
          // 13 stored positions at exactly 0%, Thomas's current book parked
          // at xhtml/epigraph.xhtml. Thirteen books are not all bookmarked
          // in their own front matter by coincidence.
          //
          // The mechanism is a FAILED RESUME, not a failed save: we ask
          // epub.js for a stored CFI, it doesn't resolve, we get dropped
          // near the front of the book, and this handler then writes that
          // wrong place over the right one through all three tiers. One bad
          // open destroys the bookmark permanently, which is what reaches
          // Thomas as "it didn't save my place".
          //
          // So: on the first relocate of an open, if we landed in an
          // EARLIER spine section than the one we asked to resume to, don't
          // write. Compare landed-vs-asked, never landed-vs-history.
          //
          // WHY THIS CANNOT BLOCK LEGITIMATE BACKWARDS NAVIGATION — three
          // independent reasons, any one of which would be sufficient:
          //
          //   1. resumeIntentRef is a strict one-shot. It is set to null and
          //      then armed immediately before the boot display(), and
          //      cleared unconditionally by the first relocate that sees it
          //      (below) — so exactly one relocate per open can ever be
          //      guarded, and it is the one display() itself emits. No view
          //      exists between arming and display(), so no other relocate
          //      can slip into that window. Every later relocate — page
          //      flip, TOC jump, font reflow, back-paging — finds the ref
          //      null and saves through the normal path.
          //
          //      NOT using relocateCountRef here, deliberately: that
          //      counter is never reset per open (it only drives the
          //      one-way hasFlippedPage latch), so on a second book opened
          //      in the same mount it is already > 1. A count-based test
          //      would silently stop guarding after the session's first
          //      open. The intent ref is armed per open and so has no such
          //      failure mode.
          //   2. The comparison is against the resume TARGET, not against
          //      the previously-stored position. Deliberate navigation never
          //      reaches this branch, but even if it did, a successful
          //      resume lands at the target, and target-vs-target is equal,
          //      not earlier. Nor can a newer position from another device
          //      trip it: whichever tier wins IS the target.
          //   3. An explicit bookmark tap (bootCfi) leaves the intent ref
          //      null, so the guard is not armed at all on those opens.
          //
          // Strictly-earlier only: equal or later always saves. A regression
          // within one section (the stored position and the epigraph sharing
          // a spine entry) slips through, as does an unresolvable CFI — both
          // fail open and save, which keeps the existing polarity. This
          // narrows the hole rather than claiming to close it.
          // 2026-10-06 — REFINED for manager:"continuous". The rendition is
          // created with manager:"continuous", which mounts several spine
          // sections into one stage at once. location.start is then the
          // FIRST section with anything on screen, not the section we are
          // "on". Front matter sections are a few lines each, so asking to
          // resume at the epigraph can legitimately report
          // start.index = dedication with the epigraph also visible just
          // below it. Comparing against start.index alone would have fired
          // the guard on a resume that actually worked — a false positive
          // that suppresses a harmless write, but which would also have
          // produced a stream of /__pvdiag/guard hits that looked like
          // confirmation of a bug that wasn't there.
          //
          // So: a regression requires the asked-for section to be absent
          // from the visible range entirely. If intent.spineIndex falls
          // anywhere in [start.index, end.index] the resume landed and we
          // save normally.
          const endSpineIndex: number | undefined =
            typeof location.end?.index === "number"
              ? location.end.index
              : undefined;
          const intent = resumeIntentRef.current;
          // One-shot: spent by the first relocate after arming, fired or not.
          resumeIntentRef.current = null;
          let isResumeRegression = false;
          let regressionDetail = "";
          if (intent && typeof spineIndex === "number") {
            const visibleTo = endSpineIndex ?? spineIndex;
            const askedIsOnScreen =
              intent.spineIndex >= spineIndex &&
              intent.spineIndex <= visibleTo;
            if (!askedIsOnScreen && visibleTo < intent.spineIndex) {
              isResumeRegression = true;
              regressionDetail = `visible ${spineIndex}-${visibleTo} all before asked ${intent.spineIndex}`;
            }
          }

          // Report the regression HERE rather than inside the suppressing
          // branch below. A cover-first book lands on a page that is both
          // wordless and a regression, and the wordless branch comes first
          // — reporting from inside the branch would lose exactly the
          // signal we most want. Every path that sets isResumeRegression
          // ends in a branch that skips the write (repagination, wordless,
          // or the regression branch itself), so an unconditional report
          // here never claims a suppression that didn't happen.
          //
          // Same /__pvdiag/ channel as the resume telemetry: deliberately
          // 404s, lands in nginx's access log with the query string intact.
          // If this fires on every open, the diagnosis is confirmed outright
          // and we don't have to wait on Thomas to reproduce it.
          if (isResumeRegression) {
            try {
              const q = new URLSearchParams({
                b: bookId.slice(0, 12),
                why: "regress",
                detail: regressionDetail,
                from: (intent?.cfi || "unknown").slice(0, 120),
                fromSpine: String(intent?.spineIndex ?? "?"),
                to: (cfi || "unknown").slice(0, 120),
                toSpine: String(spineIndex),
                toHref: (href || "unknown").slice(0, 80),
                pct: pct === undefined ? "unknown" : pct.toFixed(4),
                chose: resumeChoiceRef.current || "none",
                wordless: isWordless ? "1" : "0",
                ua: navigator.userAgent.slice(0, 40),
              });
              void fetch(`/__pvdiag/guard?${q.toString()}`, {
                method: "GET",
                cache: "no-store",
              }).catch(() => {});
            } catch {
              /* diagnostics never break a read */
            }
          }

          if (suppressPositionSaveRef.current) {
            // Mid-repagination after a chrome show/hide. epub.js is tearing
            // down and rebuilding views, and the relocations that produces
            // describe the rebuild, not the reader. Writing them would move
            // the saved position to wherever the rebuild happened to land.
            if (TTS_DEBUG_ENABLED) {
              // eslint-disable-next-line no-console
              console.log(
                `[pagevoice] position not saved — repaginating (${href})`,
              );
            }
          } else if (isWordless) {
            // Not an error — covers and full-page plates are legitimate
            // places to BE, just not to RESUME to. The previous position
            // stands untouched.
            if (TTS_DEBUG_ENABLED) {
              // eslint-disable-next-line no-console
              console.log(
                `[pagevoice] position not saved — wordless page (${visibleLen} chars) at ${href}`,
              );
            }
          } else if (isResumeRegression) {
            // The resume asked for one place and epub.js delivered an
            // earlier one. The stored position stands; the next relocate
            // (any page turn) saves normally, so a false positive here
            // costs one skipped write and nothing else. Already reported
            // through /__pvdiag/guard above.
            if (TTS_DEBUG_ENABLED) {
              // eslint-disable-next-line no-console
              console.log(
                `[pagevoice] position not saved — resume regression (${regressionDetail}) at ${href}`,
              );
            }
          } else {
            savePosition({
              bookId,
              cfi,
              spineIndex,
              href,
              progressPercent: pct,
              updatedAt: new Date().toISOString(),
            });
            // Remember it for the pagehide/visibilitychange flush. Only
            // pages we were willing to SAVE are worth flushing — a
            // wordless cover or a mid-repagination relocate must not
            // become the last-gasp write either.
            if (cfi) {
              lastGoodPosRef.current = { cfi, href, progressPercent: pct };
            }
          }
          // If TTS is playing and the page-flip wasn't driven by us, restart
          // playback from the new visible position.
          if (playingRef.current && !ttsRestartGuardRef.current) {
            restartTTSFromCurrentLocation();
          }
        });

        rendition.on("rendered", (_section: any, view: Contents) => {
          // A section actually painted — the open succeeded. Disarm the
          // watchdog and clear any stale failure marker for this book.
          openedOk = true;
          window.clearTimeout(openWatchdog);
          clearOpenFailed(bookId);
          contentsRef.current = view;
          rebuildSegmentsForCurrentSection();
          // Warm the first segment's audio in the background while the
          // reader is idle, so tapping Play starts near-instantly instead
          // of waiting out a cold synth (2026-07-10 "slow start" note).
          // Server-side caching makes repeats free; skipped while playing
          // and for the browser engine (which has no prefetch).
          if (!playingRef.current) {
            const first = segmentsRef.current[0];
            const t = useReaderStore.getState().tts;
            if (first && t.provider !== "browser") {
              try {
                activeProviderRef.current.prefetch?.(first, {
                  rate: t.rate,
                  pitch: t.pitch,
                  volume: t.volume,
                  serverVoice: t.serverVoice,
                });
              } catch {
                /* warm-up is best-effort */
              }
            }
          }
          // Repaint highlights for this section.
          repaintHighlights();
          // Listen for text selections so we can surface the highlight menu.
          wireSelectionMenu(view);
          // Forward keys typed inside the iframe to the parent. Without this,
          // arrow keys are swallowed by the iframe and never reach the
          // window-level keydown handler at the bottom of this component.
          try {
            const doc = view.document;
            // 2026-10-06: guard against stacking. "rendered" can fire more
            // than once for the same Contents (epub.js re-renders a view on
            // resize, and the chrome-visibility repagination above forces
            // exactly that). Each pass used to add ANOTHER keydown listener
            // to the same document, so one button press turned two or three
            // pages. Mark the document once and never rebind it.
            if (!keyBoundDocsRef.current.has(doc)) {
              keyBoundDocsRef.current.add(doc);
              doc.addEventListener(
                "keydown",
                (e: KeyboardEvent) => {
                  // Same decision function as the outer handler — physical
                  // page buttons honour the button-order setting, arrows are
                  // fixed. preventDefault ONLY when we actually handle it, so
                  // find-in-page, IME and text fields are left alone.
                  const pdir = pageTurnFromKeyEvent(
                    e,
                    useReaderStore.getState().reader.buttonOrder,
                  );
                  // Beacon from the IFRAME side. Which side of the boundary
                  // receives the key is the first thing we need to know:
                  // a key that targets the iframe never reaches the outer
                  // document at all (iframe-boundary rule 2 at the top of
                  // this file), so an outer-only beacon could report
                  // "no keys arrive" while they are arriving in here.
                  if (keyDiagCountRef.current < 30) {
                    keyDiagCountRef.current += 1;
                    try {
                      const q = new URLSearchParams({
                        ev: "key",
                        src: "iframe",
                        key: String(e.key || ""),
                        code: String(e.code || ""),
                        kc: String(e.keyCode || 0),
                        dir: String(pdir || ""),
                        ua: navigator.userAgent.slice(0, 32),
                      });
                      void fetch(`/__pvdiag/key?${q.toString()}`, {
                        method: "GET",
                        cache: "no-store",
                      }).catch(() => {});
                    } catch {
                      /* diagnostics never break a read */
                    }
                  }
                  if (!pdir) return;
                  e.preventDefault();
                  e.stopPropagation();
                  wakeChromeRef.current?.();
                  slidePageTurnRef.current(pdir);
                },
                // Capture phase: some EPUBs ship their own scripts that
                // listen for arrows, and a few swallow them.
                true,
              );
            }
          } catch {
            /* iframe doc may be torn down — best effort */
          }
          // Wire an explicit link-click handler inside the iframe. epub.js's
          // own handleLinks doesn't fire reliably on every build / every
          // EPUB structure (SVG <a>, hrefs on non-anchor elements, etc).
          // Without our handler running, the default browser action takes
          // the iframe to the raw href URL → /pagevoice/chapter1.xhtml →
          // server returns a 404 page rendered inside the iframe. That's
          // the 'TOC link 404' symptom.
          //
          // Capture phase + matches any ancestor with [href] or
          // [xlink:href] (covers SVG too). Hard preventDefault before any
          // routing logic so the worst case is silent no-op, never a 404.
          try {
            const doc = view.document;

            // Bulletproof in-book link routing.
            //
            // Capture-phase preventDefault by itself was still letting some
            // TOC links 404 (iOS sometimes navigates from touchstart before
            // any click handler runs; some EPUBs use inline onclick or
            // target attributes that beat our handler). So we go further:
            // every <a href> / <area href> has its REAL href moved to a
            // data attribute, and the live href is rewritten to
            // "javascript:void(0)". With no real href the browser has
            // nothing to navigate to even if every event handler is
            // bypassed. The delegated click handler reads the data
            // attribute and routes through navigateToHref().
            //
            // A MutationObserver re-defangs links the EPUB hydrates
            // after the initial render.

            const ORIG_HREF = "data-pv-href";
            const defangOne = (a: Element) => {
              const isAnchorish =
                a.tagName === "A" || a.tagName === "AREA" ||
                a.namespaceURI === "http://www.w3.org/2000/svg";
              if (!isAnchorish) return;
              if (a.hasAttribute(ORIG_HREF)) return; // already done
              const raw =
                a.getAttribute("href") ??
                a.getAttribute("xlink:href") ??
                "";
              if (!raw || raw.startsWith("javascript:") || raw.startsWith("#")) {
                // In-page fragment links we leave alone — they're harmless
                // (browser scrolls within the iframe) and breaking them
                // would lose EPUB footnote behavior.
                return;
              }
              a.setAttribute(ORIG_HREF, raw);
              try {
                a.setAttribute("href", "javascript:void(0)");
              } catch { /* SVG <a> uses xlink:href; remove it instead */ }
              try {
                a.removeAttribute("xlink:href");
              } catch { /* ignore */ }
              try {
                (a as HTMLAnchorElement).target = "";
                a.removeAttribute("target");
                a.removeAttribute("onclick");
              } catch { /* ignore */ }
            };
            const defangAll = (root: ParentNode) => {
              const sel = "a, area";
              root.querySelectorAll?.(sel).forEach((el) => defangOne(el));
            };
            defangAll(doc);
            try {
              const obs = new MutationObserver((muts) => {
                for (const m of muts) {
                  m.addedNodes.forEach((n) => {
                    if (n.nodeType !== 1) return;
                    const el = n as Element;
                    if (el.tagName === "A" || el.tagName === "AREA") defangOne(el);
                    defangAll(el);
                  });
                  if (m.type === "attributes" && (m.target as Element).tagName === "A") {
                    defangOne(m.target as Element);
                  }
                }
              });
              obs.observe(doc, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["href", "xlink:href", "target"],
              });
            } catch {
              /* MutationObserver missing — defang-on-render only */
            }

            // Delegated click handler. Reads the stashed real href from
            // the data attribute and routes via navigateToHref(). Capture
            // phase so we run before anything else; multiple events so we
            // win even if iOS only fires touchend.
            const findHrefEl = (target: Element | null): Element | null => {
              let el: Element | null = target;
              while (el) {
                if (el.getAttribute?.(ORIG_HREF)) return el;
                if (
                  el.getAttribute?.("href") &&
                  el.getAttribute("href") !== "javascript:void(0)"
                )
                  return el;
                if (el.getAttribute?.("xlink:href")) return el;
                el = el.parentElement;
              }
              return null;
            };
            const onLinkClick = (e: Event) => {
              const path = e.composedPath
                ? (e.composedPath() as EventTarget[])
                : [];
              const start =
                (path[0] as Element | undefined) ??
                (e.target as Element | null);
              const el = findHrefEl(start ?? null);
              if (!el) return;
              const href =
                el.getAttribute(ORIG_HREF) ??
                el.getAttribute("href") ??
                el.getAttribute("xlink:href") ??
                "";
              if (!href || href.startsWith("javascript:")) return;
              e.preventDefault();
              e.stopPropagation();
              if (/^(https?:|mailto:|tel:)/i.test(href)) {
                try { window.open(href, "_blank"); } catch { /* ignore */ }
                return;
              }
              void navigateToHref(href);
            };
            const opts: AddEventListenerOptions = { capture: true, passive: false };
            doc.addEventListener("pointerdown", onLinkClick, opts);
            doc.addEventListener("touchstart", onLinkClick, opts);
            doc.addEventListener("touchend", onLinkClick, opts);
            doc.addEventListener("mousedown", onLinkClick, opts);
            doc.addEventListener("click", onLinkClick, opts);
          } catch {
            /* iframe doc may be torn down — best effort */
          }
        });

        // Tap-zone navigation. Note this only fires when the user taps inside
        // the EPUB iframe itself; the visible Prev/Next overlay buttons in the
        // parent React tree handle the case where the iframe hasn't taken focus
        // yet (which is the typical "I opened the book and clicking does
        // nothing" failure mode).
        rendition.on("click", (e: MouseEvent) => {
          // If the user tapped an internal link inside the book (e.g. a
          // chapter entry on a front-matter Contents page, a footnote
          // anchor, or a glossary cross-reference), epub.js's own handler
          // has already navigated to it. Bail BEFORE the prev/next/chrome
          // logic below — otherwise we'd ALSO flip a page or toggle the
          // chrome on top of the navigation, swallowing the link.
          const tgt = e.target as HTMLElement | null;
          if (tgt?.closest?.("a[href], area[href]")) {
            return;
          }
          const iframeDoc =
            (e.target as Node | null)?.ownerDocument ??
            (e.currentTarget as Document | null);
          const w = iframeDoc?.defaultView ?? null;
          const sel = w?.getSelection?.();
          if (sel && sel.toString().length > 0) return;
          const width =
            w?.innerWidth ??
            iframeDoc?.documentElement?.clientWidth ??
            window.innerWidth;
          const x = e.clientX;
          // Any tap inside the page counts as interaction: bring the bars
          // back and restart the idle countdown. This is the "always an
          // obvious way to get it back" guarantee — tapping anywhere works,
          // not just the centre.
          wakeChromeRef.current?.();
          if (x < width * 0.3) {
            slidePageTurnRef.current("prev");
          } else if (x > width * 0.7) {
            slidePageTurnRef.current("next");
          } else {
            setChromeVisible((v) => !v);
          }
        });

        await markOpened(bookId);
        // BookCard's bookmark button drops a one-shot CFI into sessionStorage
        // before navigating; honour it once and clear it, so the next normal
        // open of this book resumes at lastKnown position instead of the
        // bookmark forever after.
        let bootCfi: string | undefined;
        try {
          const k = `pv.boot.cfi.${bookId}`;
          const v = sessionStorage.getItem(k);
          if (v) {
            bootCfi = v;
            sessionStorage.removeItem(k);
          }
        } catch {
          /* sessionStorage may be blocked — no-op */
        }

        // cloudSha256 — backfilled for older books (imported before we tracked
        // the SHA) by shaPromise at the top of boot(). Without it the
        // localStorage durable-resume fallback can't key off anything stable
        // and the user always lands at section 0 on a fresh re-open. Awaiting
        // the shared promise here (instead of recomputing) is what keeps the
        // pre-translation binder from racing this and seeing no SHA.
        const sha: string | undefined = await shaPromise;
        // Stash the book's SHA on a ref so the session-recap POST can
        // key the recap by book + start-time. Falls back to bookId at
        // POST time if the SHA never resolved.
        bookShaRef.current = sha ?? null;

        // Position resume — NEWEST WINS (2026-09-28, per Thomas).
        //
        // The one-shot bookmark jump still trumps everything: it is an
        // explicit action the user just took, not a stored position.
        //
        // Below that, the three storage tiers (cloud / Dexie / localStorage)
        // no longer have a fixed ranking. Whichever was written most recently
        // is authoritative. The old order let the cloud always win, which
        // meant a single bad cloud write propagated to every device and could
        // never be corrected locally — exactly what happened to Rock*.
        let startAt: string | undefined = bootCfi;
        if (!startAt) {
          const candidates: Array<{
            tier: "cloud" | "dexie" | "durable";
            cfi: string;
            at: number | null; // normalised to epoch MILLISECONDS
          }> = [];

          // --- cloud -----------------------------------------------------
          // 500ms cap so a slow / unreachable lib server can't stall boot.
          if (sha) {
            try {
              const cloud = await Promise.race([
                fetchCloudPosition(sha),
                new Promise<null>((resolve) =>
                  setTimeout(() => resolve(null), 500),
                ),
              ]);
              if (cloud?.cfi) {
                candidates.push({
                  tier: "cloud",
                  cfi: cloud.cfi,
                  at: normaliseStamp(cloud.savedAt),
                });
              }
            } catch {
              /* network failure → local tiers still compete below */
            }
          }

          // --- Dexie -----------------------------------------------------
          // The positions row carries updatedAt; the Book row's currentCfi
          // does not, so we read the row for the timestamp and fall back to
          // the Book row when there isn't one (pre-timestamp data).
          if (meta.currentCfi) {
            let at: number | null = null;
            try {
              const row = await getPosition(bookId);
              if (row?.updatedAt && row.cfi === meta.currentCfi) {
                const t = Date.parse(row.updatedAt);
                if (Number.isFinite(t)) at = t;
              }
            } catch {
              /* positions table unreadable — treat as untimestamped */
            }
            candidates.push({ tier: "dexie", cfi: meta.currentCfi, at });
          }

          // --- durable localStorage --------------------------------------
          if (sha) {
            const durable = readLastCfiBySha(sha);
            if (durable?.cfi) {
              candidates.push({
                tier: "durable",
                cfi: durable.cfi,
                at: normaliseStamp(durable.savedAt),
              });
            }
          }

          const winner = pickNewestPosition(candidates);
          // Telemetry: record the whole field, not just the winner — a
          // resume that picks the right tier and still lands in the wrong
          // place is a different bug from one that picks the wrong tier.
          resumeTiersRef.current = candidates
            .map((c) => `${c.tier}@${c.at ?? "untimed"}`)
            .join(",");
          resumeChoiceRef.current = winner?.tier ?? "none";
          if (winner) {
            startAt = winner.cfi;
            if (TTS_DEBUG_ENABLED) {
              // eslint-disable-next-line no-console
              console.log(
                "[pagevoice] resume tiers:",
                candidates.map((c) => `${c.tier}@${c.at ?? "untimed"}`).join(" "),
                "→ chose",
                winner.tier,
              );
            }
          }
        }
        // Pull cloud highlights for this book in the background. New
        // highlights from another device (or recovered after a PWA
        // reinstall) merge into Dexie and become visible on the next
        // repaint; if the network is slow this never blocks book open.
        if (sha) {
          void syncHighlightsForBook(bookId, sha);
        }
        // Track whether we ended up on a default (no saved position) open,
        // so the post-display TOC-skip below only runs in that case. If the
        // user actually saved their position inside the TOC (rare but
        // possible), we won't yank them off it.
        const isDefaultOpen = !startAt;

        // FIRST-RELOCATE REGRESSION GUARD — arm it here, before display().
        //
        // Resolve the CFI we are about to resume to into a spine index, so
        // the relocated handler can tell "we landed where we asked" from
        // "the CFI didn't resolve and epub.js dropped us at the front of the
        // book". Only the latter is damage, and only in that one window.
        //
        // Deliberately NOT armed when:
        //   - startAt === bootCfi — an explicit bookmark tap. The user just
        //     chose to go there; if it's earlier in the book, that is the
        //     point, and it must save normally.
        //   - default open — nothing stored, so there is nothing to protect.
        //   - the CFI won't resolve to a spine index — no baseline, so no
        //     comparison. Fail open and save, as everywhere else here.
        resumeIntentRef.current = null;
        if (startAt && startAt !== bootCfi) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sec = (book.spine as any)?.get?.(startAt);
            const idx = sec?.index;
            if (typeof idx === "number" && Number.isFinite(idx)) {
              resumeIntentRef.current = { cfi: startAt, spineIndex: idx };
            }
          } catch {
            /* unresolvable — guard stays inert, save proceeds */
          }
        }

        // Kept for the zero-stage-boot recovery in the ResizeObserver: if
        // we laid out against a 0x0 box, the first real measurement must
        // re-display THIS target, not whatever epub.js settled on.
        bootStartAtRef.current = startAt || undefined;
        if (startAt) {
          await rendition.display(startAt);
        } else {
          await rendition.display();
        }

        // 2026-10-06 — RESUME TELEMETRY (no behaviour change).
        //
        // "Didn't save place in book when home and back to book" cannot be
        // diagnosed from this machine: it reproduces on the BOOX and not on
        // a desktop, and the console on that device is not reachable. What
        // IS reachable is nginx's access log on the droplet, which records
        // the full query string of every request — so we tell the story in
        // a URL and let the 404 fall on the floor.
        //
        // Records, for each open: which tiers offered a position, their
        // timestamps, which one won, and — the part that actually settles
        // it — where the reader LANDED afterwards. If `want` and `got`
        // match, the resume is working and the complaint is about the save
        // side. If they differ, the CFI didn't resolve and we fell back to
        // the start, which would also explain why every stored position in
        // the library sits in the first 2% of its book: a failed resume
        // immediately saves the wrong place over the right one.
        //
        // Remove this block once the question is answered.
        // 2026-10-06 — ENRICHED. The first pass reported only start.cfi,
        // which produced want=/6/12 got=/6/10 on three consecutive opens
        // and looked like a one-section off-by-one in CFI resolution.
        // Checking the actual OPF spine of the book in question showed
        // /6/12 = xhtml/epigraph.xhtml and /6/10 = xhtml/dedication.xhtml
        // — adjacent front-matter sections of a few lines each. With
        // manager:"continuous" several such sections mount at once and
        // location.start is simply the first of them, so the reader may
        // well have been ON the epigraph with the dedication also in view.
        //
        // start.cfi alone cannot tell those two stories apart, so it is no
        // longer the thing we judge by. We now record the whole visible
        // RANGE (start and end, href and index), the spine index the CFI
        // resolves to, and a SECOND reading after a settle delay — because
        // currentLocation() immediately after display() can describe a
        // stage that epub.js has not finished filling.
        //
        // match is now computed on the spine range: did the section we
        // asked for end up on screen at all? That is the question that
        // matters, and it is the same test the guard above applies.
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const readLoc = (): any => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return rendition.currentLocation() as any;
            } catch {
              return null;
            }
          };
          let askedSpine: number | undefined;
          if (startAt) {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const sec = (book.spine as any)?.get?.(startAt);
              if (typeof sec?.index === "number") askedSpine = sec.index;
            } catch {
              /* unresolvable */
            }
          }
          const describe = (loc: any) => {
            const si = loc?.start?.index;
            const ei = loc?.end?.index;
            return {
              range:
                typeof si === "number"
                  ? `${si}-${typeof ei === "number" ? ei : si}`
                  : "unknown",
              sHref: String(loc?.start?.href ?? "?").slice(0, 60),
              eHref: String(loc?.end?.href ?? "?").slice(0, 60),
              cfi: String(loc?.start?.cfi ?? "?").slice(0, 120),
              onScreen:
                typeof askedSpine === "number" &&
                typeof si === "number" &&
                askedSpine >= si &&
                askedSpine <= (typeof ei === "number" ? ei : si),
            };
          };
          // ── STAGE DIMENSIONS (2026-10-06) ───────────────────────────
          //
          // Measure the viewer box at display() time, and again after the
          // settle delay. MEASURED 3/3 in a standalone harness with this
          // app's exact rendition options: a stage with ~zero height makes
          // currentLocation() return null outright, and a 40-48px stage
          // returns a start that then goes null on the next resize. Both
          // read, downstream, as "the reader lost my place".
          //
          // If the BOOX measures a zero-height stage at boot, this single
          // number says so, and no further guessing about continuous-mode
          // semantics is needed. If it reports a healthy stage, that is
          // just as useful: it rules the whole theory out.
          //
          // Counting iframes too — the continuous manager mounts one view
          // per section, so 0 frames means nothing rendered at all, which
          // is a different failure from a mis-measured box.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const measureStage = (): string => {
            try {
              const el = viewerRef.current;
              if (!el) return "noviewer";
              const r = el.getBoundingClientRect();
              const frames = el.querySelectorAll("iframe").length;
              return `${Math.round(r.width)}x${Math.round(r.height)}/c${el.clientWidth}x${el.clientHeight}/f${frames}`;
            } catch {
              return "err";
            }
          };
          const stageAtDisplay = measureStage();
          const immediate = describe(readLoc());
          // Settle delay: long enough for the continuous manager to finish
          // mounting neighbours, short enough that a user who starts
          // paging immediately still gets a reading. Fire-and-forget — the
          // open has already completed by the time this resolves.
          window.setTimeout(() => {
            try {
              const settled = describe(readLoc());
              const q = new URLSearchParams({
                b: bookId.slice(0, 12),
                tiers: resumeTiersRef.current || "none",
                chose: resumeChoiceRef.current || "none",
                want: (startAt || "default").slice(0, 120),
                askedSpine: String(askedSpine ?? "?"),
                // Immediately after display()
                iRange: immediate.range,
                iSHref: immediate.sHref,
                iEHref: immediate.eHref,
                iOn: immediate.onScreen ? "1" : "0",
                // After the settle delay
                sRange: settled.range,
                sSHref: settled.sHref,
                sEHref: settled.eHref,
                sOn: settled.onScreen ? "1" : "0",
                got: settled.cfi,
                // The question that matters: was the asked-for section on
                // screen once things settled?
                match: settled.onScreen ? "1" : "0",
                // Stage box: WxH(rect)/cWxcH(client)/fN(iframes), measured
                // at display() time and again now. A zero or near-zero
                // height at display is the one condition shown to make
                // currentLocation() return null.
                stageD: stageAtDisplay,
                stageS: measureStage(),
                // Zero-stage fix instrumentation (2026-10-06). stageWait is
                // "<ms>/<frames>/<w>x<h>[/TIMEOUT]" — how long the boot had
                // to wait for a measurable stage. zeroRz counts resize(0,0)
                // calls the ResizeObserver guard suppressed.
                //
                // THE TEST: stageD=0x0 opens should stop appearing. A
                // non-zero stageWait with stageD non-zero means the wait
                // caught a collapse that would previously have been an
                // 0x0/f10 open. A TIMEOUT means the stage genuinely never
                // measured and we rendered anyway — still broken, but now
                // loudly rather than silently.
                stageWait: (() => {
                  const s = stageWaitRef.current;
                  if (!s) return "none";
                  return `${s.waitedMs}/${s.frames}/${s.w}x${s.h}${s.timedOut ? "/TIMEOUT" : ""}`;
                })(),
                zeroRz: String(zeroResizeCountRef.current),
                // "1" = booted into a 0x0 stage and the recovery re-display
                // has already run by the settle point.
                zeroRecov: zeroBootRecoveredRef.current ? "1" : "0",
                // Did currentLocation() give us anything at all? "0" here
                // with a healthy stage means the null is NOT about layout.
                locD: immediate.range === "unknown" ? "0" : "1",
                locS: settled.range === "unknown" ? "0" : "1",
                ua: navigator.userAgent.slice(0, 40),
              });
              void fetch(`/__pvdiag/resume2?${q.toString()}`, {
                method: "GET",
                cache: "no-store",
              }).catch(() => {});
            } catch {
              /* telemetry is never allowed to break an open */
            }
          }, 1200);
          const got: string | undefined = immediate.cfi;
          const q = new URLSearchParams({
            b: bookId.slice(0, 12),
            tiers: resumeTiersRef.current || "none",
            chose: resumeChoiceRef.current || "none",
            want: (startAt || "default").slice(0, 120),
            got: (got || "unknown").slice(0, 120),
            match: startAt && got && got === startAt ? "1" : "0",
            ua: navigator.userAgent.slice(0, 40),
          });
          // Path is deliberately OUTSIDE /pagevoice/ — the service worker
          // intercepts everything under that scope and serves unknown
          // paths cache-first with ignoreSearch:true, which would cache
          // the first 404 and swallow every later report.
          void fetch(`/__pvdiag/resume?${q.toString()}`, {
            method: "GET",
            cache: "no-store",
          }).catch(() => {});
        } catch {
          /* telemetry is never allowed to break an open */
        }

        // First-open TOC skip. If we landed at section 0 (or any section
        // that looks like a nav/contents page) and we got here via a
        // default open (no saved position), advance to the first real
        // content section so Read-aloud has prose to read instead of a
        // list of chapter titles.
        // NOTE (2026-09-28): a version of this also tried to "rescue" a SAVED
        // position that pointed at a wordless page — Rock*'s stored cloud
        // position is cover.xhtml, so it resumes onto a blank cover. Measured
        // against all 19 live books it made things WORSE, not better: 14/19
        // opened instead of 18/19, because the extra archive reads on every
        // resume pushed three previously-fine books past their paint budget.
        // Reverted. The stored-position problem is real but needs fixing at
        // the write end (don't save a position on a text-less cover), not by
        // second-guessing the resume on every open.
        if (isDefaultOpen) {
          try {
            // Preferred path: the import already worked out where the prose
            // starts and stored it on the Book row, so this is a single
            // display() to a known href — no spine probing, no archive reads,
            // nothing that can disturb the rendition (which is exactly what
            // sank the open-time version of this; see the note at the top of
            // this file). Books imported before firstContentHref existed
            // simply don't have one and fall through to the old nav-skip.
            if (meta.firstContentHref) {
              await rendition.display(meta.firstContentHref);
            } else {
              await skipPastNavSection(rendition, book);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[pagevoice] first-content open failed:", e);
          }
        }
      } catch (err) {
        console.error(err);
        markOpenFailed(bookId);
        setError(
          err instanceof Error
            ? `Failed to open book: ${err.message}`
            : "Failed to open book."
        );
      }
    }
    boot();
    return () => {
      cancelled = true;
      window.clearTimeout(openWatchdog);
      // Use stopAll on unmount — by the time the cleanup runs, the user
      // may have switched engines and the captured activeProvider may
      // not be the one currently speaking.
      stopAllProviders();
      try {
        renditionRef.current?.destroy();
        bookRef.current?.destroy();
      } catch {
        /* noop */
      }
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // React to settings changes (theme / font).
  //
  // Capture the start CFI of the current page BEFORE re-applying the theme
  // so we can re-display at the same position after epub.js re-paginates.
  // Without this, bumping font size or line height makes the page snap
  // either to the chapter top or to wherever epub.js's last cached
  // position lands — the "keep top line at top" ask.
  useEffect(() => {
    const r = renditionRef.current;
    if (!r) return;
    const loc = r.currentLocation() as { start?: { cfi?: string } } | undefined;
    const cfi = loc?.start?.cfi;
    applyReaderTheme(r, reader);
    if (cfi) {
      // Two rAFs: one for layout to settle after the style override
      // applies, one for the column/page recalculation. Then display the
      // saved CFI — epub.js will land us on the page whose first character
      // is at or after that CFI, which is exactly the line that used to
      // be at the top.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          r.display(cfi).catch(() => {
            /* if the CFI no longer resolves (rare), drop the restore */
          });
        });
      });
    }
  }, [reader]);

  // Track time spent reading. Heartbeat every 15s while the document is
  // visible — pauses when the tab is hidden so we don't accumulate idle
  // time. Sessions land in Dexie and roll into per-book aggregates.
  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    let sessionId: string | null = null;
    const TICK_MS = 15_000;
    (async () => {
      const s = await startSession(bookId);
      if (cancelled) return;
      sessionId = s.id;
    })();
    const id = window.setInterval(() => {
      if (!sessionId) return;
      if (typeof document !== "undefined" && document.hidden) return;
      void tickSession(sessionId, bookId, TICK_MS / 1000);
    }, TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [bookId]);

  // ── Sleep timer ────────────────────────────────────────────────────
  //
  // When the store's sleepTimer.expiresAt is set, this effect schedules a
  // wake-up at that moment. The wake-up runs a 30-second linear fade from
  // the current TTS volume down to zero, then triggers onPause(). If the
  // timer is disarmed or replaced mid-flight, the cleanup cancels the
  // pending action.
  const sleepTimer = useReaderStore((s) => s.sleepTimer);
  const armSleepTimer = useReaderStore((s) => s.armSleepTimer);
  const preFadeVolumeRef = useRef<number | null>(null);
  useEffect(() => {
    if (!sleepTimer.expiresAt) return;
    const delay = sleepTimer.expiresAt - Date.now();
    // Give at least a beat of headroom so users who arm "5 min" don't
    // race a scheduled fade if their tab was backgrounded and re-armed.
    if (delay <= 0) {
      armSleepTimer(null);
      return;
    }
    let cancelFade: (() => void) | null = null;
    const kickoff = window.setTimeout(() => {
      // Nothing to fade if playback isn't running. Just disarm.
      if (!playingRef.current) {
        armSleepTimer(null);
        return;
      }
      const start = useReaderStore.getState().tts.volume;
      preFadeVolumeRef.current = start;
      cancelFade = fadeVolume(
        start,
        0,
        30_000,
        (v) => {
          // Push the interpolated volume through the store so the play
          // loop's onBoundary uses it on the next segment fetch. The
          // active provider also reads it via new speak calls; already-
          // playing audio elements pick it up because ReaderView's
          // useEffect on tts.volume replays the volume knob.
          useReaderStore.getState().setTTS({ volume: Math.max(0, v) });
        },
        () => {
          // Pause the reader without stopping — user can resume the
          // book from the same paragraph in the morning. Restore the
          // pre-fade volume immediately so the next play session isn't
          // silent, and disarm the timer.
          try { onPauseRef.current?.(); } catch { /* defensive */ }
          if (preFadeVolumeRef.current != null) {
            useReaderStore.getState().setTTS({ volume: preFadeVolumeRef.current });
            preFadeVolumeRef.current = null;
          }
          armSleepTimer(null);
        },
      );
    }, Math.max(0, delay - 30_000));
    return () => {
      window.clearTimeout(kickoff);
      cancelFade?.();
      // Restore any volume adjustment we made mid-fade so a cancelled
      // sleep timer doesn't leave the user with a lowered volume.
      if (preFadeVolumeRef.current != null) {
        useReaderStore.getState().setTTS({ volume: preFadeVolumeRef.current });
        preFadeVolumeRef.current = null;
      }
    };
  }, [sleepTimer.expiresAt, armSleepTimer]);

  // Forward-ref for onPause so the sleep-timer effect (declared before
  // onPause is defined lower in the file) can call it. Assigned in a
  // separate effect once onPause is stable.
  const onPauseRef = useRef<() => void>();

  // ── Session-recap trigger ─────────────────────────────────────────
  //
  // Requests a Gemma summary from /lib/recap using the accumulated text
  // of the reading session. Guards against double-posts (recapPostedRef)
  // so the same session doesn't get summarised twice, and against tiny
  // sessions (< 2 pages) where a recap would be noise.
  const requestRecap = useCallback(async (): Promise<void> => {
    if (recapPostedRef.current) return;
    if (recapBusy) return;
    const pages = pageCountRef.current;
    if (pages < 2) return;
    const text = sessionTextRef.current.join("\n\n").trim();
    if (text.length < 200) return;
    recapPostedRef.current = true;
    setRecapBusy(true);
    try {
      const r = await fetch(remoteUrl("/lib/recap"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          book_sha:      bookShaRef.current || bookId,
          session_start: Math.floor(sessionStartRef.current / 1000),
          session_end:   Math.floor(Date.now() / 1000),
          page_count:    pages,
          text,
        }),
      });
      if (r.ok) {
        const data = (await r.json()) as { summary?: string };
        if (data.summary) setRecapText(data.summary);
      }
    } catch {
      // Silent — a failed recap shouldn't scream at the user.
    } finally {
      setRecapBusy(false);
    }
  }, [bookId, recapBusy]);

  // Watch for > 5-minute pause / tab hidden → auto-request recap.
  useEffect(() => {
    const INACTIVITY_MS = 5 * 60_000;
    const id = window.setInterval(() => {
      const pausedAt = lastPausedAtRef.current;
      if (!pausedAt) return;
      // Belt and braces alongside clearing the marker on resume: if audio
      // is playing we are mid-session by definition, whatever the marker
      // says. He is listening, not looking — a panel appearing is pure
      // interruption, and on e-ink it costs a full repaint mid-paragraph.
      if (playingRef.current && !pausedRef.current) return;
      if (Date.now() - pausedAt >= INACTIVITY_MS) {
        // Clear the marker so we don't re-fire on the next tick.
        lastPausedAtRef.current = null;
        void requestRecap();
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [requestRecap]);

  // Flush a recap on tab close / navigation away. Best-effort — Gemma
  // may take longer than the browser lets us keep the request open, but
  // the pv-lib server will still write the row if the request lands.
  useEffect(() => {
    const onBeforeUnload = () => {
      // Only trigger if we haven't already — otherwise the page-hide
      // fires a duplicate.
      if (recapPostedRef.current) return;
      if (pageCountRef.current < 2) return;
      const text = sessionTextRef.current.join("\n\n").trim();
      if (text.length < 200) return;
      recapPostedRef.current = true;
      const payload = JSON.stringify({
        book_sha:      bookShaRef.current || bookId,
        session_start: Math.floor(sessionStartRef.current / 1000),
        session_end:   Math.floor(Date.now() / 1000),
        page_count:    pageCountRef.current,
        text,
      });
      try {
        // sendBeacon survives a tab close where fetch would be killed.
        // The pv-lib server accepts POST JSON on /lib/recap.
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon?.(remoteUrl("/lib/recap"), blob);
      } catch { /* best effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onBeforeUnload);
    };
  }, [bookId]);

  // Apply per-book TTS preferences on book open (and persist them back when
  // the global store changes — so a tweak on this book is remembered).
  useEffect(() => {
    if (!bookId) return;
    (async () => {
      const prefs = await getBookPrefs(bookId);
      if (!prefs) return;
      // Don't override the engine while we're already speaking. The
      // prefs fetch is async, and on book open the user might tap Play
      // before it lands. If we change the provider mid-segment, the
      // current audio gets killed and the play loop restarts on a
      // different engine — the "voice loaded but stopped and reloaded"
      // symptom hit during testing 2026-06-26.
      if (playingRef.current) return;
      const patch: Partial<typeof tts> = {};
      if (prefs.rate != null) patch.rate = prefs.rate;
      // Engine is intentionally NOT restored from per-book prefs.
      // 2026-06-28 — the standing rule is "voice always defaults
      // to Kokoro." Per-book prefs used to override the global, which
      // meant any book ever opened with a non-Kokoro engine kept
      // dragging that selection back forever. The user-facing knob is
      // the global Engine picker in Settings; per-book retains only
      // rate + voice within whatever engine is currently active.
      if (prefs.serverVoice) patch.serverVoice = prefs.serverVoice;
      if (prefs.voiceURI) patch.voiceURI = prefs.voiceURI;
      if (Object.keys(patch).length > 0) {
        useReaderStore.getState().setTTS(patch);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  // Persist any TTS pref change to the per-book record (debounced).
  useEffect(() => {
    if (!bookId) return;
    const t = window.setTimeout(() => {
      void setBookPrefs(bookId, {
        rate: tts.rate,
        provider: tts.provider,
        serverVoice: tts.serverVoice,
        voiceURI: tts.voiceURI,
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [bookId, tts.rate, tts.provider, tts.serverVoice, tts.voiceURI]);

  // iOS Safari keep-alive: SpeechSynthesis stops emitting audio after ~15s of
  // continuous speech unless tickled with pause/resume. Apple-bug, well-known.
  // Fire a no-op pause+resume every 10s while playing and not paused.
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const id = window.setInterval(() => {
      if (playingRef.current && !pausedRef.current) {
        try {
          window.speechSynthesis.pause();
          window.speechSynthesis.resume();
        } catch {
          /* best effort */
        }
      }
    }, 10000);
    return () => window.clearInterval(id);
  }, []);

  // Repaginate when the viewer container's size changes (e.g. the bottom
  // controls bar grows because the user expanded the More panel, the address
  // bar collapses on mobile, or the window is resized). Without this epub.js
  // keeps the prior viewport and the last line on a page can hide behind the
  // controls bar.
  useEffect(() => {
    const el = viewerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        try {
          // ZERO GUARD (2026-10-06). This observer fires on EVERY box change
          // including a transient collapse to 0, and it used to pass that
          // straight through as resize(0, 0). That is almost certainly the
          // second half of the stage-collapse signature: ten section views
          // mount, 100dvh momentarily re-resolves to zero, this fires, and
          // epub.js tears the views down — stage=0x0/f10 becoming f0.
          //
          // A zero box is never a layout the reader should adopt. Ignore it
          // and keep the last good pagination; the next non-zero measurement
          // (which the observer will also deliver) does the real resize. No
          // timeout needed here — doing nothing is the correct response to a
          // zero, not a deferred version of the wrong thing.
          const w = el.clientWidth;
          const h = el.clientHeight;
          if (w <= 0 || h <= 0) {
            zeroResizeCountRef.current += 1;
            return;
          }
          // RECOVERY FROM A ZERO-STAGE BOOT (2026-10-06).
          //
          // If waitForStage timed out we called renderTo against a 0x0 box,
          // because showing nothing is worse. epub.js laid out against that
          // zero and will not fix itself — resize() alone re-measures the
          // stage but does not restore the position we asked for, so the
          // reader would come back sized correctly and showing the wrong
          // place, which is the failure mode we have spent all day on.
          //
          // So on the FIRST non-zero measurement after a zero boot: resize,
          // then re-display the CFI the open originally asked for. One shot,
          // guarded by a ref, so a later legitimate resize (chrome, font,
          // rotation) never yanks the user back to where they opened.
          //
          // VERIFIED MECHANISM, not the device: with the browser pane hidden
          // window.innerHeight is 0 and `100dvh` resolves to 0px, so html,
          // body, #root and every descendant measure 0x0 and epub.js
          // produces exactly the 0x0/f10 -> f0 signature from the logs. The
          // BOOX equivalent is the WebView reporting a zero viewport at
          // launch; same shape, different trigger.
          if (recoverFromZeroBootRef.current) {
            recoverFromZeroBootRef.current = false;
            const target = bootStartAtRef.current;
            zeroBootRecoveredRef.current = true;
            try {
              const r2 = renditionRef.current as Rendition | null;
              r2?.resize(w, h);
              if (target) void r2?.display(target);
              else void r2?.display();
            } catch {
              /* best effort — the plain resize below still runs next tick */
            }
            // eslint-disable-next-line no-console
            console.warn(
              `[pagevoice] recovered from zero-stage boot — stage now ${w}x${h}, re-displaying ${target ? "resume target" : "default"}`,
            );
            return;
          }
          // ── RESIZE MUST PRESERVE POSITION (2026-10-06) ─────────────────
          // MEASURED: shrinking the viewport by 48px (exactly what the
          // Android navigation bar does when it reappears) and restoring it
          // destroyed and rebuilt the iframe and moved the reader from
          // "A Conversation About Rain…" to the book's Contents page. The
          // frame went 1760px -> 2200px wide, i.e. a full re-layout, and the
          // place was lost.
          //
          // That matters twice over: the bar reappearing is Thomas's actual
          // complaint, and the fullscreen auto-re-entry shipped in 261007-0004
          // DELIBERATELY causes this transition — so without this it would
          // have made losing his place more frequent, not less.
          //
          // epub.js's resize() re-renders but does not restore where you
          // were. The chrome-visibility effect further down already knew
          // this and passes a CFI; this observer did not. Same treatment:
          // capture the CFI first, suppress position writes and TTS restarts
          // across the rebuild, resize, then put him back.
          const r = renditionRef.current as
            | (typeof renditionRef.current & {
                resize: (w?: number, h?: number, cfi?: string) => void;
              })
            | null;
          if (!r) return;
          let keep: string | undefined;
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            keep = (r.currentLocation() as any)?.start?.cfi ?? undefined;
          } catch {
            keep = undefined;
          }
          if (!keep) {
            // No CFI to protect — a plain resize is the best we can do, and
            // is what happened before this block existed.
            r.resize(w, h);
            return;
          }
          suppressPositionSaveRef.current = true;
          ttsRestartGuardRef.current = true;
          try {
            r.resize(w, h, keep);
          } catch {
            try { r.resize(w, h); } catch { /* give up quietly */ }
          }
          // Let the rebuild's relocations drain before re-enabling writes,
          // so the position that gets saved is the restored one rather than
          // an intermediate from the rebuild.
          window.setTimeout(() => {
            suppressPositionSaveRef.current = false;
            ttsRestartGuardRef.current = false;
          }, 500);
        } catch {
          /* rendition not yet ready */
        }
      });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  // ─── Highlights state + helpers ──────────────────────────────────
  const [selectionMenu, setSelectionMenu] = useState<{
    cfiRange: string;
    text: string;
    x: number;
    y: number;
  } | null>(null);

  // ── Kindle-style page-turn with a smooth horizontal slide ──────────
  // The reader already paginates horizontally (epub.js paginated / CSS
  // columns). This wraps rendition.next()/prev() with a slide+fade so
  // taps, swipes and keys all feel like turning a page instead of a hard
  // cut. We animate the epub.js HOST div (viewerRef) — NOT epub.js's own
  // inner iframe/columns — so epub's internal scroll/transform for the
  // visible column is never disturbed; we only translate the whole host
  // and always reset it to translateX(0), so paging can never get stuck
  // or land on the wrong column. Any failure falls back to a plain turn.
  /** Beacon for the fullscreen story. Turns "sometimes" into a number. */
  /** TX COLUMNS: the translation overlay's own page handle, and a flag set
   *  when a backward turn should land on the END of the previous translated
   *  page once its columns have been measured. */
  const txNavRef = useRef<TxNav | null>(null);
  const pendingTxToEndRef = useRef(false);

  const fsDiag = useCallback((what: string, extra?: Record<string, string>) => {
    if (fsDiagCountRef.current >= 20) return;
    fsDiagCountRef.current += 1;
    try {
      const q = new URLSearchParams({
        ev: "fs",
        what,
        active: document.fullscreenElement ? "1" : "0",
        innerH: String(window.innerHeight),
        failures: String(fsFailuresRef.current),
        ...(extra ?? {}),
        ua: navigator.userAgent.slice(0, 32),
      });
      void fetch(`/__pvdiag/fs?${q.toString()}`, {
        method: "GET",
        cache: "no-store",
      }).catch(() => {});
    } catch {
      /* diagnostics never break a read */
    }
  }, []);

  /** Called on every page turn. Re-requests fullscreen if we have been out
   *  of it for a few turns and have not been refused too often. */
  const maybeRestoreFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenEnabled) return;
      if (document.fullscreenElement) {
        // Back in (or never left) — reset the grace counter so a later exit
        // gets its own full window again.
        turnsOutOfFsRef.current = 0;
        return;
      }
      if (fsFailuresRef.current >= FS_MAX_FAILURES) return;
      turnsOutOfFsRef.current += 1;
      if (turnsOutOfFsRef.current < FS_GRACE_TURNS) return;
      turnsOutOfFsRef.current = 0;
      const el = document.documentElement;
      const p = el.requestFullscreen?.();
      if (p && typeof p.then === "function") {
        p.then(() => {
          fsFailuresRef.current = 0;
          fsDiag("regained");
        }).catch((err: unknown) => {
          fsFailuresRef.current += 1;
          fsDiag("refused", {
            err: String((err as Error)?.name || err).slice(0, 24),
          });
        });
      }
    } catch (err) {
      fsFailuresRef.current += 1;
      fsDiag("threw", { err: String((err as Error)?.name || err).slice(0, 24) });
    }
  }, [fsDiag]);

  const slidePageTurn = useCallback((dir: "next" | "prev") => {
    const r = renditionRef.current;
    if (!r) return;
    // A page turn is a user gesture, which is what the Fullscreen API
    // requires — so this is the one legitimate place to re-ask.
    maybeRestoreFullscreen();
    // TX COLUMNS (2026-10-09): when the translation overlay is up, a
    // translated page is usually TALLER than the original page it covers —
    // English runs longer than the German or Spanish it replaces — so the
    // overlay lays itself out in page-sized columns. Consume those first;
    // only advance the book once the reader has seen the whole translated
    // page. This is why the overlay no longer scrolls, and it touches
    // neither the rendition nor the saved position.
    const nav = txNavRef.current;
    if (useReaderStore.getState().reader.translateOn && nav) {
      if (dir === "next" && nav.hasNext()) {
        nav.next();
        return;
      }
      if (dir === "prev" && nav.hasPrev()) {
        nav.prev();
        return;
      }
      // Falling through to a real turn. Going BACKWARD should land on the
      // END of the previous translated page, not its first column, or the
      // reader skips everything the previous page's later columns held.
      if (dir === "prev") pendingTxToEndRef.current = true;
    }
    const go = () => (dir === "next" ? r.next() : r.prev());
    // With the continuous + snap manager, epub.js pre-renders neighbours and
    // animates the turn itself, so a custom host transform would fight its
    // layout. We just advance and let epub.js snap.
    void go();
  }, [maybeRestoreFullscreen]);
  // Stable ref so the boot effect (created before slidePageTurn in source
  // order) can call the latest animated-turn without re-subscribing.
  const slidePageTurnRef = useRef(slidePageTurn);
  slidePageTurnRef.current = slidePageTurn;
  /** Cleanup for the in-iframe swipe binding. The content effect re-runs on
   *  every section render, so without detaching first the listeners stacked
   *  up and one swipe turned several pages. */
  const detachSwipeRef = useRef<(() => void) | null>(null);
  useEffect(() => () => detachSwipeRef.current?.(), []);

  const repaintHighlights = useCallback(async () => {
    const rendition = renditionRef.current;
    if (!rendition || !bookId) return;
    const highlights = await listHighlights(bookId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ann = (rendition as any).annotations;
    if (!ann) return;
    // Clear-then-add. epub.js's annotations API is idempotent on the same
    // cfi but throws if we add twice; we wrap each call in try/catch.
    for (const h of highlights) {
      try {
        ann.remove(h.cfiRange, "highlight");
      } catch {
        /* not present yet */
      }
      const colorMap: Record<string, string> = {
        peach: "rgba(249, 217, 168, 0.55)",
        yellow: "rgba(255, 236, 107, 0.45)",
        pink: "rgba(255, 179, 200, 0.45)",
        green: "rgba(168, 230, 163, 0.45)",
        blue: "rgba(169, 216, 255, 0.45)",
        orange: "rgba(255, 193, 133, 0.45)",
      };
      try {
        ann.add(
          "highlight",
          h.cfiRange,
          { id: h.id },
          undefined,
          undefined,
          { fill: colorMap[h.color] ?? colorMap.peach }
        );
      } catch (err) {
        console.warn("[pagevoice] couldn't repaint highlight", h.id, err);
      }
    }
  }, [bookId]);

  const wireSelectionMenu = useCallback((view: Contents) => {
    const doc = view.document;

    // Shared save path — given a DOM Range inside the iframe, persist it
    // as a highlight in the last-used color and repaint.
    const saveRange = (range: Range, text: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cfi = (view as any).cfiFromRange?.(range);
      pvDebug(`saveRange len=${text.length} cfi=${cfi ? "ok" : "FAIL"}`);
      if (!cfi) return;
      const color = readLastColor();
      void (async () => {
        if (!bookId) return;
        const rendition = renditionRef.current;
        const loc = rendition?.currentLocation() as
          | { start?: { href?: string } }
          | undefined;
        const href = loc?.start?.href;
        let chapterLabel: string | undefined;
        if (href) {
          const found = findInToc(toc, href);
          if (found) chapterLabel = found.label.trim();
        }
        try {
          await addHighlight({
            bookId,
            cfiRange: cfi,
            text,
            href,
            chapterLabel,
            color,
          });
          await repaintHighlights();
          pvDebug("highlight SAVED ✓");
        } catch (err) {
          pvDebug("highlight ERR " + String(err).slice(0, 40));
          console.warn("[pagevoice] auto-highlight save failed", err);
        }
      })();
    };

    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);

    // EXPERIMENT (2026-08-11): hand the page swipe to epub.js's native
    // continuous+snap manager (which pre-renders neighbours and snaps between
    // pages). The custom gesture machine below owned the touch surface and
    // disabled native selection, which suppressed that native snap swipe — so
    // it's turned off here. Highlight falls through to the native-selection
    // path (long-press selects, we save on selection end).
    const USE_CUSTOM_TOUCH = false;
    if (isTouch && USE_CUSTOM_TOUCH) {
      // ── iOS/touch gesture layer (2026-07-23 Kindle-paging rewrite) ──
      //
      // The reader pages horizontally (epub.js paginated / CSS columns).
      // The APP owns the touch surface inside the iframe so highlighting
      // can finally work on iOS. Apple's native long-press selection and
      // its Copy / Look-Up callout are fully OPTED OUT of below
      // (-webkit-touch-callout + user-select:none) — with no native
      // selection there is nothing for iOS to hijack — and we run our own
      // gesture machine that cleanly disambiguates three gestures:
      //
      //   • quick tap             → NOT consumed here; falls through to the
      //                             tap zones (page edge / chrome toggle).
      //                             We never preventDefault a plain tap.
      //   • horizontal swipe      → page turn (with the slide animation).
      //                             Detected when the finger travels past a
      //                             slop threshold BEFORE the long-press
      //                             timer fires and the move is x-dominant.
      //   • long-press then hold  → starts an APP-DRAWN selection anchored
      //     or drag, then release   at the word under the finger; dragging
      //                             after the press EXTENDS the selection;
      //                             lifting the finger COMMITS a highlight.
      //
      // Selection is painted by us (overlay rectangles from the live DOM
      // Range) rather than the browser, so it survives with native
      // selection disabled and repagination re-paints via repaintHighlights.
      try {
        const style = doc.createElement("style");
        style.setAttribute("data-pv", "touch-selection");
        style.textContent = [
          "* { -webkit-touch-callout: none !important; }",
          "html, body { -webkit-user-select: none !important; user-select: none !important; }",
          ".pv-sel-layer { position: fixed; inset: 0; pointer-events: none; z-index: 2147483646; }",
          // Warm peach selection tint (matches the highlight colour) drawn as
          // a soft rounded background behind the selected text.
          ".pv-sel-rect { position: fixed; background: rgba(249,217,168,0.60); border-radius: 3px; pointer-events: none; }",
          // Subtle handle marking the selection start.
          ".pv-sel-handle { position: fixed; width: 3px; border-radius: 2px; background: #d89a4e; pointer-events: none; }",
        ].join("\n");
        (doc.head || doc.documentElement)?.appendChild(style);
      } catch {
        /* iframe may be tearing down */
      }

      const LONG_PRESS_MS = 380;
      const TAP_SLOP = 10; // movement under this stays a tap / press

      type Mode = "pending" | "selecting";
      let mode: Mode = "pending";
      let pressTimer: number | undefined;
      let startX = 0;
      let startY = 0;

      let selLayer: HTMLDivElement | null = null;
      let anchor: { node: Node; offset: number } | null = null;
      let currentRange: Range | null = null;

      const clearTimer = () => {
        if (pressTimer !== undefined) {
          window.clearTimeout(pressTimer);
          pressTimer = undefined;
        }
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const d = doc as any;
      const caretAt = (
        x: number,
        y: number
      ): { node: Node; offset: number } | null => {
        if (typeof d.caretRangeFromPoint === "function") {
          const r = d.caretRangeFromPoint(x, y);
          if (r) return { node: r.startContainer, offset: r.startOffset };
        } else if (typeof d.caretPositionFromPoint === "function") {
          const p = d.caretPositionFromPoint(x, y);
          if (p) return { node: p.offsetNode, offset: p.offset };
        }
        return null;
      };
      // Expand a caret to the whole word it sits in — the unit a long-press
      // selects first (Kindle behaviour), before any drag extends it.
      const wordAt = (
        x: number,
        y: number
      ): { node: Node; offset: number } | null => {
        const c = caretAt(x, y);
        if (!c || c.node.nodeType !== Node.TEXT_NODE) return null;
        const full = c.node.textContent ?? "";
        let s = c.offset;
        while (s > 0 && /\S/.test(full[s - 1])) s -= 1;
        return { node: c.node, offset: s };
      };
      const buildRange = (
        a: { node: Node; offset: number },
        f: { node: Node; offset: number }
      ): Range | null => {
        try {
          const range = doc.createRange();
          const cmp = a.node.compareDocumentPosition(f.node);
          const sameNode = a.node === f.node;
          const focusBefore =
            (cmp & Node.DOCUMENT_POSITION_PRECEDING) !== 0 ||
            (sameNode && f.offset < a.offset);
          if (focusBefore) {
            range.setStart(f.node, f.offset);
            range.setEnd(a.node, a.offset);
          } else {
            range.setStart(a.node, a.offset);
            range.setEnd(f.node, f.offset);
          }
          // Grow the end to a word boundary so selection covers whole words.
          const endNode = range.endContainer;
          if (endNode.nodeType === Node.TEXT_NODE) {
            const t = endNode.textContent ?? "";
            let e = range.endOffset;
            while (e < t.length && /\S/.test(t[e])) e += 1;
            try {
              range.setEnd(endNode, e);
            } catch {
              /* offset out of range — leave as-is */
            }
          }
          return range;
        } catch {
          return null;
        }
      };
      const clearSelLayer = () => {
        if (selLayer && selLayer.parentNode)
          selLayer.parentNode.removeChild(selLayer);
        selLayer = null;
      };
      const drawSelection = (range: Range) => {
        if (!selLayer) {
          selLayer = doc.createElement("div");
          selLayer.className = "pv-sel-layer";
          doc.body.appendChild(selLayer);
        }
        selLayer.textContent = "";
        const rects = range.getClientRects();
        let first: DOMRect | null = null;
        for (let i = 0; i < rects.length; i++) {
          const rc = rects[i];
          if (rc.width < 1 || rc.height < 1) continue;
          if (!first) first = rc;
          const box = doc.createElement("div");
          box.className = "pv-sel-rect";
          box.style.left = `${rc.left}px`;
          box.style.top = `${rc.top}px`;
          box.style.width = `${rc.width}px`;
          box.style.height = `${rc.height}px`;
          selLayer.appendChild(box);
        }
        // Subtle handle at the selection start.
        if (first) {
          const handle = doc.createElement("div");
          handle.className = "pv-sel-handle";
          handle.style.left = `${first.left - 2}px`;
          handle.style.top = `${first.top - 2}px`;
          handle.style.height = `${first.height + 4}px`;
          selLayer.appendChild(handle);
        }
      };
      const beginSelection = (x: number, y: number): boolean => {
        const w = wordAt(x, y);
        if (!w) return false;
        anchor = w;
        const range = buildRange(w, { node: w.node, offset: w.offset });
        if (!range) return false;
        currentRange = range;
        drawSelection(range);
        mode = "selecting";
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (navigator as any).vibrate?.(10);
        } catch {
          /* no haptics */
        }
        return true;
      };
      const extendSelection = (x: number, y: number) => {
        if (!anchor) return;
        const f = caretAt(x, y);
        if (!f) return;
        const range = buildRange(anchor, f);
        if (!range) return;
        currentRange = range;
        drawSelection(range);
      };
      const commitSelection = () => {
        const range = currentRange;
        clearSelLayer();
        currentRange = null;
        anchor = null;
        if (!range) { pvDebug("commitSel: no range"); return; }
        const text = range.toString().trim();
        pvDebug(`commitSel len=${text.length}`);
        if (text.length < 2) return;
        saveRange(range.cloneRange(), text);
      };
      const resetGesture = () => {
        clearTimer();
        clearSelLayer();
        currentRange = null;
        anchor = null;
        mode = "pending";
      };

      const onTouchStart = (e: TouchEvent) => {
        resetGesture();
        if (e.touches.length !== 1) return;
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        pvDebug(`down x=${Math.round(startX)}`);
        pressTimer = window.setTimeout(() => {
          pressTimer = undefined;
          const okSel = beginSelection(startX, startY);
          pvDebug(`long-press → beginSelection=${okSel}`);
        }, LONG_PRESS_MS);
      };
      const onTouchMove = (e: TouchEvent) => {
        const t = e.touches[0];
        if (!t) return;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (mode === "selecting") {
          // Extending the selection — own the gesture, block page scroll.
          e.preventDefault();
          extendSelection(t.clientX, t.clientY);
          return;
        }
        if (mode === "pending") {
          if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) {
            // Any drag cancels the pending long-press. A horizontal drag is a
            // page swipe — epub.js's snap manager owns that now, so we do NOT
            // consume it (no preventDefault); we just step aside.
            clearTimer();
          }
        }
      };
      const onTouchEnd = (e: TouchEvent) => {
        clearTimer();
        if (mode === "selecting") {
          e.preventDefault();
          commitSelection();
          mode = "pending";
          return;
        }
        // pending + no long-press + no swipe = a plain tap → let the tap
        // zones handle it (do NOT preventDefault).
        mode = "pending";
      };

      doc.addEventListener("touchstart", onTouchStart, { passive: false });
      doc.addEventListener("touchmove", onTouchMove, { passive: false });
      doc.addEventListener("touchend", onTouchEnd, { passive: false });
      doc.addEventListener("touchcancel", resetGesture, { passive: true });
      return;
    }

    // ── Desktop path: native mouse selection, save on mouseup ──
    const onUp = () => {
      const w = view.window;
      const sel = w?.getSelection?.();
      const text = sel?.toString().trim() ?? "";
      if (!text || text.length < 2) return;
      const range = sel?.getRangeAt(0);
      if (!range) return;
      try {
        saveRange(range.cloneRange(), text);
      } finally {
        try {
          sel?.removeAllRanges();
        } catch {
          /* defensive — the iframe may already be unloading */
        }
      }
    };
    doc.addEventListener("mouseup", onUp);
    // iOS: a long-press selection finishes with touchend (no mouseup). Save on
    // both. onUp bails when the selection is empty, so plain taps are ignored.
    doc.addEventListener("touchend", onUp);

    // Minimal swipe → page turn. epub.js's own snap swipe doesn't fire from
    // inside the iframe on iOS, so we detect the swipe ourselves and call the
    // turn. It coexists with native selection: it never fires while text is
    // selected (that's a highlight) and never preventDefaults, so the
    // continuous manager and native selection both keep working. Because the
    // manager is continuous, the turn is pre-rendered — no blank frame.
    if (isTouch) {
      // Thresholds and rationale live on attachSwipePaging above. The outer
      // window gets the same treatment in its own effect below, so a swipe
      // that starts on an edge strip or a margin works identically.
      detachSwipeRef.current?.();
      detachSwipeRef.current = attachSwipePaging(
        doc,
        () => view.window?.getSelection?.()?.toString() ?? "",
        slidePageTurn,
      );
    }
  }, [bookId, toc, repaintHighlights, slidePageTurn]);

  // ── PANEL ANCHOR (2026-10-06) ──────────────────────────────────────────
  // Thomas: "Should appear above bar row" / "Search should also appear above
  // the bar". Publish the bar's measured height as --pv-bar-h on the root
  // element so every panel anchors to the SAME number instead of each one
  // hard-coding its own guess. SearchPanel had 84px baked in with a comment
  // estimating the bar at "~72px"; the bar actually measures 151px now, so
  // that panel has been sitting on top of the bar rather than above it.
  //
  // Measured, not computed: the bar's height depends on safe-area insets,
  // the progress strip and the percentage row, none of which are knowable
  // from CSS alone.
  //
  // This cannot repaginate: the bar is an absolute overlay and the panels
  // are `fixed`, so none of them is in the viewer's flow and the stage box
  // never changes. That is the same reason the bar itself is reflow-free.
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const publish = () => {
      // Closed, the bar is translated off-screen and only the 56px reveal
      // caret occupies that space, so panels should clear 56 rather than
      // the full bar height.
      const h = barOpen ? Math.round(el.getBoundingClientRect().height) : 56;
      document.documentElement.style.setProperty("--pv-bar-h", `${h}px`);
    };
    publish();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [barOpen]);

  // Swipe on the OUTER document too. The in-iframe binding above only sees
  // touches that start on the text; a swipe beginning on an edge strip, on
  // the margin, or anywhere else in the app shell is an outer-window touch
  // that never reaches the iframe. That asymmetry is the same class of bug
  // as the taps and keys that never reached the outer window — this is the
  // mirror image of it, and it leaves exactly the 64px-wide bands at each
  // edge (where his thumb naturally starts a swipe) unable to swipe at all.
  useEffect(() => {
    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (!isTouch) return;
    // Indirect through the ref so this effect never has to re-subscribe.
    return attachSwipePaging(
      window,
      () => window.getSelection?.()?.toString() ?? "",
      (dir) => slidePageTurnRef.current(dir),
    );
  }, []);

  const dismissSelectionMenu = useCallback(() => {
    setSelectionMenu(null);
    // Clear native selection so menu doesn't reappear from the same range.
    const view = contentsRef.current;
    view?.window?.getSelection?.()?.removeAllRanges();
  }, []);

  const onCreateHighlight = useCallback(
    async (color: import("@/types/highlight").HighlightColor) => {
      if (!selectionMenu || !bookId) return;
      const rendition = renditionRef.current;
      const loc = rendition?.currentLocation() as
        | { start?: { href?: string } }
        | undefined;
      const href = loc?.start?.href;
      // Try to resolve the chapter label from the TOC.
      let chapterLabel: string | undefined;
      if (href) {
        const found = findInToc(toc, href);
        if (found) chapterLabel = found.label.trim();
      }
      await addHighlight({
        bookId,
        cfiRange: selectionMenu.cfiRange,
        text: selectionMenu.text,
        href,
        chapterLabel,
        color,
      });
      dismissSelectionMenu();
      await repaintHighlights();
    },
    [bookId, dismissSelectionMenu, repaintHighlights, selectionMenu, toc]
  );

  // Resolve the Contents object that holds the CURRENTLY visible spine
  // section. epub.js may have several views mounted at once (it pre-renders
  // adjacent sections for smooth navigation), so `contentsRef` from the last
  // `rendered` event can point at a pre-rendered neighbour rather than what
  // the user is looking at. This always pulls the matching one.
  const getVisibleView = useCallback((): Contents | null => {
    const rendition = renditionRef.current;
    if (!rendition) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allRaw = (rendition as any).getContents?.();
    const all: Contents[] = Array.isArray(allRaw)
      ? allRaw
      : allRaw
        ? [allRaw]
        : [];
    if (all.length === 0) return contentsRef.current;
    if (all.length === 1) return all[0];

    // PRIMARY: spine-href match against the rendition's current location.
    // currentLocation() reflects what epub.js considers to be the user's
    // current page — when it's available, that's the source of truth and
    // we should believe it ahead of any geometric heuristic. The TOC bug
    // came from doing rect-overlap first and that iframe still being at
    // (0,0) when its peer (the TOC) had been positioned already.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loc = (rendition as any).currentLocation?.() as
      | { start?: { index?: number; href?: string } }
      | undefined;
    const wantIndex = loc?.start?.index;
    const wantHref = loc?.start?.href;
    if (wantHref) {
      const wantTail = wantHref.split("/").pop() || wantHref;
      const byHref = all.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (c: any) => {
          const ch = c.sectionHref || c.section?.href || "";
          if (ch === wantHref) return true;
          const winHref: string = c.window?.location?.href || "";
          if (winHref.endsWith(wantHref)) return true;
          if (wantTail && winHref.endsWith(wantTail)) return true;
          const chTail = ch.split("/").pop() || ch;
          return chTail === wantTail;
        }
      );
      if (byHref) return byHref;
    }
    if (wantIndex !== undefined) {
      const byIndex = all.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (c: any) =>
          c.sectionIndex === wantIndex || c.section?.index === wantIndex
      );
      if (byIndex) return byIndex;
    }

    // SECONDARY: rect intersection. Only reached when the location object
    // doesn't surface a usable href / index — e.g. the iframe finished
    // mounting before epub.js updated currentLocation(). Use the iframe
    // that actually owns the centre of the viewer.
    const viewerEl = viewerRef.current;
    if (viewerEl) {
      const viewerRect = viewerEl.getBoundingClientRect();
      const cx = viewerRect.left + viewerRect.width / 2;
      const cy = viewerRect.top + viewerRect.height / 2;
      let bestArea = 0;
      let best: Contents | null = null;
      for (const c of all) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const iframe = (c as any).document?.defaultView?.frameElement as
          | HTMLIFrameElement
          | undefined;
        if (!iframe) continue;
        const r = iframe.getBoundingClientRect();
        if (r.left <= cx && cx <= r.right && r.top <= cy && cy <= r.bottom) {
          return c;
        }
        const ox = Math.max(0, Math.min(r.right, viewerRect.right) - Math.max(r.left, viewerRect.left));
        const oy = Math.max(0, Math.min(r.bottom, viewerRect.bottom) - Math.max(r.top, viewerRect.top));
        const area = ox * oy;
        if (area > bestArea) {
          bestArea = area;
          best = c;
        }
      }
      if (best) return best;
    }

    // Last resort: any non-nav iframe is better than the nav one.
    const nonNav = all.find((c) => !isNavContents(c));
    if (nonNav) return nonNav;

    console.warn(
      "[pagevoice] getVisibleView: no match — returning first mounted Contents",
      { wantIndex, wantHref, mounted: all.length }
    );
    return all[0];
  }, []);

  /** Show the bars and restart the idle countdown. Called from every
   *  interaction — taps in the reading iframe, tap-zone page turns, keys,
   *  and toolbar presses. */
  const wakeChrome = useCallback(() => {
    // 2026-10-06: deliberately does NOT reveal the bottom bar any more.
    // The bar is an overlay owned by the carets; making an interaction
    // reveal it would resurrect the exact confusion of a bar that appears
    // and disappears on its own. Kept as a no-op hook so the existing
    // call sites (iframe taps, keys, tap-zone flips) stay wired and this
    // is a one-line change in either direction.
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Panels own the screen while open; a retracting bar underneath them
    // would be confusing, and closing the panel re-arms this anyway.
    if (tocOpen || settingsOpen || notesOpen || searchOpen || marksOpen) return;
    // Paused mid-listen means the user is reaching for Resume — leave the
    // controls up. While actually playing we DO retract: that is the point
    // of reading, and any tap brings them straight back.
    if (playing && paused) return;
    // 2026-10-06, from the BOOX ("Page button controls not found"):
    // the 4-second idle auto-hide is DISABLED. It made the reader
    // unusable on e-ink.
    //
    // Two things compound on that device. First, the "any tap brings
    // them back" promise is not kept: the reading content lives in an
    // epub.js iframe, so a tap on the text does not reach the window
    // pointerdown listener that calls wakeChrome(). Second, even when
    // the state does flip back, an e-ink panel does not necessarily
    // push a fresh frame for it — so the controls can be present in
    // the DOM and still not visible on the screen.
    //
    // Net effect for Thomas: the bar disappeared four seconds after
    // opening a book and he could not get it back, i.e. he could not
    // turn pages at all. A cluttered reader beats one you cannot use,
    // so the bars now simply stay. Re-adding any form of hiding needs
    // to be verified ON the BOOX, not in headless Chrome — and needs a
    // reveal affordance that is permanently on screen.
    //
    // (Kept the timer ref and wakeChrome() call sites intact so this is
    // a one-line revert in either direction.)
    return;
  }, [tocOpen, settingsOpen, notesOpen, searchOpen, marksOpen, playing, paused]);
  wakeChromeRef.current = wakeChrome;

  // Arm on mount and re-arm whenever a condition above changes.
  useEffect(() => {
    wakeChrome();
    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [wakeChrome]);

  // 2026-09-29 — "When interface hides text needs to get longer."
  //
  // The bars used to be translated out of view but left in the flex flow, so
  // the reading column kept its old height and a black strip sat where the
  // bar had been. Getting the extra lines means the bar has to leave the
  // layout, which means epub.js has to repaginate — which is exactly what
  // that earlier decision was avoiding, because a repagination loses the
  // reader's place.
  //
  // Resolved by making the repagination explicit and position-preserving
  // instead of avoiding it:
  //   - capture currentLocation().start.cfi BEFORE the layout changes
  //   - hand it to rendition.resize(w, h, cfi); epub.js's own onResized then
  //     calls display(epubcfi) once the new layout is measured, so the same
  //     passage is put back on screen
  //   - suppress position writes and the TTS restart across the round trip
  //
  // Anti-thrash, given the chrome hides after 4s and returns on any tap:
  //   - the 60ms timer is cleaned up on re-run, so a burst of toggles
  //     collapses into one resize
  //   - the height change is INSTANT, never animated. Animating it would
  //     repaginate on every frame of the transition
  //   - epub.js's manager.resize() early-returns when the measured stage
  //     size is unchanged, so a no-op toggle costs nothing
  const chromeLayoutKeyRef = useRef<string>("");
  useEffect(() => {
    const key = `${barsVisible}|${chromeVisible}`;
    if (chromeLayoutKeyRef.current === key) return;
    const isFirstRun = chromeLayoutKeyRef.current === "";
    chromeLayoutKeyRef.current = key;
    // Nothing rendered yet on the first pass — there is no place to keep.
    if (isFirstRun) return;
    const rendition = renditionRef.current;
    if (!rendition) return;

    let cfi: string | undefined;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cfi = (rendition.currentLocation() as any)?.start?.cfi ?? undefined;
    } catch {
      cfi = undefined;
    }

    suppressPositionSaveRef.current = true;
    // Same guard the auto-advance uses: stops the relocate handler tearing
    // down and restarting the play loop while the views are being rebuilt.
    ttsRestartGuardRef.current = true;

    const t = window.setTimeout(() => {
      try {
        // Undefined w/h keeps settings at "100%" and re-measures the
        // container, which is what actually changed.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (rendition as any).resize(undefined, undefined, cfi);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[pagevoice] chrome-resize repagination failed", err);
      }
      // Let the redisplay settle before re-enabling writes, so the relocate
      // it produces is the restored position rather than an intermediate.
      window.setTimeout(() => {
        suppressPositionSaveRef.current = false;
        ttsRestartGuardRef.current = false;
      }, 500);
    }, 60);
    return () => window.clearTimeout(t);
  }, [barsVisible, chromeVisible]);

  // Fullscreen at boot, and every loss afterwards. Reports how often the
  // Android bar actually comes back, which turns Thomas's "sometimes" into
  // a number — the second time today that word has hidden a specific,
  // reproducible condition.
  useEffect(() => {
    fsDiag("boot", {
      enabled: document.fullscreenEnabled ? "1" : "0",
      standalone:
        window.matchMedia?.("(display-mode: fullscreen)")?.matches
          ? "fullscreen"
          : window.matchMedia?.("(display-mode: standalone)")?.matches
            ? "standalone"
            : "browser",
    });
    const onChange = () => {
      if (!document.fullscreenElement) {
        turnsOutOfFsRef.current = 0;
        fsDiag("lost");
      } else {
        fsFailuresRef.current = 0;
        turnsOutOfFsRef.current = 0;
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [fsDiag]);

  // Any key press counts as interaction.
  useEffect(() => {
    const onAny = () => wakeChrome();
    window.addEventListener("keydown", onAny);
    window.addEventListener("pointerdown", onAny);
    return () => {
      window.removeEventListener("keydown", onAny);
      window.removeEventListener("pointerdown", onAny);
    };
  }, [wakeChrome]);

  // Heuristic: does this spine href look like a nav / TOC / contents page?
  // Used to skip past the table of contents on a fresh book open so the
  // reader (and TTS) land on actual prose, not a list of chapter titles.
  const isNavHref = (href: string | undefined): boolean => {
    if (!href) return false;
    const h = href.toLowerCase();
    return (
      /(^|\/)nav\.x?html?(\?|#|$)/.test(h) ||
      /(^|\/)toc\.x?html?(\?|#|$)/.test(h) ||
      /(^|\/)contents?\.x?html?(\?|#|$)/.test(h) ||
      /(^|\/)table[\-_]?of[\-_]?contents?\.x?html?(\?|#|$)/.test(h)
    );
  };

  // If the currently-displayed spine item is a nav/TOC/contents page,
  // advance through subsequent spine items until we hit something that
  // isn't. Used only for default opens (no saved position) — a real
  // resume CFI is honoured even if it lands on the TOC.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const skipPastNavSection = async (rendition: any, book: any) => {
    const loc = rendition.currentLocation?.();
    const startHref: string | undefined = loc?.start?.href;
    const startIndex: number | undefined = loc?.start?.index;
    if (!isNavHref(startHref) && (startIndex ?? 1) !== 0) {
      // Either not a nav-looking href and not section 0 — leave it alone.
      // (Some books legitimately start at content in section 0; only the
      //  nav-href check would trigger the skip.)
      if (!isNavHref(startHref)) return;
    }
    const items = book?.spine?.spineItems as Array<{ href?: string; idref?: string }> | undefined;
    if (!items?.length) return;
    // Walk forward from the current index for the first non-nav item.
    const startAt = typeof startIndex === "number" ? startIndex + 1 : 1;
    for (let i = startAt; i < items.length; i++) {
      const it = items[i];
      if (!it?.href) continue;
      if (isNavHref(it.href)) continue;
      await rendition.display(it.href);
      return;
    }
  };

  // Heuristic: does this iframe look like the EPUB nav/TOC document?
  // True if the URL or document type clearly says so. Used as the final
  // safety net so a half-initialised rendition can't drop us into the TOC.
  const isNavContents = (c: Contents): boolean => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ca = c as any;
      const href: string = (ca.sectionHref || ca.section?.href || "").toLowerCase();
      const winHref: string = (ca.window?.location?.href || "").toLowerCase();
      if (/(^|\/)nav\.x?html?$/.test(href) || /(^|\/)toc\.x?html?$/.test(href)) {
        return true;
      }
      if (/(^|\/)nav\.x?html?(\?|$)/.test(winHref) || /(^|\/)toc\.x?html?(\?|$)/.test(winHref)) {
        return true;
      }
      // EPUB 3 nav documents carry <nav epub:type="toc"> or properties="nav".
      const nav = ca.document?.querySelector?.('nav[epub\\:type="toc"], nav#toc');
      if (nav) return true;
    } catch {
      /* ignore */
    }
    return false;
  };

  // Build segments + tag DOM for the CURRENTLY visible section.
  const rebuildSegmentsForCurrentSection = useCallback(() => {
    const view = getVisibleView();
    const book = bookRef.current;
    const rendition = renditionRef.current;
    if (!view || !book || !rendition) return;
    contentsRef.current = view;
    const doc = view.document;
    const location = rendition.currentLocation() as any;
    const spineIndex = location?.start?.index ?? 0;
    const href = location?.start?.href ?? "";
    const segs = buildSegments({
      doc,
      bookId,
      spineIndex,
      href,
      level: tts.segmentLevel,
    });
    segmentsRef.current = segs;
    segmentsEpochRef.current += 1;
    currentIdxRef.current = 0;
    // Warmup: pre-fetch the first paragraph's audio in the background so
    // the very first Read-aloud tap plays with no delay. The provider
    // dedupes if it's already cached.
    if (segs.length > 0 && tts.provider !== "browser") {
      try {
        // Route warmup through the active provider so picking Kokoro
        // warms the Kokoro server, not Piper. Browser provider doesn't
        // implement prefetch — optional chaining no-ops for it.
        const p = providerByName(tts.provider);
        p.prefetch?.(segs[0], {
          rate: tts.rate,
          pitch: tts.pitch,
          volume: tts.volume,
          serverVoice: tts.serverVoice,
        });
      } catch {
        /* warmup is best-effort */
      }
    }
  }, [
    bookId,
    getVisibleView,
    tts.provider,
    tts.rate,
    tts.pitch,
    tts.volume,
    tts.segmentLevel,
    tts.serverVoice,
  ]);

  // ── AUTO PAGE-TURN DURING READ-ALOUD (2026-10-06) ──────────────────────
  // Thomas: "Voice was working but didn't turn the page. Kept reading though".
  //
  // CAUSE: buildSegments() is handed the whole section document, so
  // segmentsRef holds every paragraph in the CHAPTER. The play loop only
  // turned the page at `currentIdx >= segments.length - 1` — the end of the
  // chapter. A chapter is many screenfuls, so the voice read on through
  // paragraphs that had scrolled off the page while the page sat still.
  // Nothing was broken in the turning machinery; it was simply asked for at
  // chapter granularity instead of page granularity.
  //
  // FIX, following the /pv-reader/ foliate prototype: after each segment,
  // ask whether the NEXT one is still on the visible page, and turn if it
  // isn't. foliate does this with an onScreen() rect test against the
  // rendered document's viewport; epub.js paginates into horizontal columns
  // inside the iframe, so "on screen" is a horizontal band test on the
  // element's rect in the iframe's own coordinate space.
  //
  // TWO CAUTIONS, both deliberate:
  //  * Never turn BACKWARDS. If the element is behind the viewport the user
  //    has turned the page forward manually mid-playback; dragging him back
  //    to the audio would be fighting him. We leave the page where he put it
  //    and let the audio catch up.
  //  * Never turn while not playing, and always go through the same
  //    ttsRestartGuardRef dance the chapter-end turn uses, so the relocate
  //    this produces cannot be mistaken for a user navigation. The position
  //    IS saved by that relocate, and should be: the page we turn to is the
  //    page containing the words being spoken, which is exactly where the
  //    reader is.
  const segmentOnScreen = useCallback((seg: { elementSelector?: string }) => {
    // MEASURED THE HARD WAY (2026-10-06). Two traps here, both found by
    // running the real epub.js build in a harness rather than reasoning:
    //
    //  1. The obvious test — the element's rect against the IFRAME's own
    //     viewport — is wrong. In continuous+paginated mode the iframe is as
    //     wide as every column in the section at once (measured: 9216x768
    //     against a 1024x768 stage), so every paragraph in the chapter sits
    //     inside it and all 26 test paragraphs classified as "visible".
    //     The scrolling window is the STAGE, not the iframe, so the
    //     comparison has to happen in OUTER-document coordinates.
    //  2. `document.defaultView` is NULL on some views, and pre-rendered
    //     neighbour views report zero rects for everything. An earlier
    //     version bailed on both, which would have made this whole feature
    //     silently never fire.
    //
    // So: walk the stage's iframes, find the one where this element is
    // actually laid out, translate its rect into page coordinates, and
    // compare against the stage box. This is the same technique the resume
    // probe used to show the epigraph on screen and the dedication at
    // x = -757.
    // Returns a DIAGNOSTIC object, not just a verdict. The auto-turn has
    // now failed twice while measuring correctly in a harness, and every
    // one of its failure modes ("can't tell") is silent and looks exactly
    // like the original bug. `why` names which branch produced the answer
    // so the telemetry can tell them apart on Thomas's device.
    const d = {
      where: null as null | "behind" | "visible" | "ahead",
      why: "",
      frames: 0,
      found: 0,
      stageW: 0,
    };
    try {
      const stage = viewerRef.current;
      if (!stage) { d.why = "nostage"; return d; }
      if (!seg.elementSelector) { d.why = "nosel"; return d; }
      const sr = stage.getBoundingClientRect();
      d.stageW = Math.round(sr.width);
      if (sr.width === 0) { d.why = "stage0"; return d; }
      const frames = stage.querySelectorAll("iframe");
      d.frames = frames.length;
      if (frames.length === 0) { d.why = "noframes"; return d; }
      for (let i = 0; i < frames.length; i++) {
        const f = frames[i] as HTMLIFrameElement;
        let doc: Document | null = null;
        try { doc = f.contentDocument; } catch { d.why = "xorigin"; continue; }
        if (!doc) { d.why = "nodoc"; continue; }
        const el = doc.querySelector(seg.elementSelector) as HTMLElement | null;
        if (!el) continue;
        d.found += 1;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) { d.why = "zerorect"; continue; }
        const fr = f.getBoundingClientRect();
        const left = fr.left + r.left;
        const right = fr.left + r.right;
        // Tolerance so a paragraph straddling the column edge counts as
        // present rather than triggering a turn that immediately needs
        // turning back.
        // Thomas's long-paragraph clue: a paragraph that BEGINS on the
        // visible page and runs past its right edge was classified purely
        // by its start, so it read as "visible" and no turn happened —
        // then the turn came a paragraph later, which is the lateness he
        // described. Judge by how much of it is actually on screen: if
        // most of the paragraph lies beyond the right edge, the words
        // being spoken are on the next page and we should already be there.
        const TOL = 4;
        const visibleW = Math.min(right, sr.right) - Math.max(left, sr.left);
        const totalW = Math.max(1, right - left);
        d.why = "ok";
        if (right <= sr.left + TOL) d.where = "behind";
        else if (left >= sr.right - TOL) d.where = "ahead";
        else if (visibleW / totalW < 0.35) d.where = "ahead";
        else d.where = "visible";
        return d;
      }
      if (!d.why) d.why = d.found ? "allzero" : "notfound";
      return d;
    } catch (e) {
      d.why = "throw:" + String((e as Error)?.name || e).slice(0, 20);
      return d;
    }
  }, []);

  /** Turn forward until `seg` is on the visible page. Bounded, and stops the
   *  moment the location stops changing. Returns true if it is (or already
   *  was) visible, false if we gave up. */
  const bringSegmentOnScreen = useCallback(
    async (seg: { elementSelector?: string }) => {
      // ── THE GUARD MUST SPAN THE WHOLE OPERATION (2026-10-06) ──────────
      // MEASURED from /__pvdiag/turn on Thomas's BOOX: the turn itself
      // works — 17 attempts, 16 of which moved the location, with the
      // visibility check returning why=ok every time. What it also showed
      // was 25 `loopstart` beacons in one sitting: the play loop was being
      // torn down and restarted after almost every turn.
      //
      // Cause: ttsRestartGuardRef was set and cleared around the `next()`
      // call alone, in a finally that ran the moment next() resolved. The
      // `relocated` event the turn produces arrives AFTER that — so it
      // found the guard down, treated an auto-turn as a user navigation,
      // and called restartTTSFromCurrentLocation(). The restart re-probes
      // the top of the new page and begins again, which is why the segment
      // index in the telemetry jumps backwards (10 -> 9, then restart at
      // 3) and why the flip rate tripped the circuit breaker five times.
      //
      // So the guard is now raised ONCE for the whole operation and lowered
      // only after the relocations have settled.
      const MAX_TURNS = 6; // a paragraph spanning 6 screens is pathological
      ttsRestartGuardRef.current = true;
      try {
        return await (async () => {
      for (let i = 0; i < MAX_TURNS; i++) {
        if (!playingRef.current) return false;
        const where = segmentOnScreen(seg).where;
        // null = can't tell (not laid out, cross-origin, no selector). Fail
        // open: leave the page alone rather than turning on a guess.
        if (where === null || where === "visible" || where === "behind") {
          return where !== null;
        }
        const before = (() => {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const l = renditionRef.current?.currentLocation() as any;
            return l?.start?.cfi ?? null;
          } catch { return null; }
        })();
        // No per-call guard here: it is held for the whole operation above.
        await renditionRef.current?.next();
        await new Promise((r) => setTimeout(r, 120));
        const after = (() => {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const l = renditionRef.current?.currentLocation() as any;
            return l?.start?.cfi ?? null;
          } catch { return null; }
        })();
        if (!after || after === before) return false; // couldn't advance
      }
      return false;
        })();
      } finally {
        // Let the relocations this produced drain BEFORE lowering the
        // guard, or the last one restarts the play loop — which is the
        // whole bug this block documents. 350ms comfortably covers the
        // 120ms settle plus epub.js's own relocate dispatch.
        await new Promise((r) => setTimeout(r, 350));
        ttsRestartGuardRef.current = false;
      }
    },
    [segmentOnScreen],
  );

  // ---- TTS playback loop ----
  const ttsRestartGuardRef = useRef(false);
  // Bumps every time rebuildSegmentsForCurrentSection swaps the segments
  // array. The play loop waits for a bump after rendition.next() so it
  // never reads stale segments from the previous section — that was
  // letting the MJ Fox book race through pages because the loop hit the
  // section's last index, flipped, and re-hit the same last index of the
  // still-old segments before the rebuild landed.
  const segmentsEpochRef = useRef(0);
  // Recent page-flip timestamps (last 5 seconds). If we exceed a small
  // threshold, the loop pauses itself — a healthy book should not need
  // to flip more than a couple times per second in TTS, ever.
  const recentFlipsRef = useRef<number[]>([]);

  const highlightCurrent = useCallback(() => {
    const view = contentsRef.current;
    if (!view) return;
    const doc = view.document;
    doc.querySelectorAll(".pv-tts-active").forEach((el) =>
      el.classList.remove("pv-tts-active")
    );
    const seg = segmentsRef.current[currentIdxRef.current];
    if (!seg?.elementSelector) return;
    const target = doc.querySelector(seg.elementSelector) as HTMLElement | null;
    if (!target) return;
    target.classList.add("pv-tts-active");
    // In paginated mode we DON'T call scrollIntoView — epub.js owns the page
    // viewport and any DOM scroll would desync the page tracker. If the
    // highlighted paragraph is off-screen, the page-flip-on-segment-end logic
    // will move us forward when speech reaches the end of the current page.
  }, []);

  // Clear every highlight class from the current view. Used on stop and
  // before each segment so state doesn't leak between play sessions.
  const clearAllHighlights = useCallback(() => {
    const view = contentsRef.current;
    view?.document
      .querySelectorAll(".pv-w-active, .pv-tts-active")
      .forEach((el) => el.classList.remove("pv-w-active", "pv-tts-active"));
  }, []);

  const startTTSFromIndex = useCallback(
    async (startIdx: number) => {
      // Same correction as the button gate: only the browser provider
      // requires SpeechSynthesis. Refusing to start server playback because
      // window.speechSynthesis was missing is what made Play do nothing on
      // webviews that lack it.
      if (!canReadAloud(tts.provider)) {
        setError(
          tts.provider === "browser"
            ? "Read-aloud isn't supported in this browser. Try Chrome, Edge, or Safari, or switch to a server voice in Settings."
            : "Read-aloud isn't available on this device."
        );
        return;
      }
      // Guard against double-entry — if a play loop is already running
      // (user double-tapped Read aloud, or relocate-while-playing kicked off
      // a restart that's racing with this call), don't start another loop.
      if (playingRef.current && !pausedRef.current) {
        // Already playing; just advance to the new index.
        currentIdxRef.current = Math.max(
          0,
          Math.min(segmentsRef.current.length - 1, startIdx)
        );
        return;
      }
      if (segmentsRef.current.length === 0) {
        // Wait for the rendered event to land — the user tapped Play
        // before the iframe finished mounting. Poll for up to ~2 s,
        // rebuilding each tick. If we still have nothing after that,
        // surface the failure to the user via setError instead of the
        // silent console warn (the "Play just doesn't load" bug
        // hit on a fresh library tap 2026-06-27).
        for (let i = 0; i < 20; i++) {
          await new Promise((r) => setTimeout(r, 100));
          rebuildSegmentsForCurrentSection();
          if (segmentsRef.current.length > 0) break;
        }
        if (segmentsRef.current.length === 0) {
          console.warn("[pagevoice] no segments built after 2s wait");
          // Quiet failure (2026-07-10, per Thomas): don't replace the
          // reader with the full-screen error state — show a small
          // transient notice and leave Play available as the retry.
          setTtsNotice(
            "No readable text on this page yet — flip to a text page or tap ▶ again in a moment.",
          );
          if (ttsNoticeTimerRef.current !== null) {
            window.clearTimeout(ttsNoticeTimerRef.current);
          }
          ttsNoticeTimerRef.current = window.setTimeout(
            () => setTtsNotice(null),
            5000,
          );
          return;
        }
      }
      clearAllHighlights();
      // Session marker. Says WHICH loop is running — the auto-turn only
      // exists in this one, and the translated loop at startTranslatedLoop
      // has its own page-advance. If Thomas is reading with translation on,
      // the main-loop fix would never execute for him, and that is
      // indistinguishable from a broken fix without this line.
      turnDiagCountRef.current = 0;
      try {
        const q = new URLSearchParams({
          ev: "loopstart",
          loop: "main",
          segs: String(segmentsRef.current.length),
          translateOn: useReaderStore.getState().reader.translateOn ? "1" : "0",
          provider: String(tts.provider),
          b: bookId.slice(0, 12),
          ua: navigator.userAgent.slice(0, 32),
        });
        void fetch(`/__pvdiag/turn?${q.toString()}`, {
          method: "GET",
          cache: "no-store",
        }).catch(() => {});
      } catch {
        /* diagnostics never break a read */
      }
      playingRef.current = true;
      pausedRef.current = false;
      // Claim the global playback generation — any newer loop (either
      // mode) invalidates this one even if playingRef gets re-set.
      const myGen = ++playGenRef.current;
      setPlaying(true);
      setPaused(false);
      currentIdxRef.current = Math.max(
        0,
        Math.min(segmentsRef.current.length - 1, startIdx)
      );
      // Resolve voices once per play session — they're cached after the first
      // lookup so this is cheap on subsequent plays.
      const voices = await listVoices();
      const voice = voices.find((v) => v.voiceURI === tts.voiceURI);
      // Consecutive failed segments. Reset by any segment that speaks, so
      // an occasional bad paragraph never accumulates towards a stop.
      let consecutiveTtsFailures = 0;
      while (playingRef.current && playGenRef.current === myGen) {
        if (pausedRef.current) {
          await new Promise((r) => setTimeout(r, 150));
          continue;
        }
        const seg = segmentsRef.current[currentIdxRef.current];
        if (!seg) break;

        // ── TURN BEFORE SPEAKING (2026-10-07) ──────────────────────────
        // Thomas: "page turn is late on TTS ... it's turning after first
        // para on next page."
        //
        // The check used to run AFTER a segment finished, having already
        // incremented the index — so the question it asked was effectively
        // "was the thing I just handled still visible?", and the turn
        // landed a paragraph late. The voice read the first paragraph of
        // the next page while the previous page was still on screen.
        //
        // The decision belongs HERE: immediately before speaking segment
        // n, classify segment n. If it is ahead, turn first, then speak.
        // The page is then always showing the words being said.
        //
        // Everything that was working is preserved: forward-only (a
        // "behind" classification means he turned the page himself and we
        // leave it alone), the ttsRestartGuardRef dance that stops the
        // relocate restarting the loop, and the runaway-flip breaker.
        if (playingRef.current) {
          const now = performance.now();
          recentFlipsRef.current = recentFlipsRef.current.filter(
            (t) => now - t < 5000,
          );
          if (recentFlipsRef.current.length <= 5) {
            const d = segmentOnScreen(seg);
            if (d.where === "ahead") {
              recentFlipsRef.current.push(now);
              await bringSegmentOnScreen(seg);
            }
            if (turnDiagCountRef.current < 40) {
              turnDiagCountRef.current += 1;
              try {
                const q = new URLSearchParams({
                  ev: "pre",
                  i: String(currentIdxRef.current),
                  n: String(segmentsRef.current.length),
                  where: d.where ?? "null",
                  why: d.why,
                  turned: d.where === "ahead" ? "1" : "0",
                  b: bookId.slice(0, 12),
                  ua: navigator.userAgent.slice(0, 32),
                });
                void fetch(`/__pvdiag/turn?${q.toString()}`, {
                  method: "GET",
                  cache: "no-store",
                }).catch(() => {});
              } catch {
                /* diagnostics never break a read */
              }
            }
          }
        }
        highlightCurrent();

        // Word-by-word setup: wrap the active paragraph's words in spans
        // (idempotent), then forward boundary events to highlight the
        // currently-spoken word. We clear any prior word highlight first
        // so segments don't leak state. A time-based fallback also runs in
        // case the voice engine doesn't fire boundary events (most iOS
        // Safari voices and Chrome's network voices don't).
        let wordSpans: WordSpan[] = [];
        let wrapTarget: HTMLElement | null = null;
        const view = contentsRef.current;
        if (view && seg.elementSelector) {
          wrapTarget = view.document.querySelector(
            seg.elementSelector
          ) as HTMLElement | null;
          if (wrapTarget) {
            try {
              wordSpans = wrapWords(wrapTarget);
            } catch (err) {
              console.warn("[pagevoice] wrapWords failed", err);
            }
            view.document
              .querySelectorAll(".pv-w-active")
              .forEach((el) => el.classList.remove("pv-w-active"));
          }
        }
        // Patch the live debug toast with the wrap result so we can see
        // whether word-level highlighting is actually wired for this
        // segment. wrapCount=0 means wrapWords didn't run or returned
        // nothing — that's the diagnosis for a paragraph-only highlight.
        if (currentIdxRef.current === firstSegmentRef.current.idx) {
          const prev = ttsDebugInfoRef.current;
          if (prev) {
            setTtsDebugInfo({
              ...prev,
              wrapCount: wordSpans.length,
              boundaryFired: false,
            });
          }
        }

        // Compute a trim offset so the first segment of this play session
        // starts at the word visible at the top of the current page rather
        // than at the start of the paragraph (which may have begun on the
        // previous page). Only apply on the very first segment of a play.
        let trimOffset = 0;
        if (
          currentIdxRef.current === firstSegmentRef.current.idx &&
          firstSegmentRef.current.charOffset > 0 &&
          firstSegmentRef.current.segId === seg.id
        ) {
          trimOffset = firstSegmentRef.current.charOffset;
        }
        const segForSpeech =
          trimOffset > 0
            ? { ...seg, text: seg.text.slice(trimOffset) }
            : seg;

        // Time-based fallback word highlighter. Computes per-word durations
        // from rate (~180wpm at 1.0x is a good baseline) and advances the
        // highlight on a timer. If onboundary fires, we snap to its position
        // and reset the timer offset.
        const wpm = 180 * Math.max(0.5, Math.min(2, tts.rate));
        const wordsHere = wordSpans.filter(
          (w) => w.start >= trimOffset
        );
        let fallbackIdx = 0;
        let lastBoundaryAt = performance.now();
        let fallbackTimer: number | null = null;
        const tickFallback = () => {
          // Stop scheduling entirely once playback is no longer active.
          // Earlier code kept rescheduling forever after stop which leaked
          // a setTimeout chain until the current await completed.
          if (!playingRef.current) {
            fallbackTimer = null;
            return;
          }
          if (pausedRef.current) {
            fallbackTimer = window.setTimeout(tickFallback, 120);
            return;
          }
          // Don't move the word highlight until audio is actually
          // playing. Without this, the highlight starts stepping
          // through words during Kokoro's ~3s cold-start while no
          // sound is coming out yet — the visual outpaces the voice,
          // and by the time audio kicks in the highlight is already
          // several words ahead of the first spoken word. Wait for
          // the first boundary signal (real or synthetic — see
          // BrowserSS utter.onstart) to flip audioStartedRef.
          if (!audioStartedRef.current) {
            fallbackTimer = window.setTimeout(tickFallback, 120);
            return;
          }
          if (fallbackIdx >= wordsHere.length) {
            fallbackTimer = null;
            return;
          }
          // Don't override an onboundary that just fired in the last 250ms.
          if (performance.now() - lastBoundaryAt < 250) {
            fallbackTimer = window.setTimeout(tickFallback, 120);
            return;
          }
          const span = wordsHere[fallbackIdx];
          if (span) {
            view?.document
              .querySelectorAll(".pv-w-active")
              .forEach((el) => el.classList.remove("pv-w-active"));
            span.el.classList.add("pv-w-active");
          }
          fallbackIdx++;
          // Approximate ms-per-word: 60_000 / wpm.
          const ms = Math.max(120, 60000 / wpm);
          fallbackTimer = window.setTimeout(tickFallback, ms);
        };
        // Kick off the fallback shortly after we start speaking.
        fallbackTimer = window.setTimeout(tickFallback, 300);

        // Pre-fetch the next segment in the background so server-side TTS
        // is gapless. The provider de-dupes if it's already cached.
        const upcoming = segmentsRef.current[currentIdxRef.current + 1];
        if (upcoming && tts.provider !== "browser") {
          try {
            activeProviderRef.current.prefetch?.(upcoming, {
              rate: tts.rate,
              pitch: tts.pitch,
              volume: tts.volume,
              serverVoice: tts.serverVoice,
            });
          } catch {
            /* prefetch failures are non-fatal */
          }
        }

        // Arm the 3-2-1 countdown. Only renders when audio takes >400ms
        // to actually start — fast loads briefly arm-and-clear without
        // ever showing a number. Cleared by the first onBoundary inside
        // the speakSegment options below.
        audioStartedRef.current = false;
        if (countdownIntervalRef.current !== null) {
          window.clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        {
          const showAt = performance.now() + 400;
          const hardTimeoutAt = performance.now() + 30000;
          countdownIntervalRef.current = window.setInterval(() => {
            if (audioStartedRef.current || !playingRef.current) {
              setCountdownNum(null);
              if (countdownIntervalRef.current !== null) {
                window.clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              return;
            }
            const now = performance.now();
            if (now < showAt) return;
            // 30-second hard timeout — if audio still hasn't fired
            // anything by then, the request is wedged. Stop politely
            // and let the user decide; nothing more useful we can do.
            if (now >= hardTimeoutAt) {
              setCountdownNum(null);
              if (countdownIntervalRef.current !== null) {
                window.clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              return;
            }
            const elapsed = (now - showAt) / 1000;
            // Count 3 → 2 → 1, then HOLD on 1 until audio actually
            // starts or the hard timeout kicks. Previous version was
            // clearing the display at 3 s, which is exactly when Kokoro's
            // first paragraph is still arriving — users would see the
            // overlay finish and then silence, which read as "voice
            // didn't load" even though it was still on its way.
            setCountdownNum(Math.max(1, Math.ceil(3 - elapsed)));
          }, 100);
        }

        try {
          await activeProviderRef.current.speakSegment(segForSpeech, {
            rate: tts.rate,
            pitch: tts.mode === "classic" ? 0.95 : tts.pitch,
            volume: tts.volume,
            voice,
            serverVoice: tts.serverVoice,
            onBoundary: (charIndex) => {
              // Mark audio as actually playing — clears the countdown
              // overlay if it was showing, and prevents the late-arming
              // setInterval from re-introducing it on the next tick.
              if (!audioStartedRef.current) {
                audioStartedRef.current = true;
                setCountdownNum(null);
              }
              if (!wordSpans.length) return;
              lastBoundaryAt = performance.now();
              // First-ever boundary event during this play session —
              // flip a toast flag so we can see in the diagnostic
              // overlay whether the engine fires real boundary events
              // (browser SpeechSynthesis on some voices) or we're
              // relying entirely on the time-based fallback (Piper).
              const cur = ttsDebugInfoRef.current;
              if (cur && !cur.boundaryFired) {
                setTtsDebugInfo({ ...cur, boundaryFired: true });
              }
              // charIndex is into segForSpeech.text; shift by trim offset
              // back into the original paragraph coordinates so we match
              // the span data-pv-wo values.
              const trueIndex = charIndex + trimOffset;
              const span = wordSpans.find(
                (w) => trueIndex >= w.start && trueIndex < w.end
              );
              if (!span) return;
              const doc = view?.document;
              doc
                ?.querySelectorAll(".pv-w-active")
                .forEach((el) => el.classList.remove("pv-w-active"));
              span.el.classList.add("pv-w-active");
              // Re-sync the fallback index so it picks up from here.
              fallbackIdx = Math.max(
                fallbackIdx,
                wordsHere.findIndex((w) => w === span) + 1
              );
            },
          });
          // Spoke without throwing — the server is answering, so forget any
          // earlier stumbles.
          consecutiveTtsFailures = 0;
        } catch (err) {
          console.warn("[pagevoice] TTS error", err);
          if (countdownIntervalRef.current !== null) {
            window.clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          setCountdownNum(null);
          // 2026-09-29: this used to `break` on the FIRST failed segment, so
          // a single blip ended the reading session and the user had to tap
          // Play again — "stops at a new paragraph". ttsService now retries
          // the fetch twice internally, so reaching here means three
          // failures in a row on this segment. Skip it and carry on; only
          // give up once several consecutive segments fail, which means the
          // server really is gone rather than one request having been
          // unlucky.
          consecutiveTtsFailures += 1;
          if (consecutiveTtsFailures >= 3) {
            setTtsNotice("Couldn't load the voice — tap ▶ to try again.");
            if (ttsNoticeTimerRef.current !== null) {
              window.clearTimeout(ttsNoticeTimerRef.current);
            }
            ttsNoticeTimerRef.current = window.setTimeout(
              () => setTtsNotice(null),
              5000,
            );
            break;
          }
          // Fall through to the advance at the bottom of the loop so the
          // next paragraph is attempted.
        } finally {
          if (fallbackTimer !== null) {
            window.clearTimeout(fallbackTimer);
            fallbackTimer = null;
          }
          // Clear both word and paragraph highlights at end of segment.
          view?.document
            .querySelectorAll(".pv-w-active, .pv-tts-active")
            .forEach((el) =>
              el.classList.remove("pv-w-active", "pv-tts-active")
            );
        }
        if (!playingRef.current) break;
        if (currentIdxRef.current >= segmentsRef.current.length - 1) {
          // Circuit breaker: if we've flipped pages a lot in the last
          // five seconds, something's wrong (empty section, runaway
          // loop, server returning silent audio). Pause cleanly instead
          // of grinding through 200 pages in 10 seconds — that was the
          // MJ Fox book symptom.
          const now = performance.now();
          recentFlipsRef.current = recentFlipsRef.current.filter(
            (t) => now - t < 5000,
          );
          recentFlipsRef.current.push(now);
          if (recentFlipsRef.current.length > 5) {
            console.warn(
              "[pv-tts] page-flip rate too high (%d in 5s) — pausing playback",
              recentFlipsRef.current.length,
            );
            break;
          }

          // Flip to next page; the relocate handler will rebuild segments
          // and we resume on the new chapter. Wait for the rebuild to
          // actually land (epoch bump) instead of a fixed 300ms — that
          // was the other half of the page-flip storm bug, the loop
          // racing past rebuild and hitting end-of-section on the OLD
          // segments array.
          // 2026-09-29 — THE "STOPS AT A NEW PARAGRAPH" BUG.
          //
          // This used to wait for segmentsEpochRef to bump and stop if it
          // didn't, blaming an iOS/WebKit page-turn limitation. The epoch
          // bump can't arrive, and not because of iOS:
          //
          //   - the epoch only bumps inside rebuildSegmentsForCurrentSection
          //   - during auto-advance that is only reachable via `rendered`
          //     (fires when a SECTION paints, not on a page turn inside one)
          //     or via restartTTSFromCurrentLocation off `relocated`
          //   - `relocated` fires DURING `await next()`, while
          //     ttsRestartGuardRef is still true, so it is suppressed
          //
          // So turning a page inside a chapter — the ordinary case, since a
          // section is a whole chapter and a page is one screenful — always
          // left the epoch unchanged and hit the stop below. Playback read
          // the paragraphs on screen, then stopped. Every screenful, on
          // every platform.
          //
          // Fixed by asking the question directly: did the location move?
          // If it did, rebuild the segments ourselves (the guard is down by
          // then, and an explicit rebuild is what the suppressed relocate
          // would have done anyway) and keep reading. Only genuinely stop
          // when the reader really cannot advance.
          const locBefore = (() => {
            try {
              const l = renditionRef.current?.currentLocation() as any;
              return l?.start?.cfi ?? null;
            } catch { return null; }
          })();
          // 2026-10-06 — SECOND RESTART PATH. Identical shape to the bug
          // the /__pvdiag/turn telemetry found in bringSegmentOnScreen:
          // the guard was lowered the instant next() resolved, so the
          // `relocated` it produces arrived with the guard DOWN and
          // restartTTSFromCurrentLocation() tore the play loop down and
          // started a new one. After fixing the auto-turn the loopstart
          // count fell from 25 to 11 but did not reach 1 — this is the
          // remainder. Hold the guard across the settle, same as there.
          ttsRestartGuardRef.current = true;
          await renditionRef.current?.next();
          // Let epub.js settle the new page before we read its DOM, and
          // let the relocation drain before lowering the guard.
          await new Promise((r) => setTimeout(r, 350));
          ttsRestartGuardRef.current = false;
          const locAfter = (() => {
            try {
              const l = renditionRef.current?.currentLocation() as any;
              return l?.start?.cfi ?? null;
            } catch { return null; }
          })();
          const advanced = !!locAfter && locAfter !== locBefore;
          if (!advanced) {
            // Genuinely stuck: end of book, or epub.js really couldn't turn
            // the page. Say so and stop cleanly.
            setTtsNotice("Reached the end of the page — turn the page to keep reading.");
            if (ttsNoticeTimerRef.current !== null) {
              window.clearTimeout(ttsNoticeTimerRef.current);
            }
            ttsNoticeTimerRef.current = window.setTimeout(() => setTtsNotice(null), 4000);
            break;
          }
          // The page moved. Rebuild against what's on screen now — this is
          // the call the suppressed `relocated` would have made, and it
          // bumps the epoch so the rest of the loop sees fresh segments.
          rebuildSegmentsForCurrentSection();
          currentIdxRef.current = 0;
          if (segmentsRef.current.length === 0) {
            // A page with no readable text (a full-page image plate). Don't
            // stop the session on it — try the next page, bounded by the
            // page-flip circuit breaker above.
            continue;
          }
        } else {
          // 2026-10-07: the auto-turn that used to live here, AFTER the
          // segment was spoken and the index incremented, has moved to the
          // top of the loop — see "TURN BEFORE SPEAKING". Running it in
          // both places would turn twice. All that remains is the advance.
          currentIdxRef.current++;
        }
      }
      // A superseded loop (newer generation took over) must not stomp
      // on its replacement's shared state.
      if (playGenRef.current !== myGen) return;
      playingRef.current = false;
      setPlaying(false);
      setPaused(false);
      // Clear highlight on stop
      const view = contentsRef.current;
      view?.document
        .querySelectorAll(".pv-tts-active")
        .forEach((el) => el.classList.remove("pv-tts-active"));
    },
    [
      highlightCurrent,
      rebuildSegmentsForCurrentSection,
      // Both are stable (empty / single-stable dep) so this never
      // re-creates the loop; listed so the dependency set stays honest.
      segmentOnScreen,
      bringSegmentOnScreen,
      tts.mode,
      tts.pitch,
      tts.rate,
      tts.voiceURI,
      tts.volume,
    ]
  );

  /** Probe the top of the visible viewport and return the segment index +
   *  in-paragraph character offset that should be the start of playback.
   *  Used by both initial Read-aloud and by relocate-driven restarts so
   *  page-flips while playing don't restart the chapter from paragraph 0. */
  const probeTopOfVisiblePage = useCallback((): {
    idx: number;
    charOffset: number;
    path: string;
    cfi?: string;
  } => {
    let startIdx = 0;
    let trimCharOffset = 0;
    let foundAny = false;
    let probePath = "none";
    let probeCfi: string | undefined;
    const view = getVisibleView();
    if (!view) return { idx: startIdx, charOffset: trimCharOffset, path: "no-view" };

    // CFI PATH: ask epub.js directly. rendition.currentLocation().start.cfi
    // is epub.js's authoritative pointer to the FIRST CHARACTER OF THE
    // VISIBLE PAGE — it knows the column layout because it computed it.
    // Convert that CFI to a DOM Range and the start container is the
    // first visible text node. From there we walk up to find the
    // [data-pv-seg] paragraph and sum text-node lengths to the caret.
    //
    // Adopted 260625-1100 after the segment-iterate FAST PATH was
    // observed picking column-0 headings (chapter titles, book titles)
    // on multi-column transform layouts where iframe scrollLeft stays
    // at 0 — visible in the debug toast as "Picked: Henry Moore at The
    // British Museum" / "1Mrs. Maxine Hortence Simmons" while the page
    // was mid-chapter. The geometric heuristic can't disambiguate which
    // column is visible in that layout; the CFI can.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const loc = (renditionRef.current as any)?.currentLocation?.() as
        | { start?: { cfi?: string } }
        | undefined;
      const startCfi = loc?.start?.cfi;
      probeCfi = startCfi;
      if (startCfi) {
        const cfi = new EpubCFI(startCfi);
        const range = cfi.toRange(view.document);
        const startNode = range?.startContainer ?? null;
        const startOffset = range?.startOffset ?? 0;
        const startEl =
          startNode && startNode.nodeType === Node.TEXT_NODE
            ? (startNode.parentElement as HTMLElement | null)
            : (startNode as HTMLElement | null);
        const tagged = startEl?.closest<HTMLElement>("[data-pv-seg]") ?? null;
        if (tagged) {
          const segMatch = findNearestSegment(segmentsRef.current, tagged);
          const idx = segMatch
            ? segmentsRef.current.findIndex((s) => s.id === segMatch.id)
            : -1;
          if (idx >= 0) {
            startIdx = idx;
            foundAny = true;
            const fullText = segmentsRef.current[idx].text;
            // Sum text-node lengths up to and including the caret.
            let charsBefore = 0;
            try {
              const walker = view.document.createTreeWalker(
                tagged,
                NodeFilter.SHOW_TEXT,
                null,
              );
              let tn: Node | null = walker.nextNode();
              while (tn) {
                if (tn === startNode) {
                  charsBefore += startOffset;
                  break;
                }
                charsBefore += (tn.nodeValue ?? "").length;
                tn = walker.nextNode();
              }
              while (
                charsBefore > 0 &&
                /\S/.test(fullText[charsBefore - 1] ?? "")
              ) {
                charsBefore--;
              }
            } catch {
              /* if Range walk fails, leave charsBefore at 0 */
            }
            trimCharOffset = Math.max(
              0,
              Math.min(fullText.length, charsBefore),
            );
            probePath = "cfi";
            return {
              idx: startIdx,
              charOffset: trimCharOffset,
              path: probePath,
              cfi: probeCfi,
            };
          }
        }
      }
    } catch (err) {
      console.warn("[pv-tts] CFI-path probe failed, falling through", err);
      /* fall through to FAST PATH then LEGACY */
    }

    // FAST PATH: iterate every tagged segment in this section and pick
    // the first one whose bounding rect is at or below the top of the
    // viewport AND overlaps horizontally with the visible column.
    //
    // This is GATED: epub.js paginated mode lays out content using CSS
    // columns inside the iframe. Some builds horizontally scroll the
    // iframe (scrollingElement.scrollLeft > 0) to advance pages — in
    // that mode getBoundingClientRect returns viewport-relative coords
    // and our horizontal filter works. Other builds use transforms on
    // the iframe's parent and leave the iframe's own scroll at 0 — in
    // that mode every column's rect.left looks like a small positive
    // number from the iframe's perspective, the filter accepts column
    // 0 even when the user is on column 5, and the probe latches onto
    // the chapter title at column 0 (the 260625-1051 toast on Mrs
    // American Pie: section index_split_004.html was right, page was
    // mid-chapter, but Picked was the "1Mrs. Maxine Hortence Simmons"
    // heading — a column-0 element). The caret-from-point path further
    // down handles columns natively via the browser, so we let that
    // run first when this is the suspect layout.
    try {
      const view2 = view;
      const w2 = view2.window;
      const vh2 = w2?.innerHeight ?? view2.document.documentElement.clientHeight;
      const vw2 = w2?.innerWidth ?? view2.document.documentElement.clientWidth;
      const scrollEl =
        view2.document.scrollingElement || view2.document.documentElement;
      const docScrollLeft = scrollEl?.scrollLeft ?? 0;
      const docScrollWidth = view2.document.body?.scrollWidth ?? 0;
      // Multi-column transform layout = body wider than viewport AND
      // iframe itself isn't scrolled. In that case the FAST PATH lies;
      // bail and let the caret-from-point sweep handle it.
      if (docScrollLeft === 0 && docScrollWidth > vw2 * 1.5) {
        throw new Error("transform-paged layout — skip fast path");
      }
      const segEls = Array.from(
        view2.document.querySelectorAll<HTMLElement>("[data-pv-seg]")
      );
      const TOP_GUARD = -2;
      const LEFT_GUARD = -2;
      const isOnVisibleColumn = (r: DOMRect) =>
        r.right > LEFT_GUARD && r.left < vw2;
      let firstVisible: HTMLElement | null = null;
      for (const el of segEls) {
        const r = el.getBoundingClientRect();
        if (!isOnVisibleColumn(r)) continue;        // off in a previous/next page-column
        if (r.bottom <= TOP_GUARD) continue;        // entirely above the page
        if (r.top >= vh2) break;                    // walked past the bottom
        if (r.top >= TOP_GUARD) { firstVisible = el; break; }
        // Segment straddles the top of the page — user is partway through
        // it. Pick it and we'll compute a char-offset below so TTS resumes
        // at the visible portion, not the segment's start.
        if (r.top < TOP_GUARD && r.bottom > TOP_GUARD) {
          firstVisible = el;
          break;
        }
      }
      if (firstVisible) {
        const segMatch = findNearestSegment(segmentsRef.current, firstVisible);
        const idx = segMatch
          ? segmentsRef.current.findIndex((s) => s.id === segMatch.id)
          : -1;
        if (idx >= 0) {
          startIdx = idx;
          foundAny = true;
          const fullText = segmentsRef.current[idx].text;

          // Column-aware trim-offset. The picked paragraph can straddle a
          // page break — in CSS-columns layout the previous-page portion
          // is in the column to the LEFT of the visible viewport, with
          // overlapping vertical coordinates. Walk text nodes and check
          // BOTH vertical AND horizontal position to find the first
          // text node that's actually inside the visible column.
          let charsBefore = 0;
          try {
            const walker = view2.document.createTreeWalker(
              firstVisible,
              NodeFilter.SHOW_TEXT,
              null,
            );
            const range = view2.document.createRange();
            let tn: Node | null = walker.nextNode();
            const isVisible = (rect: DOMRect): boolean => {
              if (rect.bottom < TOP_GUARD) return false;
              if (rect.top >= vh2) return false;
              if (rect.right < LEFT_GUARD) return false;
              if (rect.left >= vw2) return false;
              return true;
            };
            while (tn) {
              // getClientRects() returns one DOMRect per line / column,
              // so for a text node that spans the page break we can find
              // the SPECIFIC sub-rect that's in the visible column. The
              // old getBoundingClientRect() collapsed multiple lines into
              // a single bounding box, which is what let "Mrs. Maxine"
              // (previous page's part of the same paragraph) win the
              // probe even though it's in a different column.
              range.selectNodeContents(tn);
              const rects = Array.from(range.getClientRects());
              let found = -1;
              for (let i = 0; i < rects.length; i++) {
                if (isVisible(rects[i])) { found = i; break; }
              }
              if (found >= 0) {
                // Estimate where in this text node the visible portion
                // begins. Each rect approximately corresponds to one
                // line; advance by (found / rects.length) of the node's
                // text length. Good enough to land in the right line.
                const nodeLen = (tn.nodeValue ?? "").length;
                const frac = rects.length > 0 ? found / rects.length : 0;
                charsBefore += Math.round(frac * nodeLen);
                break;
              }
              charsBefore += (tn.nodeValue ?? "").length;
              tn = walker.nextNode();
            }
            // Back up to whitespace so we don't start mid-word.
            while (
              charsBefore > 0 &&
              /\S/.test(fullText[charsBefore - 1] ?? "")
            ) {
              charsBefore--;
            }
          } catch {
            /* if Range fails, fall through with charsBefore=0 */
          }
          trimCharOffset = Math.max(0, Math.min(fullText.length, charsBefore));
          probePath = "fast";
          return {
            idx: startIdx,
            charOffset: trimCharOffset,
            path: probePath,
            cfi: probeCfi,
          };
        }
      }
    } catch {
      /* fall through to the legacy caret-from-point sweep below */
    }
    const w = view.window;
    const vw = w?.innerWidth ?? view.document.documentElement.clientWidth;
    const vh = w?.innerHeight ?? view.document.documentElement.clientHeight;
    type CaretAPI = {
      caretPositionFromPoint?: (
        x: number,
        y: number
      ) => { offsetNode: Node; offset: number } | null;
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    const docAny = view.document as unknown as CaretAPI;
    // Probe a much wider y range than before — many EPUBs have heavy top
    // margins (chapter titles, drop caps, image headers) and the first
    // real paragraph can be 100+ px down the visible viewport.
    const ys = [
      4, 12, 24, 40, 60, 80,
      100, 130, 170, 220,
      Math.round(vh * 0.35), Math.round(vh * 0.5),
    ];
    const xs = [
      8, 24,
      Math.round(vw * 0.2),
      Math.round(vw * 0.35),
      Math.round(vw * 0.5),
      Math.round(vw * 0.7),
      Math.round(vw * 0.85),
    ];
    const docCfn = docAny.caretPositionFromPoint?.bind(docAny);
    const docCrn = docAny.caretRangeFromPoint?.bind(docAny);

    probe: for (const y of ys) {
      for (const x of xs) {
        // Try the caret API first — gives us both segment AND char offset.
        const caret = docCfn?.(x, y) ?? docCrn?.(x, y);
        let node: Node | null = null;
        let offset = 0;
        if (caret) {
          if ("offsetNode" in caret) {
            node = caret.offsetNode;
            offset = caret.offset;
          } else {
            node = caret.startContainer;
            offset = caret.startOffset;
          }
        }
        // Fallback to elementFromPoint when the caret API returns nothing
        // useful — handles columnar / weird-flow EPUBs where caret APIs
        // are flaky. We lose the char-offset precision but still find the
        // right paragraph.
        let tagged: HTMLElement | null = null;
        if (node) {
          const el =
            node.nodeType === Node.TEXT_NODE
              ? (node.parentElement as HTMLElement | null)
              : (node as HTMLElement);
          tagged = el?.closest<HTMLElement>("[data-pv-seg]") ?? null;
        }
        if (!tagged) {
          const el2 = view.document.elementFromPoint(x, y) as
            | HTMLElement
            | null;
          tagged = el2?.closest<HTMLElement>("[data-pv-seg]") ?? null;
          node = null; // no usable caret → trim offset will be 0
        }
        if (!tagged) continue;

        const segMatch = findNearestSegment(segmentsRef.current, tagged);
        const idx = segMatch
          ? segmentsRef.current.findIndex((s) => s.id === segMatch.id)
          : -1;
        if (idx < 0) continue;
        startIdx = idx;
        foundAny = true;

        const fullText = segmentsRef.current[idx].text;
        if (node) {
          let charsBefore = 0;
          const walker = view.document.createTreeWalker(
            tagged,
            NodeFilter.SHOW_TEXT,
            null
          );
          let tn: Node | null = walker.nextNode();
          while (tn) {
            if (tn === node) {
              charsBefore += offset;
              break;
            }
            charsBefore += (tn.nodeValue ?? "").length;
            tn = walker.nextNode();
          }
          while (
            charsBefore > 0 &&
            /\S/.test(fullText[charsBefore - 1] ?? "")
          ) {
            charsBefore--;
          }
          trimCharOffset = Math.max(0, Math.min(fullText.length, charsBefore));
        } else {
          // Caret API didn't give us a usable node, but elementFromPoint
          // matched a tagged segment that spans the page break. Without a
          // trim offset, TTS would read the entire segment from its first
          // character — which for a multi-page paragraph means reading
          // the PREVIOUS page out loud before the visible one. Walk the
          // segment's text nodes and find the first node whose bounding
          // rect sits at or below the top of the visible viewport; use
          // chars-before-that-node as the offset.
          let charsBefore = 0;
          try {
            const walker = view.document.createTreeWalker(
              tagged,
              NodeFilter.SHOW_TEXT,
              null
            );
            const range = view.document.createRange();
            let tn: Node | null = walker.nextNode();
            // Anything within 2px of the top counts as "the visible start";
            // EPUBs often have small line-height padding above the column.
            const TOP_THRESHOLD = -2;
            while (tn) {
              range.selectNodeContents(tn);
              const rect = range.getBoundingClientRect();
              if (rect.bottom >= TOP_THRESHOLD && rect.top >= TOP_THRESHOLD) {
                // This whole text node starts inside the visible viewport.
                break;
              }
              if (rect.bottom >= TOP_THRESHOLD && rect.top < TOP_THRESHOLD) {
                // This node straddles the top — estimate which character
                // crosses by proportion of the rect that's still above.
                const above = TOP_THRESHOLD - rect.top;
                const total = rect.bottom - rect.top || 1;
                const frac = Math.max(0, Math.min(1, above / total));
                const len = (tn.nodeValue ?? "").length;
                charsBefore += Math.round(frac * len);
                break;
              }
              charsBefore += (tn.nodeValue ?? "").length;
              tn = walker.nextNode();
            }
            // Back up to the nearest whitespace so we don't start mid-word.
            while (
              charsBefore > 0 &&
              /\S/.test(fullText[charsBefore - 1] ?? "")
            ) {
              charsBefore--;
            }
          } catch {
            /* if Range fails for any reason, fall through with 0 */
          }
          trimCharOffset = Math.max(0, Math.min(fullText.length, charsBefore));
        }
        break probe;
      }
    }
    if (!foundAny) {
      console.warn(
        "[pagevoice] probe found no tagged paragraph in the viewport — falling back to segment 0"
      );
      probePath = "none";
    } else {
      probePath = "legacy";
    }
    return {
      idx: startIdx,
      charOffset: trimCharOffset,
      path: probePath,
      cfi: probeCfi,
    };
  }, [getVisibleView]);

  /**
   * Resolve an EPUB href through every variant epub.js's display() might
   * accept, and navigate to the first one that actually mounts a Section.
   *
   * Used by:
   *   - the Contents (TOC) panel
   *   - in-book links (front-matter Contents, footnotes, cross-refs)
   *
   * display() silently no-ops when the href doesn't EXACTLY match a spine
   * entry — no throw, no rejection, no navigation. Trying multiple
   * variants (raw, spine.get resolved, decoded, fragment-stripped, leading-
   * slash-stripped, last-segment-only) catches the malformed-href EPUBs
   * that exist in the wild.
   */
  const navigateToHref = useCallback(async (href: string) => {
    const r = renditionRef.current;
    const book = bookRef.current;
    if (!r || !href) return;
    const candidates: string[] = [];
    const push = (s?: string) => {
      if (s && !candidates.includes(s)) candidates.push(s);
    };
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const spineItem = (book as any)?.spine?.get?.(href);
      push(spineItem?.href);
      push(spineItem?.url);
    } catch {
      /* spine.get can throw on unparseable href */
    }
    push(href);
    try {
      push(decodeURIComponent(href));
    } catch {
      /* malformed URI */
    }
    push(href.split("#")[0]);
    push(href.replace(/^\/+/, ""));
    push(href.split("#")[0].replace(/^\/+/, ""));
    push(href.split("/").pop() ?? "");
    for (const target of candidates) {
      if (!target) continue;
      try {
        const result = (await r.display(target)) as unknown;
        if (result) return;
      } catch {
        /* try next candidate */
      }
    }
    // Last-ditch fallback: iterate the actual spine and match by
    // basename. Some books (Mr & Mrs American Pie's split-EPUB
    // layout, 2026-06-26) have TOC hrefs that point to a logical
    // chapter path that doesn't directly resolve through spine.get()
    // or display() — but the spine item's own href does. Compare
    // basenames so case- and prefix-mismatches don't block us.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const spineItems: any[] = (book as any)?.spine?.items ?? [];
      const wantTail = (href.split("#")[0].split("/").pop() ?? "")
        .toLowerCase();
      if (wantTail && spineItems.length) {
        for (const item of spineItems) {
          const candTail = (
            (item?.href || item?.url || "")
              .split("#")[0]
              .split("/")
              .pop() ?? ""
          ).toLowerCase();
          if (candTail && candTail === wantTail) {
            try {
              const result = (await r.display(item.href || item.url)) as unknown;
              if (result) return;
            } catch {
              /* fall through and warn */
            }
            break;
          }
        }
      }
    } catch {
      /* spine inspection is best-effort */
    }
    console.warn(
      "[pagevoice] in-book navigation failed — no candidate matched",
      href,
      candidates
    );
  }, []);

  /** Extract the actual text BETWEEN currentLocation.start.cfi and
   *  currentLocation.end.cfi — that's the prose on the visible page,
   *  not the whole chapter. Used by TranslatePanel so single-page
   *  translation actually translates one page. */
  const getVisiblePageText = useCallback((): {
    text: string;
    cfi: string | undefined;
    paras?: string[];
  } => {
    const r = renditionRef.current;
    const view = getVisibleView();
    if (!r || !view) return { text: "", cfi: undefined };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loc = (r as any).currentLocation?.() as
      | { start?: { cfi?: string }; end?: { cfi?: string } }
      | undefined;
    const startCfi = loc?.start?.cfi;
    const endCfi = loc?.end?.cfi;
    try {
      if (startCfi && endCfi) {
        const sRange = new EpubCFI(startCfi).toRange(view.document);
        const eRange = new EpubCFI(endCfi).toRange(view.document);
        if (sRange && eRange) {
          const range = view.document.createRange();
          range.setStart(sRange.startContainer, sRange.startOffset);
          range.setEnd(eRange.endContainer, eRange.endOffset);
          const text = (range.toString() || "").replace(/\s+/g, " ").trim();
          if (text.length > 40) {
            // Paragraph boundaries for the chunked translator: clip
            // each block element that intersects the visible range to
            // the range, so partial first/last paragraphs are the
            // partial text actually on screen.
            const paras: string[] = [];
            try {
              const blocks = view.document.querySelectorAll(
                "p, h1, h2, h3, h4, h5, h6, li, blockquote",
              );
              blocks.forEach((el) => {
                if (!range.intersectsNode(el)) return;
                const r2 = view.document.createRange();
                r2.selectNodeContents(el);
                if (r2.compareBoundaryPoints(Range.START_TO_START, range) < 0) {
                  r2.setStart(range.startContainer, range.startOffset);
                }
                if (r2.compareBoundaryPoints(Range.END_TO_END, range) > 0) {
                  r2.setEnd(range.endContainer, range.endOffset);
                }
                const t = (r2.toString() || "").replace(/\s+/g, " ").trim();
                if (t) paras.push(t);
              });
            } catch {
              /* paragraph extraction is best-effort — chunker falls
                 back to sentence splitting */
            }
            return { text, cfi: startCfi, paras: paras.length ? paras : undefined };
          }
        }
      }
    } catch (err) {
      console.warn("[pv-translate] CFI range extraction failed", err);
    }
    // Fallback: concatenate every segment in the current section. The
    // translate panel limits to 6000 chars itself so this won't blow up
    // a chapter-sized blob to the LLM.
    const segParas = segmentsRef.current.map((s) => s.text).filter(Boolean);
    const segText = segParas.join("\n\n").trim();
    return {
      text: segText,
      cfi: startCfi,
      paras: segParas.length ? segParas : undefined,
    };
  }, [getVisibleView]);

  // Refresh the TranslatePanel's source-text snapshot whenever the
  // overlay is on AND the rendition has moved (or on first activation).
  // 600 ms settle (was 80 ms, 2026-07-11): every snapshot fires a
  // /lib/translate chunk, and the browser aborting a fetch does NOT
  // cancel the llama generation behind pv-lib's proxy — rapid page
  // flips were stacking abandoned generations that queued MINUTES
  // ahead of the page the user actually stopped on. Debouncing to
  // after the flipping settles keeps the queue clean.
  useEffect(() => {
    if (!reader.translateOn) return;
    const t = window.setTimeout(() => {
      setTranslatePage(getVisiblePageText());
    }, 600);
    return () => window.clearTimeout(t);
  }, [reader.translateOn, currentPageCfi, getVisiblePageText]);

  // TX COLUMNS: a backward turn out of the first column lands on the
  // PREVIOUS page, whose translated text may itself be several columns
  // long. Jump to its last column so paging back is the exact inverse of
  // paging forward — otherwise the reader would skip everything after the
  // previous page's first column. Runs once the new page's columns have
  // been measured.
  useEffect(() => {
    if (!pendingTxToEndRef.current) return;
    const id = window.setTimeout(() => {
      if (!pendingTxToEndRef.current) return;
      pendingTxToEndRef.current = false;
      txNavRef.current?.toEnd();
    }, 260);
    return () => window.clearTimeout(id);
  }, [translatePage.cfi]);

  // Translation look-ahead loop + status caption pinned at the bottom
  // of the overlay. Always sets SOMETHING when translateOn is true so
  // the user can see whether the system is working. States:
  //   - "Building page index…" — book.locations not yet generated
  //   - "Translating this page…" — current page fetch in flight
  //   - "Pre-translating page +N…" — look-ahead is working
  //   - "Ready · N pages ahead cached" — buffer full
  // "Pages" are approximated by book.locations CFI percentages because
  // epub.js doesn't expose future page boundaries directly; per-CFI
  // cache keys may not hit perfectly when the user actually flips, but
  // the Gemma KV cache stays warm and wall-clock for the visible-page
  // fetch shrinks.
  useEffect(() => {
    if (!reader.translateOn || !bookId) {
      setAheadStatus("");
      return;
    }
    if (!translatePage.text || !translatePage.cfi) {
      setAheadStatus("Translating this page…");
      return;
    }
    aheadAbortRef.current?.abort();
    const ctrl = new AbortController();
    aheadAbortRef.current = ctrl;
    const book = bookRef.current;
    const view = getVisibleView();
    if (!book || !view) {
      setAheadStatus("Loading…");
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const locations = (book as any).locations;
    if (!locations || !locations.length || locations.length < 1) {
      setAheadStatus("Building page index…");
      return;
    }
    const total = locations.length;
    let currentPct = 0;
    let pageSpanPct = 1 / total;  // fallback to "1 location" if we
                                  // can't measure the current visible page
    try {
      currentPct = locations.percentageFromCfi(translatePage.cfi) ?? 0;
      // Measure the size of the CURRENT visible page in percentage-
      // of-book units. This is the right step for the look-ahead —
      // moving forward by N pages, not by N locations. (epub.js'
      // locations index typically packs ~5 entries per rendered page,
      // so stepping by 1/total per "page" was actually stepping by
      // 1/5 of a page → look-ahead pre-translated ~150-char fragments
      // instead of full pages.) 2026-06-29 fix.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rendition = renditionRef.current as any;
      const loc = rendition?.currentLocation?.();
      if (loc?.start?.cfi && loc?.end?.cfi) {
        const startPct = locations.percentageFromCfi(loc.start.cfi);
        const endPct = locations.percentageFromCfi(loc.end.cfi);
        if (typeof startPct === "number" && typeof endPct === "number") {
          const span = endPct - startPct;
          if (span > 0) {
            // Use the measured page span. Cap at 1/20 of the book
            // so an unusually large or page (a chapter intro etc.)
            // doesn't stretch the look-ahead beyond reason.
            pageSpanPct = Math.min(0.05, span);
          }
        }
      }
    } catch {
      return;
    }
    // Build the next-40-pages CFI/text snapshots BEFORE going async,
    // so a relocate during the long fetches can't shift the targets
    // out from under us. Step by `pageSpanPct` rather than `1/total`
    // so each look-ahead target really IS the next rendered page.
    type AheadSpec = { idx: number; cfi: string; text: string };
    const targets: AheadSpec[] = [];
    const BUFFER = 40;
    for (let i = 1; i <= BUFFER; i++) {
      const startPct = currentPct + i * pageSpanPct;
      const endPct = currentPct + (i + 1) * pageSpanPct;
      if (startPct >= 1) break;
      let startC: string;
      let endC: string;
      try {
        startC = locations.cfiFromPercentage(startPct);
        endC = locations.cfiFromPercentage(Math.min(endPct, 0.9999));
      } catch {
        continue;
      }
      if (!startC || !endC) continue;
      try {
        const sR = new EpubCFI(startC).toRange(view.document);
        const eR = new EpubCFI(endC).toRange(view.document);
        if (!sR || !eR) continue;
        const range = view.document.createRange();
        range.setStart(sR.startContainer, sR.startOffset);
        range.setEnd(eR.endContainer, eR.endOffset);
        const text = range.toString().replace(/\s+/g, " ").trim();
        if (text.length < 50) continue;
        targets.push({ idx: i, cfi: startC, text });
      } catch {
        /* CFI lives in another section — skip */
      }
    }
    // The rest of the work — cross-section top-up + Promise.all over
    // the targets — must be async because section.load() returns a
    // Promise. useEffect callbacks can't be async themselves, so the
    // whole tail goes in an IIFE.
    void (async () => {
      // Cross-section top-up — if in-section look-ahead came up short
      // (we're near the end of the current chapter), load the next
      // spine section(s) and queue their opening text as additional
      // targets. Without this, the buffer stalls at "end of section"
      // exactly when the user is about to flip into a new chapter —
      // the worst time for the audio to stutter. Cache key under those
      // synthetic CFIs doesn't perfectly align with what the panel
      // will use when the user actually lands there, but Gemma stays
      // warm and the queue keeps moving, which is what the
      // 2026-06-27 brief asked for.
      if (targets.length < BUFFER) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const spine = (book as any).spine;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const loc2 = (renditionRef.current as any)?.currentLocation?.();
          const curIdx: number = loc2?.start?.index ?? 0;
          const items: unknown[] = spine?.items ?? spine?.spineItems ?? [];
          let added = 0;
          const CHUNK_CHARS = 2000;
          for (
            let s = 1;
            targets.length < BUFFER && curIdx + s < items.length && added < BUFFER;
            s++
          ) {
            if (ctrl.signal.aborted) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sec: any = items[curIdx + s];
            if (!sec) break;
            try {
              if (typeof sec.load === "function") {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await sec.load((book as any).load?.bind(book));
              }
              const doc = sec.contents || sec.document;
              const body =
                (doc && (doc.body || doc.documentElement)) || null;
              if (!body) continue;
              const sectionText = String(body.textContent || "")
                .replace(/\s+/g, " ")
                .trim();
              if (sectionText.length < 50) continue;
              const baseCfi: string =
                (typeof sec.cfiFromElement === "function"
                  ? sec.cfiFromElement(body)
                  : null) || `${sec.href || sec.idref || `s${curIdx + s}`}`;
              for (
                let c = 0;
                c < sectionText.length && targets.length < BUFFER;
                c += CHUNK_CHARS
              ) {
                const chunk = sectionText.slice(c, c + CHUNK_CHARS).trim();
                if (chunk.length < 50) continue;
                targets.push({
                  idx: targets.length + 1,
                  cfi: `${baseCfi}::chunk:${c}`,
                  text: chunk,
                });
                added += 1;
              }
            } catch (err) {
              console.warn(
                "[pv-translate] failed to load next section for look-ahead",
                err,
              );
            }
          }
        } catch (err) {
          console.warn("[pv-translate] cross-section look-ahead bailed", err);
        }
      }
      if (ctrl.signal.aborted) return;
      if (targets.length === 0) {
        setAheadStatus("Ready");
        return;
      }
      // Fire every uncached look-ahead request in parallel. Local
      // Gemma serializes server-side, but launching everything at once
      // lets the server process them back-to-back with zero round-trip
      // overhead AND the counter ticks up live as each lands.
      let cachedCount = 0;
      const TOTAL = targets.length;
      const recomputeStatus = () => {
        if (ctrl.signal.aborted) return;
        if (cachedCount >= TOTAL) {
          setAheadStatus(`Ready · ${cachedCount} ahead`);
        } else {
          setAheadStatus(`Translating… ${cachedCount}/${TOTAL}`);
        }
      };
      // Seed with what's already in the cache from a prior pass. Pass
      // the TEXT so the text-hash lookup hits — synthetic look-ahead
      // CFIs from prior passes won't match by CFI but their text will.
      for (const target of targets) {
        if (
          readTranslationCache(bookId, target.cfi, reader.translateLang, target.text) !== null
        ) {
          cachedCount += 1;
        }
      }
      recomputeStatus();
      // SEQUENTIAL look-ahead (2026-07-11). The old Promise.all fired
      // every target at once — fine when llama-server serialized
      // internally, but with --parallel 4 the burst occupies every slot
      // and QUEUES ahead of the visible page's own requests, so the
      // user stares at a blank overlay while future pages translate.
      // One target at a time keeps the buffer filling while leaving
      // slots free; fetchTranslation additionally yields to the visible
      // page between chunks (fgIdle gate in TranslatePanel).
      for (const target of targets) {
        if (ctrl.signal.aborted) return;
        if (
          readTranslationCache(bookId, target.cfi, reader.translateLang, target.text) !== null
        ) {
          continue;
        }
        try {
          await fetchTranslation(
            bookId,
            target.cfi,
            target.text,
            reader.translateLang,
            ctrl.signal,
          );
          if (ctrl.signal.aborted) return;
          cachedCount += 1;
          recomputeStatus();
        } catch (err) {
          if ((err as { name?: string })?.name === "AbortError") return;
          console.warn("[pv-translate] look-ahead fetch failed", err);
        }
      }
    })();
    return () => {
      ctrl.abort();
    };
  }, [
    bookId,
    reader.translateOn,
    reader.translateLang,
    translatePage.cfi,
    translatePage.text,
    getVisibleView,
  ]);

  // Forward ref to startTranslationTTS (defined below) so the restart
  // path — captured once by the boot-time relocated handler — always
  // invokes the LATEST loop instance rather than a stale closure.
  const startTranslationTTSRef = useRef<(() => Promise<void>) | null>(null);
  // Monotonic id per translation-loop run. A restart bumps it, which
  // makes any superseded loop bail out of its own epilogue instead of
  // clobbering the playing state of the loop that replaced it.
  const txLoopSeqRef = useRef(0);
  // Global playback generation. EVERY play loop (segment + translation)
  // captures this at start and bails when it changes. Fixes overlapping
  // voices: the segment loop only checked playingRef, so a translation
  // loop starting during a mode handoff could re-set playingRef=true
  // and unintentionally revive the old loop mid-await — two voices at
  // once. (2026-07-11)
  const playGenRef = useRef(0);
  // Character index into the translated page currently being spoken —
  // drives word highlighting inside TranslatePanel.
  const [txSpeechIndex, setTxSpeechIndex] = useState<number | null>(null);
  const txSpeechThrottleRef = useRef(0);

  const restartTTSFromCurrentLocation = useCallback(async () => {
    // Translate mode: the visible "page" is the overlay's translated
    // text, not the iframe DOM — restarting the segment pipeline here
    // would speak the ORIGINAL language over the English overlay (the
    // manual-page-flip-while-playing path). Kick the translation loop
    // instead. translateOn is read through the store, not the closure,
    // because this callback instance is captured at boot. (2026-07-10)
    if (useReaderStore.getState().reader.translateOn) {
      activeProviderRef.current.stop();
      playingRef.current = false;
      // Let the old loop's await release and exit before starting anew.
      await new Promise((r) => setTimeout(r, 200));
      // Wait for the debounced translatePage snapshot (600ms settle) to
      // catch up with the flip. Without this the new loop reads the
      // PREVIOUS page's text from the ref, finds its cached translation,
      // and re-speaks the old page over the new page's overlay — the
      // "next/prev breaks play voice" bug. (2026-07-11)
      for (let i = 0; i < 25; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loc = (renditionRef.current as any)?.currentLocation?.();
        const curCfi: string | undefined = loc?.start?.cfi;
        if (curCfi && translatePageRef.current.cfi === curCfi) break;
        await new Promise((r) => setTimeout(r, 100));
      }
      void startTranslationTTSRef.current?.();
      return;
    }
    activeProviderRef.current.stop();
    clearAllHighlights();
    // Reset the trim cache so the next play computes a fresh start position
    // for the new visible page.
    firstSegmentRef.current = { idx: -1, segId: "", charOffset: 0 };
    // Give epub.js a tick to mount the new section + relocate firing.
    await new Promise((r) => setTimeout(r, 120));
    // Tear down the prior play loop cleanly before kicking off a new one.
    playingRef.current = false;
    await new Promise((r) => setTimeout(r, 50));
    rebuildSegmentsForCurrentSection();
    // PROBE the new visible page so we resume at the paragraph the reader
    // is actually looking at — NOT at segment 0 (start of chapter). This
    // fixes the "voice reads chapter twice" bug: previously, paging
    // forward while playing restarted playback from the chapter top.
    const { idx, charOffset } = probeTopOfVisiblePage();
    firstSegmentRef.current = {
      idx,
      segId: segmentsRef.current[idx]?.id ?? "",
      charOffset,
    };
    void startTTSFromIndex(idx);
  }, [
    clearAllHighlights,
    probeTopOfVisiblePage,
    rebuildSegmentsForCurrentSection,
    startTTSFromIndex,
  ]);

  /** Translation-mode read-aloud loop. Used when reader.translateOn is
   *  true — Play reads the TRANSLATED text of the visible page in the
   *  user's chosen target language, not the book's original. Each
   *  page's translation comes from the same localStorage cache the
   *  TranslatePanel writes; if it isn't there yet we briefly wait for
   *  the panel to drop a fresh one in before speaking. After speaking
   *  the page we auto-advance to the next page and repeat — the
   *  rendition flip triggers the panel to fetch the next translation,
   *  which we then speak. */
  const startTranslationTTS = useCallback(async () => {
    if (!bookId) return;
    const loopId = ++txLoopSeqRef.current;
    const myGen = ++playGenRef.current;
    // Hard-stop every provider before claiming playback: a segment-mode
    // loop or an older translation loop may still have audio rolling —
    // this is the "extra voices all at once" bug. stopAllProviders()
    // also releases their awaits so those loops exit. (2026-07-11)
    stopAllProviders();
    playingRef.current = true;
    pausedRef.current = false;
    setPlaying(true);
    setPaused(false);
    const iOwnPlayback = () =>
      playingRef.current &&
      txLoopSeqRef.current === loopId &&
      playGenRef.current === myGen;
    // 3-2-1 countdown, same contract as the segment loop: renders only
    // if audio takes >400ms, holds at 1 while the page is still being
    // translated/synthesized. The fixed z-40 countdown layer sits above
    // the z-30 TranslatePanel overlay. Previously the translation loop
    // never armed this at all — that's why "3..2..1 didn't load".
    const clearCountdown = () => {
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdownNum(null);
    };
    const armCountdown = () => {
      audioStartedRef.current = false;
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      const showAt = performance.now() + 400;
      // Must outlive the translation wait below (150s) — if the countdown
      // dies first the user sees a dead screen while the page translates.
      const hardTimeoutAt = performance.now() + 160000;
      countdownIntervalRef.current = window.setInterval(() => {
        if (audioStartedRef.current || !playingRef.current) {
          clearCountdown();
          return;
        }
        const now = performance.now();
        if (now < showAt) return;
        if (now >= hardTimeoutAt) {
          clearCountdown();
          return;
        }
        const elapsed = (now - showAt) / 1000;
        setCountdownNum(Math.max(1, Math.ceil(3 - elapsed)));
      }, 100);
    };
    const showNotice = (msg: string) => {
      setTtsNotice(msg);
      if (ttsNoticeTimerRef.current !== null) {
        window.clearTimeout(ttsNoticeTimerRef.current);
      }
      ttsNoticeTimerRef.current = window.setTimeout(
        () => setTtsNotice(null),
        5000,
      );
    };
    // Normalizes + truncates exactly like TranslatePanel's requestText
    // so the text-hash cache key matches what the panel wrote. Without
    // the slice(0, 7000), a very long page would hash differently here
    // than at write time and the lookup would never hit.
    const sourceOf = (raw: string): string =>
      (raw || "").replace(/\s+/g, " ").trim().slice(0, 7000);
    while (iOwnPlayback()) {
      if (pausedRef.current) {
        await new Promise((r) => setTimeout(r, 150));
        continue;
      }
      // Current visible page — via ref, NOT closure state. The loop
      // outlives every page flip; closure state froze at Play time,
      // which is why the old loop re-read page 1's translation after
      // auto-advancing (stale text hashed to the old cache entry).
      const cfi = translatePageRef.current.cfi;
      const source = sourceOf(translatePageRef.current.text);
      // Arm the countdown for this page — covers both the wait for the
      // translation to land AND the voice synthesis latency.
      armCountdown();
      // One durable-cache probe first — Dexie survives reloads even
      // when the localStorage L1 was evicted, and a hit backfills L1.
      let text = "";
      try {
        text =
          (await readTranslationCacheAsync(
            bookId, cfi, reader.translateLang, source,
          )) || "";
      } catch {
        /* fall through to polling */
      }
      // Then poll the sync cache while the TranslatePanel's own fetch
      // is in flight. A LIVE Gemma page takes ~50s under contention, so
      // the tail is 150s — the old 30s tail expired mid-translation and
      // playback stopped as if the book had ended. Read the ref fresh
      // each tick — the page can move under us. Every ~5s also re-probe
      // the durable (Dexie) cache with the LIVE page: the look-ahead
      // writes there and the L1 entry may have been evicted meanwhile.
      if (!text) {
        for (let i = 0; i < 1500; i++) {
          if (!iOwnPlayback()) {
            clearCountdown();
            return;
          }
          if (pausedRef.current) {
            await new Promise((r) => setTimeout(r, 150));
            continue;
          }
          const live = translatePageRef.current;
          const cached = readTranslationCache(
            bookId, live.cfi, reader.translateLang, sourceOf(live.text),
          );
          if (cached) {
            text = cached;
            break;
          }
          if (i > 0 && i % 50 === 0) {
            try {
              const durable = await readTranslationCacheAsync(
                bookId, live.cfi, reader.translateLang, sourceOf(live.text),
              );
              if (durable) {
                text = durable;
                break;
              }
            } catch {
              /* keep polling the sync cache */
            }
          }
          await new Promise((r) => setTimeout(r, 100));
        }
      }
      if (!text) {
        // Per spec: stop cleanly when the page's translation isn't
        // ready, with a quiet retry affordance — no silent hang.
        console.warn("[pv-tts/tx] translation not ready, stopping loop");
        clearCountdown();
        showNotice("This page isn't translated yet — tap ▶ once it finishes.");
        break;
      }
      // Speak the whole page as one segment. Word-level highlighting
      // doesn't apply (the translation lives in the overlay layer, not
      // in the iframe DOM), so we keep this simple. TTS knobs are read
      // fresh from the store so a mid-session rate/voice change applies
      // from the next page onward.
      const ttsNow = useReaderStore.getState().tts;
      const seg = {
        id: `tx:${bookId}:${cfi || "_"}:${reader.translateLang}`,
        bookId,
        spineIndex: 0,
        href: "",
        text,
      };
      try {
        await activeProviderRef.current.speakSegment(seg, {
          rate: ttsNow.rate,
          pitch: ttsNow.mode === "classic" ? 0.95 : ttsNow.pitch,
          volume: ttsNow.volume,
          voice: undefined,
          serverVoice: ttsNow.serverVoice,
          onBoundary: (charIndex) => {
            if (!audioStartedRef.current) {
              audioStartedRef.current = true;
              setCountdownNum(null);
            }
            // Word highlighting in the TranslatePanel overlay —
            // throttled so the panel isn't re-rendered per character.
            const now = performance.now();
            if (now - txSpeechThrottleRef.current > 120) {
              txSpeechThrottleRef.current = now;
              setTxSpeechIndex(charIndex);
            }
          },
        });
      } catch (err) {
        console.warn("[pv-tts/tx] speak failed", err);
        clearCountdown();
        setTxSpeechIndex(null);
        showNotice("Couldn't load the voice — tap ▶ to try again.");
        break;
      }
      setTxSpeechIndex(null);
      if (!iOwnPlayback()) break;
      // Page done — auto-advance to the next page. The relocate handler
      // refreshes translatePage, the panel kicks off a fresh translation,
      // the next iteration of this loop waits for it and reads it.
      const prevCfi = translatePageRef.current.cfi;
      try {
        ttsRestartGuardRef.current = true;
        await renditionRef.current?.next();
      } catch {
        /* end of book — break */
        break;
      } finally {
        ttsRestartGuardRef.current = false;
      }
      // Wait for the relocate → translatePage refresh to actually land
      // before looping, otherwise the cache lookup would hit the OLD
      // page's translation and re-speak it. 30s: a slow relocate (big
      // section mount, busy main thread) can take well past the old 5s
      // cap, which made mid-book pages stop as if the book had ended.
      let moved = false;
      for (let i = 0; i < 300; i++) {
        if (!iOwnPlayback()) return;
        if (translatePageRef.current.cfi !== prevCfi) {
          moved = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 100));
      }
      if (!moved) {
        // Distinguish REAL end-of-book (next() was a no-op because
        // nothing follows this page) from "relocate never landed".
        // Only the former may end the session quietly.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loc = (renditionRef.current as any)?.currentLocation?.();
        const endPct = loc?.end?.percentage;
        const atEnd =
          !!loc?.atEnd || (typeof endPct === "number" && endPct >= 0.995);
        if (atEnd) {
          showNotice("End of book.");
        } else {
          console.warn("[pv-tts/tx] page never moved after next() — stopping");
          showNotice("Lost the page — tap ▶ to continue reading.");
        }
        break;
      }
    }
    // Only the loop that still OWNS playback clears the shared state —
    // a superseded loop (restart after a manual flip) must not stomp
    // on its replacement.
    if (txLoopSeqRef.current === loopId && playGenRef.current === myGen) {
      playingRef.current = false;
      setPlaying(false);
      setPaused(false);
      clearCountdown();
      setTxSpeechIndex(null);
    }
  }, [bookId, reader.translateLang]);
  // Keep the forward ref (used by restartTTSFromCurrentLocation above)
  // pointing at the freshest instance.
  startTranslationTTSRef.current = startTranslationTTS;

  // If the user toggles translation while TTS is already playing, the
  // running loop belongs to the OTHER mode — the spoken audio would no
  // longer match what's on screen. Hand playback over to the loop that
  // matches the new mode. (2026-07-10)
  const prevTranslateOnRef = useRef(reader.translateOn);
  useEffect(() => {
    const prev = prevTranslateOnRef.current;
    prevTranslateOnRef.current = reader.translateOn;
    if (prev === reader.translateOn) return; // not a toggle
    if (!playingRef.current) return; // idle — Play will pick the right mode
    if (reader.translateOn) {
      // Original-text loop is speaking — stop it and start the
      // translation loop once its await releases.
      stopAllProviders();
      playingRef.current = false;
      window.setTimeout(() => {
        void startTranslationTTSRef.current?.();
      }, 250);
    } else {
      // Translation loop is speaking — orphan it (seq bump makes it
      // bail without touching shared state) and restart the original-
      // text pipeline from the visible page. restartTTSFromCurrentLocation
      // re-reads translateOn (now false) so it takes the segment path.
      txLoopSeqRef.current += 1;
      void restartTTSFromCurrentLocation();
    }
  }, [reader.translateOn, restartTTSFromCurrentLocation]);

  const onPlay = useCallback(async () => {
    // iOS Safari only allows speechSynthesis to start from within a user
    // gesture. The async work below (await listVoices, etc) breaks out of
    // that gesture context, so we fire a synchronous one-character primer
    // utterance HERE — that "unlocks" the audio session for the rest of
    // the playback loop. Without this, the tap is registered but no sound
    // ever comes out on iPhone/iPad.
    try {
      if (
        typeof window !== "undefined" &&
        window.speechSynthesis
      ) {
        window.speechSynthesis.cancel();
        const primer = new SpeechSynthesisUtterance(" ");
        primer.volume = 0;
        window.speechSynthesis.speak(primer);
      }
    } catch {
      /* primer is best-effort */
    }

    // iOS Safari gates the server-TTS <audio> element on a real user gesture
    // too. Unlock the active provider's shared element HERE, synchronously,
    // before any await below breaks us out of the gesture — otherwise the
    // network fetch happens, play() is called outside a gesture, and Safari
    // silently refuses (the "audio stalls in Safari" report). Best-effort.
    try {
      if (tts.provider !== "browser") {
        activeProviderRef.current?.unlock?.();
      }
    } catch {
      /* unlock is best-effort */
    }

    // Branch: translation mode speaks the translated text instead of
    // the original. Skip the whole segment-build + probe pipeline.
    if (reader.translateOn) {
      // Double-tap guard: a second ▶ while already playing must not
      // spawn a second concurrent loop (overlapping voices).
      if (playingRef.current) return;
      void startTranslationTTS();
      return;
    }

    // Wait for the rendition to settle on a real location before reading.
    // On a fresh book open the user can tap Play before the relocated
    // event has updated currentLocation() — currentLocation() can still
    // be pointing at section 0 (often the nav/TOC) even though the page
    // they see is from a saved CFI farther in. Without this wait, TTS
    // builds segments from the TOC and reads chapter-title links instead
    // of the visible prose. 600ms cap so a healthy book never feels slow.
    {
      const r = renditionRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const okHref = (l: any) =>
        typeof l?.start?.href === "string" && l.start.href.length > 0;
      for (let i = 0; i < 12; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const loc = (r as any)?.currentLocation?.();
        if (okHref(loc)) break;
        await new Promise((res) => setTimeout(res, 50));
      }
    }

    // Always rebuild segments from the currently visible spine section right
    // before we read. epub.js pre-renders neighbouring sections, so the
    // segments cached during navigation could belong to a different chapter.
    rebuildSegmentsForCurrentSection();

    // Probe the top-left of the viewport to find the segment + char offset
    // at the top of the visible page.
    // `let` so the heading-skip block further down can step startIdx
    // forward when the picked segment is a non-prose heading.
    const probeResult = probeTopOfVisiblePage();
    let startIdx = probeResult.idx;
    let trimCharOffset = probeResult.charOffset;
    const probePathTaken = probeResult.path;
    const probeCfiSeen = probeResult.cfi;
    // Capture what the probe landed on BEFORE heading-skip moved us, so
    // the debug toast can distinguish "probe was off" from "skip was off."
    const preSkipSeg = segmentsRef.current[startIdx];
    const preSkipText =
      (preSkipSeg?.text || "").replace(/\s+/g, " ").trim().slice(0, 80);
    const preSkipIdx = startIdx;
    // How many headings the skip block walked past — surfaced in the
    // debug toast as "skip N" so when a book reads wrong we can tell
    // immediately whether the probe got close and skip walked too far,
    // or the probe itself landed in the wrong place.
    let skippedHeadings = 0;

    firstSegmentRef.current = {
      idx: startIdx,
      segId: segmentsRef.current[startIdx]?.id ?? "",
      charOffset: trimCharOffset,
    };

    // Kick Piper a head-start on the trimmed first segment right now so
    // there's no perceived load delay between the tap and audio. The
    // server provider de-dupes on segment id, so the speakSegment call
    // below picks up the in-flight blob.
    if (tts.provider !== "browser") {
      const seg = segmentsRef.current[startIdx];
      if (seg) {
        const speakSeg =
          trimCharOffset > 0
            ? { ...seg, text: seg.text.slice(trimCharOffset) }
            : seg;
        try {
          activeProviderRef.current.prefetch?.(speakSeg, {
            rate: tts.rate,
            pitch: tts.pitch,
            volume: tts.volume,
            serverVoice: tts.serverVoice,
          });
        } catch {
          /* prefetch is best-effort */
        }
      }
    }

    // Heading + chapter-list skip. The picked first segment can be
    // anything that survived the geometric filters but isn't actually
    // prose: a one-word chapter title like "Contents", a chapter-list
    // entry like "6 Maxine" / "7. Robert / 12", a numbered front-matter
    // section header, etc. Walk forward through subsequent segments
    // until we find one that looks like real prose (multiple words,
    // not all-numeric, contains a clause boundary, etc). Capped at 12
    // steps so a section that's nothing but headings can't loop.
    const looksLikeProse = (txt: string): boolean => {
      const t = (txt || "").trim();
      if (t.length < 30) return false;
      // Need at least 8 word-tokens AND at least one word >= 4 chars
      // long. Chapter-list entries usually have very few short tokens.
      const tokens = t.split(/\s+/).filter(Boolean);
      if (tokens.length < 8) return false;
      const hasMeatyWord = tokens.some(
        (w) => w.replace(/[^a-zA-Z]/g, "").length >= 4,
      );
      if (!hasMeatyWord) return false;
      // Need at least one in-sentence punctuation (period/comma/etc)
      // that isn't just trailing the last token.
      if (!/[.!?…,;:][\s)\]"”]/.test(t) && !/[.!?…]\s/.test(t)) {
        return false;
      }
      return true;
    };
    const isTOCLine = (txt: string): boolean => {
      const t = (txt || "").trim();
      // Length guard. Real TOC entries, chapter titles, and section
      // banners are short. Long text is prose — even if it happens to
      // start with a heading keyword. Without this guard the Henry Moore
      // book's `<p><strong>INTRODUCTION</strong> I made my first visit
      // to the British Museum in 1921…</p>` paragraph was being treated
      // as a TOC line because of the leading "INTRODUCTION", and skipped
      // past entirely (toast on 260625-1808: pre-skip[6] was that exact
      // paragraph, picked landed on the next chapter heading).
      if (t.length > 80) return false;
      // Front-matter / chapter title keywords.
      if (/^(contents?|table of contents?|index|chapter|part|book|epilogue|prologue|foreword|introduction|appendix|acknowledg)\b/i.test(t)) {
        return true;
      }
      // Chapter-list entry patterns: "6 Maxine", "6. Maxine", "Maxine 6",
      // "6. Maxine 12", "Chapter 6 — Maxine", "Six: Maxine", etc.
      if (/^\d+\.?\s+[A-Za-z][\w'’\- ]{0,40}$/.test(t)) return true;
      if (/^[A-Za-z][\w'’\- ]{0,40}\s+\d{1,4}$/.test(t)) return true;
      if (/^\d+\.?\s+[A-Za-z][\w'’\- ]{0,40}\s+\d{1,4}$/.test(t)) return true;
      // Pure numerals or roman numerals as a stand-alone "title".
      if (/^[ivxlcdm]{1,8}$/i.test(t)) return true;
      if (/^\d{1,4}$/.test(t)) return true;
      return false;
    };
    // Inline-heading prefix detection. When the kept paragraph starts
    // with a single heading word ("INTRODUCTION", "CHAPTER ONE", etc.)
    // followed by real prose, advance the trim-offset past the heading
    // word so TTS reads "I made my first visit..." instead of saying
    // "Introduction. I made my first visit...".
    //
    // Pattern: 1-3 ALL-CAPS or Title-Case heading words at the start,
    // followed by whitespace and then a sentence-starting capital
    // letter. Only fires on segments that are long prose (>120 chars)
    // so we never accidentally trim a real short heading down to empty.
    const inlineHeadingPrefixLen = (txt: string): number => {
      const t = txt || "";
      if (t.length < 120) return 0;
      const m = t.match(
        /^(contents?|table of contents?|index|chapter\s+(?:[ivxlcdm]+|\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)|part\s+(?:[ivxlcdm]+|\d+|one|two|three|four|five|six|seven|eight|nine|ten)|book\s+(?:[ivxlcdm]+|\d+|one|two|three)|epilogue|prologue|foreword|introduction|appendix|acknowledg(?:ments?|ements?))\s+(?=[A-Z])/i,
      );
      return m ? m[0].length : 0;
    };
    {
      // Step past three families of "don't read this aloud" segments at
      // the top of the visible page, in priority order:
      //
      //   1. Heading tags (h1-h6). Chapter titles, book titles, section
      //      banners — the screenshots from 260625-1051 showed all three
      //      failing books were on heading segments ("Henry Moore at The
      //      British Museum", "1 MOM TRIES TO GET BETWEEN ME AND MY
      //      CALVINS", "1Mrs. Maxine Hortence Simmons"). These are never
      //      prose, regardless of their text.
      //   2. TOC-style text patterns — chapter-list rows ("6 Maxine"),
      //      bare numerals, "Contents" / "Chapter N" keywords. Catches
      //      headings disguised as <p> (some EPUBs do this).
      //   3. (Intentionally not !looksLikeProse: short dialog like "He
      //      shrugged." would get skipped and TTS would start one
      //      paragraph past the visible top — the "Forward of the
      //      page" symptom from 260625-0707.)
      void looksLikeProse;
      const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6"]);
      let walked = 0;
      // Rule from 260625-2103 round of testing: "All books should start
      // at top of page. No headers." So heading-skip is on for every
      // probe path — h1-h6 and short TOC-style lines are stepped over
      // and TTS starts on the first non-header content. The length cap
      // inside isTOCLine (>80 chars = prose, not TOC) keeps legitimate
      // body paragraphs that happen to start with words like "Chapter"
      // or "Introduction" from being skipped. 10 steps is enough for a
      // realistic chapter front-matter pile-up (h1 title + h2 subtitle
      // + figure caption + first body), not enough to overshoot into
      // the next section.
      const skipCap = probePathTaken === "cfi" ? 10 : 30;
      while (
        walked < skipCap &&
        segmentsRef.current[startIdx] &&
        startIdx + 1 < segmentsRef.current.length
      ) {
        const cur = segmentsRef.current[startIdx];
        const tag = (cur.tag || "").toUpperCase();
        const isHeading = HEADING_TAGS.has(tag);
        const isTOC = isTOCLine(cur.text);
        if (!isHeading && !isTOC) break;
        startIdx += 1;
        trimCharOffset = 0;
        walked += 1;
      }
      skippedHeadings = walked;
      // Inline-heading-prefix trim. After skip lands, if the kept
      // paragraph still starts with an inline heading word — e.g.
      // "INTRODUCTION I made my first visit..." in the Henry Moore
      // book where the `<strong>INTRODUCTION</strong>` is inline with
      // the body prose — advance trim past it. Without this the "no
      // headers" rule would still surface the heading word in the TTS
      // opening. Skipped when trim>0 (CFI already positioned us inside
      // a straddling paragraph; its trim is more specific than ours).
      if (trimCharOffset === 0) {
        const landed = segmentsRef.current[startIdx];
        if (landed) {
          const prefixLen = inlineHeadingPrefixLen(landed.text);
          if (prefixLen > 0) {
            trimCharOffset = prefixLen;
          }
        }
      }
      firstSegmentRef.current = {
        idx: startIdx,
        segId: segmentsRef.current[startIdx]?.id ?? "",
        charOffset: trimCharOffset,
      };
    }

    // Surface what we're ACTUALLY about to read so it's visible on-device
    // without DevTools. Stays on screen for ~6s then fades. If it shows
    // chapter content but the speaker reads something else, the bug is
    // downstream; if it shows TOC text, the bug is in section selection.
    try {
      const seg = segmentsRef.current[startIdx];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const loc = (renditionRef.current as any)?.currentLocation?.();
      const href: string | undefined = loc?.start?.href;
      const fullText = (seg?.text || "").replace(/\s+/g, " ").trim();
      const picked = fullText.slice(0, 120);
      const pickedTag = (seg?.tag || "").toUpperCase() || undefined;
      const trimmed = trimCharOffset > 0
        ? fullText.slice(trimCharOffset).replace(/^\s+/, "")
        : fullText;
      const spoken = trimmed.slice(0, 120);
      setTtsDebugInfo({
        href,
        pickedTag,
        skipped: skippedHeadings,
        picked,
        trim: trimCharOffset,
        spoken,
        idx: startIdx,
        path: probePathTaken,
        cfi: probeCfiSeen,
        preSkipIdx,
        preSkipText: skippedHeadings > 0 ? preSkipText : undefined,
      });
      console.log(
        "[pv-tts] section=%s path=%s cfi=%s tag=%s skipped=%d idx=%d trim=%d picked=%s spoken=%s",
        href ?? "(no href)",
        probePathTaken,
        probeCfiSeen ?? "(no cfi)",
        pickedTag ?? "?",
        skippedHeadings,
        startIdx,
        trimCharOffset,
        picked.slice(0, 60),
        spoken.slice(0, 60),
      );
      // Stay on screen 20s — long enough that you can press Play, watch
      // the page advance, AND pull up the screen-capture gesture before
      // it fades. Earlier 9s was too tight.
      setTimeout(() => setTtsDebugInfo((cur) =>
        cur && cur.spoken === spoken ? null : cur,
      ), 20000);
    } catch {
      /* debug snippet is best-effort */
    }

    void startTTSFromIndex(startIdx);
  }, [
    probeTopOfVisiblePage,
    rebuildSegmentsForCurrentSection,
    startTTSFromIndex,
    // translateOn + the translation loop were MISSING here (2026-07-10
    // fix): onPlay was created while translateOn was still false (it's
    // reset on every book open), and nothing in the old dep list changed
    // when the user toggled translation on — so the closure kept seeing
    // translateOn=false and Play read the ORIGINAL text instead of the
    // translation. Listing both keeps the branch live.
    reader.translateOn,
    startTranslationTTS,
    tts.pitch,
    tts.provider,
    tts.rate,
    tts.serverVoice,
    tts.volume,
  ]);

  const onPause = useCallback(() => {
    if (!playingRef.current) return;
    pausedRef.current = true;
    setPaused(true);
    // Pause via the ref so a mid-session engine switch routes to the
    // engine that's actually playing right now, not the one captured at
    // first render.
    activeProviderRef.current.pause();
    // Mark the moment of pause so the session-recap watcher can decide
    // when >5 minutes of inactivity means "session over".
    lastPausedAtRef.current = Date.now();
  }, []);
  // Keep the forward-ref used by the sleep-timer effect in sync.
  onPauseRef.current = onPause;

  const onResume = useCallback(() => {
    if (!playingRef.current) return;
    // ── CLEAR THE PAUSE MARKER (2026-10-07) ──────────────────────────
    // Thomas: "something called session recap came up during TTS".
    //
    // onPause stamped lastPausedAtRef and NOTHING ever cleared it. The
    // watcher then fired a recap five minutes after that stamp —
    // regardless of the fact that he had resumed and was listening. So a
    // pause early in a session produced a modal in the middle of a
    // paragraph later on. Resuming means the session did not end.
    lastPausedAtRef.current = null;
    pausedRef.current = false;
    setPaused(false);
    activeProviderRef.current.resume();
  }, []);

  const onStop = useCallback(() => {
    playingRef.current = false;
    pausedRef.current = false;
    // Invalidate every in-flight play loop, both modes.
    playGenRef.current += 1;
    txLoopSeqRef.current += 1;
    setPlaying(false);
    setPaused(false);
    setTxSpeechIndex(null);
    // Kill ALL provider singletons, not just the one we think is active.
    // If the user switched engines mid-session or any callback closure is
    // holding a stale activeProvider, the stale one could still be
    // playing in the background. stopAllProviders() short-circuits the
    // outstanding speakSegment Promise via the resolveActive hook so the
    // play loop's await releases cleanly — that was the loop leak that
    // showed up as "audio doesn't stop" 2026-06-26.
    stopAllProviders();
    clearAllHighlights();
    firstSegmentRef.current = { idx: -1, segId: "", charOffset: 0 };
    // Tear down the loading countdown if it's still on screen.
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdownNum(null);
  }, [clearAllHighlights]);

  const onPrev = useCallback(() => {
    // Translate mode has no segment index — prev/next are page flips.
    // While playing, the relocated handler restarts the translation
    // loop on the new page (see restartTTSFromCurrentLocation).
    if (!playingRef.current || useReaderStore.getState().reader.translateOn) {
      slidePageTurn("prev");
      return;
    }
    activeProviderRef.current.stop();
    currentIdxRef.current = Math.max(0, currentIdxRef.current - 1);
    void startTTSFromIndex(currentIdxRef.current);
  }, [startTTSFromIndex, slidePageTurn]);

  const onNext = useCallback(() => {
    if (!playingRef.current || useReaderStore.getState().reader.translateOn) {
      slidePageTurn("next");
      return;
    }
    activeProviderRef.current.stop();
    if (
      currentIdxRef.current >= segmentsRef.current.length - 1
    ) {
      ttsRestartGuardRef.current = true;
      void renditionRef.current?.next().then(() => {
        ttsRestartGuardRef.current = false;
        setTimeout(() => {
          currentIdxRef.current = 0;
          void startTTSFromIndex(0);
        }, 250);
      });
    } else {
      currentIdxRef.current++;
      void startTTSFromIndex(currentIdxRef.current);
    }
  }, [startTTSFromIndex, slidePageTurn]);

  // 2026-10-06 — Last-gasp position flush for Android / BOOX.
  //
  // The reader already writes on every relocate, and those writes do land
  // (the lib server's access log shows PUT /lib/position firing constantly
  // through a reading session). What it had no handler for was the moment
  // the app goes away: Android freezes or kills a backgrounded page without
  // firing `unload`, and usually without `beforeunload` either. The events
  // that DO fire are `visibilitychange` → hidden and `pagehide`, so those
  // are what we listen for.
  //
  // flushPositionSync() uses only localStorage — synchronous, committed
  // before the handler returns — precisely because an awaited Dexie put
  // that is still pending when the process is frozen never completes.
  useEffect(() => {
    const flush = () => {
      const p = lastGoodPosRef.current;
      if (!p) return;
      flushPositionSync(bookShaRef.current, p);
    };
    const onVis = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", flush);
    // Also flush when the reader unmounts — which is what actually happens
    // on "home and back to book", since that is a route change inside the
    // SPA rather than the page going away.
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // This same function is registered twice (document-capture AND
      // window-bubble, see the note at the bottom of this effect). A single
      // press would otherwise run it twice and turn two pages, because
      // preventDefault() does not stop propagation. Stamp the event object
      // the first time we see it and ignore it on the second pass.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seen = e as any;
      if (seen.__pvKeyHandled) return;
      seen.__pvKeyHandled = true;
      // ── KEY TELEMETRY (2026-10-06) ────────────────────────────────────
      // Thomas: the BOOX's external page buttons draw a focus box, which
      // means SOMETHING in the web layer is reacting to them — but we have
      // never established what they actually emit. An Onyx WebView may send
      // key "", code "PageUp", keyCode 33, an arrow, or something else
      // entirely depending on model and firmware, and pageTurnFromKeyEvent
      // can only match what it is told about.
      //
      // So report the raw event from his device instead of asking him to
      // run the key-test screen (which he never did). Capped per mount.
      // `dir` is what pageTurnFromKeyEvent made of it — if a key arrives
      // and dir is empty, that is the whole bug in one line.
      if (keyDiagCountRef.current < 30) {
        keyDiagCountRef.current += 1;
        try {
          const t = e.target as HTMLElement | null;
          const q = new URLSearchParams({
            ev: "key",
            src: "outer",
            key: String(e.key || ""),
            code: String(e.code || ""),
            kc: String(e.keyCode || 0),
            dir: String(
              pageTurnFromKeyEvent(
                e,
                useReaderStore.getState().reader.buttonOrder,
              ) || "",
            ),
            order: String(useReaderStore.getState().reader.buttonOrder || ""),
            tgt: String(t?.tagName || "?"),
            tgtLabel: String(t?.getAttribute?.("aria-label") || "").slice(0, 24),
            phase: String(e.eventPhase),
            rep: e.repeat ? "1" : "0",
            ua: navigator.userAgent.slice(0, 32),
          });
          void fetch(`/__pvdiag/key?${q.toString()}`, {
            method: "GET",
            cache: "no-store",
          }).catch(() => {});
        } catch {
          /* diagnostics never break a read */
        }
      }
      if (
        (e.target as HTMLElement | null)?.tagName === "INPUT" ||
        (e.target as HTMLElement | null)?.tagName === "TEXTAREA" ||
        (e.target as HTMLElement | null)?.isContentEditable
      )
        return;
      // Physical page buttons + arrows — order-aware, single source.
      // pageTurnFromKeyEvent reads key, code AND keyCode, because an Onyx
      // WebView does not reliably populate `key` (see its comment block).
      const pdir = pageTurnFromKeyEvent(
        e, useReaderStore.getState().reader.buttonOrder);
      if (pdir) {
        e.preventDefault();
        wakeChromeRef.current?.();
        slidePageTurn(pdir);
        return;
      }
      switch (e.key) {
        case " ":
          e.preventDefault();
          if (!playingRef.current) onPlay();
          else if (pausedRef.current) onResume();
          else onPause();
          break;
        case "Escape":
          onStop();
          break;
        case "ArrowRight":
          slidePageTurn("next");
          break;
        case "ArrowLeft":
          slidePageTurn("prev");
          break;
        case "]":
          onNext();
          break;
        case "[":
          onPrev();
          break;
        case "+":
        case "=":
          useReaderStore
            .getState()
            .setReader({ fontSize: Math.min(28, reader.fontSize + 1) });
          break;
        case "-":
          useReaderStore
            .getState()
            .setReader({ fontSize: Math.max(12, reader.fontSize - 1) });
          break;
      }
    };
    // 2026-10-06: also bind on the outer document in CAPTURE phase.
    // `window` only sees a keydown that bubbles all the way up undisturbed.
    // On the BOOX, the focused element after a tap is often a button in the
    // bottom bar, and a focused <button> treats some keys as its own — so
    // the event can be consumed before it reaches window. Capture on
    // document runs before any of that. onKey is idempotent (it either
    // handles the key and returns, or falls through), so receiving the same
    // event twice is harmless; the capture pass handles it and
    // preventDefault + the natural ordering stop the second pass mattering.
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [onPause, onPlay, onResume, onStop, onPrev, onNext, reader.fontSize, slidePageTurn]);

  // The active theme class is applied at the <html> level by App.tsx, so the
  // reader container only needs theme-aware foreground/background via vars.

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="text-lg font-medium">This book didn&apos;t open.</div>
        <div className="text-sm text-neutral-500">{error}</div>
        {/* Deliberately NOT offering the /read/ foliate route here: tested
            2026-09-28, it renders an empty shell for every book including
            ones epub.js opens fine, so it's still milestone-1 WIP. A button
            to a second blank screen is worse than no button. */}
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={() => navigate("/library")}
            className="rounded bg-neutral-900 px-3 py-1.5 text-sm text-white"
          >
            Back to library
          </button>
        </div>
        <div className="pv-fg-soft mt-1 max-w-xs text-xs">
          If it still won&apos;t open, re-import the file from the library —
          the copy on this device may be damaged.
        </div>
      </div>
    );
  }

  return (
    <div className="pv-fg relative flex h-full flex-col">
      {chromeVisible ? (
        <ReaderToolbar
          title={bookTitle}
          chapter={chapterLabel}
          hidden={barHidden}
          playing={playing}
          paused={paused}
          onPlay={onPlay}
          onPause={onPause}
          onResume={onResume}
        />
      ) : (
        // No-chrome mode: an Apple-Books-style minimal header — a small,
        // centered, grey, uppercase chapter title (truncated with an
        // ellipsis if long). Also carries the iOS notch / status-bar
        // safe-area inset so the first line of text never sits under the
        // dynamic island. The reading area below claims the rest.
        // 2026-09-29, "Text needs to fill screen": this running head is
        // chrome too, and it was the last thing holding vertical space once
        // the bottom bar collapsed — 28px of a 812px viewport. It now
        // collapses with the rest, leaving ONLY the safe-area inset behind
        // so the first line still can't slide under the notch or Dynamic
        // Island. On hardware with no inset that reclaims the full 28px.
        <div
          style={{
            backgroundColor: "var(--pv-bg)",
            paddingTop: barsVisible
              ? "calc(env(safe-area-inset-top, 0px) + 6px)"
              : "env(safe-area-inset-top, 0px)",
            paddingBottom: barsVisible ? "5px" : 0,
            paddingLeft: "16px",
            paddingRight: "16px",
            flexShrink: 0,
            height: barsVisible ? "auto" : 0,
            overflow: "hidden",
          }}
          aria-hidden={!barsVisible}
        >
          <div
            className="pv-fg-soft mx-auto truncate text-center"
            style={{
              fontSize: "11px",
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              maxWidth: "82%",
            }}
          >
            {/* 2026-10-05, from the BOOX ("Remove title too"): the book
                title was the FALLBACK here, so on any page without a
                chapter label — which includes the front matter of most
                Gutenberg books, where he starts — the running head read
                the book's name on every page. He knows what he opened.
                Chapter only now; when there is no chapter we render an
                empty head rather than falling back to the title. */}
            {(chapterLabel || "").trim()}
          </div>
        </div>
      )}

      {/* ── TOP-RIGHT BOOKMARK (2026-10-06) ───────────────────────────────
          Thomas: "Maybe bookmark button in upper right. Only visible when
          button bar is up."

          ABSOLUTELY POSITIONED, deliberately. It is a sibling of the
          viewer's flex parent but takes itself out of flow, so it reserves
          no space and the stage box is byte-identical with it present or
          absent. Given the whole zero-height-stage saga came out of this
          exact layout chain, nothing new goes INTO the chain.

          Visibility is tied to barOpen, so full-screen reading is
          untouched — it appears and disappears with the bar, which is what
          he asked for and also means it costs nothing while reading.

          State is shown by FILL, not by colour alone: bookmarked paints the
          accent background (orange on colour screens, solid black/white on
          the e-ink themes, same as the active bar tiles), unbookmarked is
          an outline. Readable at 16 grey levels without relying on hue.

          56x56 target in a corner that is thumb-awkward on a 13" panel, and
          z-40 to match the bar it belongs to. It sits above the page-flip
          strips (z-30), but those already stop below the top toolbar, so
          there is no overlap to resolve. */}
      {barOpen && (
        <button
          type="button"
          onClick={() => void toggleBookmarkOnThisPage()}
          aria-pressed={isPageBookmarked}
          aria-label={
            isPageBookmarked
              ? "Remove bookmark from this page"
              : "Bookmark this page"
          }
          title={
            isPageBookmarked ? "Remove bookmark" : "Bookmark this page"
          }
          className="pv-chrome-btn absolute z-40 flex items-center justify-center"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 8px)",
            right: "calc(env(safe-area-inset-right, 0px) + 10px)",
            // 2026-10-06, Thomas: "No box around bookmark button." Same
            // treatment as the carets — glyph only. The 56x56 TOUCH target
            // is unchanged; the plate, border and background are gone.
            //
            // State is now carried entirely by the glyph: filled ribbon when
            // the page is bookmarked, outline when not. That is a shape
            // difference rather than a colour one, so it survives a
            // greyscale panel without the fill the box used to provide.
            width: "56px",
            height: "56px",
            background: "none",
            border: "none",
            padding: 0,
            color: "var(--pv-fg)",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <IconBookmarkRibbon filled={isPageBookmarked} />
        </button>
      )}

      <div className="relative flex-1 min-h-0">
        <div ref={viewerRef} className="absolute inset-0 select-text" />
      </div>

      {/* ── CARET STRIP (2026-10-07) ──────────────────────────────────────
          Thomas, several messages back: the text area should end ABOVE the
          caret, with the caret living in its own reserved line, nothing
          overlapping.

          Until now the reveal caret was an absolute overlay sitting on top
          of the last line of text. This is a real row in the flex column
          instead, so the viewer's box genuinely ends above it.

          CRITICAL — WHY IT IS ALWAYS RENDERED, never conditional on
          barOpen: reserving the strip changes the stage box, and changing
          the stage box after the book has rendered is a repagination.
          That is the hazard that produced the zero-height stage and, in a
          different guise, dumped him from mid-chapter to the Contents page
          when the Android bar appeared. A strip that appeared and vanished
          with the bar would resize the stage on every toggle — exactly the
          thing the overlay bar was designed to avoid.

          So the strip is in the layout from the FIRST measure and keeps a
          constant height for the whole session. When the bar is closed it
          holds the caret; when the bar is open the bar overlays it (the
          bar is absolute, bottom-0) and the strip simply sits underneath,
          unchanged. The stage box is therefore identical in both states
          and NO_REPAGINATION is preserved by construction. */}
      <div
        className="relative shrink-0"
        style={{
          height: "56px",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        {!barOpen && (
          <button
            type="button"
            aria-label="Show controls"
            aria-expanded={false}
            onClick={() => setBarOpen(true)}
            // 2026-10-07: positioned inside the reserved caret strip rather
            // than overlaying the last line of text. Still absolute so it can
            // be centred without affecting the strip's height, but anchored
            // to the strip (bottom: 0 of a 56px row) instead of to the
            // viewer — so the text genuinely ends above it.
            className="pv-chrome-btn absolute bottom-0 left-1/2 z-40 flex items-center justify-center"
            style={{
              transform: "translateX(-50%)",
              // The TOUCH target is the full 96x56 — a thumb or stylus on a
              // 6–13" panel needs that, and it is deliberately decoupled
              // from the paint so the glyph can shrink without shrinking the
              // hit area. 2026-10-06: the painted plate that used to sit in
              // here (36px, opaque, bordered) is gone at Thomas's request;
              // the span below paints the glyph only. Keep this transparent.
              // Historical note: the plate was 36px rather than 56px because
              // at 56px it clipped the descenders of the last line of text —
              // verified at 768x600 on Monte Cristo, cutting the tail of the
              // "g" in "Morgiou". With no plate there is nothing to clip,
              // but the 24px glyph row below preserves the same clearance.
              width: "96px",
              height: "56px",
              background: "transparent",
              border: "none",
              padding: 0,
              color: "var(--pv-fg)",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {/* 2026-10-06, Thomas: "Make menu carrot smaller and no box
                around it." So: no plate, no border, no background — the
                glyph alone. The 96x56 TOUCH target on the button above is
                unchanged; only the paint shrinks, which is the whole point
                of keeping the two separate.

                The glyph goes 32px -> 20px and keeps strokeWidth 3, so the
                stroke is proportionally HEAVIER than before, not lighter.
                That is deliberate: with the opaque plate gone the caret now
                sits directly over the last line of text, and on a 16-level
                greyscale panel a thin stroke over prose is the one thing
                that would make it vanish.

                Still no transition, no opacity, no auto-hide: this is the
                only affordance that gets the bar back, and the 2026-10-05
                failure was exactly a reveal the user could not find. */}
            <span
              className="flex w-full items-center justify-center"
              style={{ height: "24px", background: "none", border: "none" }}
            >
              <ChevronUpGlyph size={20} />
            </span>
          </button>
        )}
      </div>

      {/* Page-flip tap zones — thin invisible strips on the LEFT and RIGHT
          edges of the reading area. Vertically constrained so they sit
          between the top toolbar and the bottom bar, never overlapping
          either. Suppressed entirely when an overlay panel is open OR when
          the user has switched to Highlight mode — the latter frees the
          whole viewport for text selection. */}
      {!tocOpen && !settingsOpen && !notesOpen && !searchOpen && !marksOpen && (
        <>
          <PageFlipZone
            side="left"
            bottomInset={flipZoneBottomInset}
            onFlip={() => {
              slidePageTurn("prev");
            }}
          />
          <PageFlipZone
            side="right"
            bottomInset={flipZoneBottomInset}
            onFlip={() => {
              slidePageTurn("next");
            }}
          />
        </>
      )}

      {/* Loading countdown — centred 3-2-1 overlay shown when TTS audio
          takes >400ms to start. Disappears the moment the first audio
          boundary fires, so fast loads briefly flash nothing. The dark
          backplate matches the soft-tint hover so it doesn't war with
          the reader theme. */}
      {countdownNum !== null && (
        <div
          aria-live="polite"
          aria-label={`Loading voice in ${countdownNum} seconds`}
          className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center"
        >
          <div
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "min(28vw, 220px)",
              fontWeight: 700,
              lineHeight: 1,
              color: "rgba(240, 214, 139, 0.95)",
              textShadow: "0 4px 24px rgba(0, 0, 0, 0.55)",
              opacity: 0.9,
              transition: "opacity 200ms ease-out",
            }}
          >
            {countdownNum}
          </div>
        </div>
      )}

      {/* Quiet TTS notice — small, transient, non-blocking. */}
      {ttsNotice && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute left-1/2 z-30 -translate-x-1/2 rounded-full border px-4 py-2 text-sm shadow-lg"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
            backgroundColor: "rgba(20, 20, 22, 0.92)",
            color: "#f0ece0",
            borderColor: "rgba(212, 175, 55, 0.4)",
            maxWidth: "90%",
          }}
        >
          {ttsNotice}
        </div>
      )}

      {/* TTS debug snippet — shows what the probe picked, how many chars
          we trimmed off the front, and what TTS is actually about to
          speak. Visible for ~9s after Play. If "Picked" looks right but
          "Spoken" looks wrong, the trim math is off; if "Picked" itself
          is TOC/wrong, the probe is wrong. Retire once start-at-top is
          rock solid. */}
      {TTS_DEBUG_ENABLED && ttsDebugInfo && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute left-2 right-2 z-30 rounded-md border px-3 py-2 text-[11px] shadow-lg"
          style={{
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
            backgroundColor: "rgba(20, 20, 22, 0.92)",
            color: "#f0ece0",
            borderColor: "rgba(212, 175, 55, 0.4)",
          }}
        >
          <div style={{ color: "#d4af37", fontWeight: 600 }}>
            ▶ {ttsDebugInfo.href || "(unknown section)"}
            {ttsDebugInfo.path && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· {ttsDebugInfo.path}
              </span>
            )}
            {ttsDebugInfo.pickedTag && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· {ttsDebugInfo.pickedTag}
              </span>
            )}
            {typeof ttsDebugInfo.idx === "number" && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· idx {ttsDebugInfo.idx}
              </span>
            )}
            {ttsDebugInfo.skipped > 0 && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· skip {ttsDebugInfo.skipped}
              </span>
            )}
            {ttsDebugInfo.trim > 0 && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· trim {ttsDebugInfo.trim}
              </span>
            )}
            {typeof ttsDebugInfo.wrapCount === "number" && (
              <span style={{ opacity: 0.7, fontWeight: 400 }}>
                {" "}· wrap {ttsDebugInfo.wrapCount}
                {ttsDebugInfo.boundaryFired ? " (bnd)" : " (timer)"}
              </span>
            )}
          </div>
          {ttsDebugInfo.cfi && (
            <div style={{ marginTop: 2, opacity: 0.5, fontSize: 10, fontFamily: "monospace" }}>
              cfi: ...{ttsDebugInfo.cfi.slice(-50)}
            </div>
          )}
          {ttsDebugInfo.preSkipText && (
            <div style={{ marginTop: 2, opacity: 0.55, fontFamily: "Georgia, serif", fontStyle: "italic" }}>
              Pre-skip[{ttsDebugInfo.preSkipIdx}]: {ttsDebugInfo.preSkipText}
            </div>
          )}
          <div style={{ marginTop: 2, opacity: 0.7, fontFamily: "Georgia, serif" }}>
            Picked: {ttsDebugInfo.picked || "(empty)"}
          </div>
          <div style={{ marginTop: 2, opacity: 1, fontFamily: "Georgia, serif" }}>
            Spoken: {ttsDebugInfo.spoken || "(empty)"}
          </div>
        </div>
      )}

      {/* Bottom bar. Holds, left to right: Back (to library) · Theme ·
            Bookmark · Contents · Search · Fonts · Play. Thin progress strip
            above the icons.
            2026-09-29: retracts with the rest of the chrome after 4s idle
            (it used to be unconditionally visible, which is why the
            interface never disappeared).
            Revised same day, per "when interface hides text needs to get
            longer": the bar now COLLAPSES out of the flex flow rather than
            merely translating out of view, so the reading column claims the
            space and shows more lines. That reflow is deliberate; the effect
            above repaginates once and restores the captured CFI. Height is
            switched instantly, never transitioned — animating it would
            repaginate on every frame. */}
      {/* ── REVEAL CARET (2026-10-06) ────────────────────────────────────
          "Have hide menu carrot and show menu carrot for bottom bar."

          Pinned to the bottom edge and ALWAYS on screen while the bar is
          closed. This is the one affordance that must never be clever:
            - it does not fade, auto-hide, or wait for an interaction
            - it is NOT inside the epub.js iframe, so it receives taps
              directly. The 2026-10-05 failure was a reveal that depended
              on a tap reaching the outer window; taps land in the iframe
              and never arrive, which is how Thomas was left unable to
              turn a page at all.
            - z-40 puts it above the z-30 page-flip strips, so a thumb at
              the bottom centre reveals the bar instead of flipping.
          56px tall and 96px wide: a stylus/thumb target on a 6–13" panel,
          not a mouse target. No transition — e-ink does not animate. */}

      {/* ── BOTTOM BAR — OVERLAY, NOT IN FLOW (2026-10-06) ───────────────
          Absolutely positioned and translated off-screen when closed.

          WHY AN OVERLAY: the previous bar collapsed its height out of the
          flex flow, which changed the viewer's measured size and so forced
          rendition.resize() — a full repagination. On e-ink that is slow,
          and it tears down and rebuilds the epub.js iframe. Because it is
          absolute, toggling barOpen changes nothing about the viewer's box,
          so the text never reflows and the reader's place cannot be lost by
          showing the controls. It covers the last few lines while open,
          which is the accepted trade.

          Note this element no longer reads barsVisible at all, and barOpen
          is NOT a dependency of the chrome-resize effect above — that is
          what keeps the reveal reflow-free. */}
      <div
        ref={barRef}
        className="absolute inset-x-0 bottom-0 z-40 flex flex-col items-stretch gap-1 px-3"
        style={{
          backgroundColor: "var(--pv-bg)",
          // 2026-10-06, Thomas on build 1337: "No big lind." This was it —
          // a 2px full-bleed rule in the foreground colour running the
          // whole width of the panel across the top of the bar, and the
          // only border in this file. At 16 grey levels a full-width
          // high-contrast rule reads as a heavy line, which is exactly
          // what he is describing. Removed.
          //
          // NOT replaced with a thinner rule (he said no line, not a
          // smaller one) and NOT replaced with a shadow — shadows dither
          // badly on a greyscale e-ink panel, which is why this was a hard
          // border in the first place. The bar's own opaque background and
          // its controls are what separate it from the page now. If that
          // turns out to be too little separation on the device, the fix
          // is more padding, not the line back.
          paddingTop: 0,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
          // Instant, never animated: an e-ink panel cannot render a
          // transition legibly, and animating it would also repaint the
          // overlay dozens of times for nothing.
          transform: barOpen ? "translateY(0)" : "translateY(110%)",
          pointerEvents: barOpen ? "auto" : "none",
        }}
        aria-hidden={!barOpen}
      >
        {/* HIDE CARET — full-width row at the top of the bar so it is the
            easiest thing to hit when the bar is open. The ROW stays 48px
            and full width: that is the touch target, and 2026-10-06 Thomas
            was explicit that the bar and its targets stay large ("Keep
            menubar large"). Only the PAINT shrinks.

            Glyph matched to the reveal caret: 20px, strokeWidth 3, same
            weight, pointing the other way. They are one control in two
            states and should read as the same object. No plate, no
            background, no border — the row has never had one, and the
            full-width rule that used to sit just above it is gone. */}
        <button
          type="button"
          aria-label="Hide controls"
          aria-expanded
          onClick={() => setBarOpen(false)}
          className="pv-chrome-btn flex w-full items-center justify-center"
          style={{
            height: "48px",
            color: "var(--pv-fg)",
            background: "none",
            border: "none",
            padding: 0,
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <ChevronDownGlyph size={20} />
        </button>

        {/* ── PANEL SLOT (2026-10-06) ──────────────────────────────────
            Thomas: "Make settings appear on top of progress and carrot
            (progress not shown. Carrot moves up). Make it match the button
            bar style. Same for search and bookmarks."

            So a panel is not a floating popover — it is PART OF THE BAR
            ASSEMBLY. It renders here, inside the bar's flex column, in the
            place the progress row occupies, and the progress row is not
            rendered at all while it is open. The hide-caret sits above this
            slot and therefore rises with it, and the tile row stays below,
            so the whole thing grows upward as one object.

            Rendering it INSIDE the bar is also what makes "match the button
            bar style" true by construction rather than by copying values:
            it inherits the bar's background and sits inside the bar's
            padding, so there is nothing to keep in sync.

            NO REPAGINATION: the bar is `absolute inset-x-0 bottom-0`, an
            overlay. Growing it upward changes nothing about the viewer's
            box, which is the same property that makes showing the bar
            itself reflow-free. */}
        {anyPanelOpen ? (
          <div
            className="flex min-h-0 flex-col overflow-hidden"
            style={{ maxHeight: "min(52dvh, 460px)" }}
          >
            {settingsOpen && (
              <SettingsPanel
                inline
                onClose={() => setSettingsOpen(false)}
                onOpenChapters={() => openPanel("toc")}
              />
            )}
            {searchOpen && bookRef.current && (
              <SearchPanel
                inline
                book={bookRef.current}
                hrefLabels={tocHrefLabels(toc)}
                onClose={() => setSearchOpen(false)}
                onJumpTo={(cfi) => {
                  setSearchOpen(false);
                  renditionRef.current?.display(cfi);
                }}
              />
            )}
            {marksOpen && bookId && (
              <MarksPanel
                inline
                bookId={bookId}
                isPageBookmarked={isPageBookmarked}
                onAddBookmark={toggleBookmarkOnThisPage}
                onClose={() => setMarksOpen(false)}
                onJumpTo={(cfi) => {
                  setMarksOpen(false);
                  renditionRef.current?.display(cfi);
                }}
              />
            )}
            {tocOpen && (
              <TOCPanel
                inline
                items={toc}
                onClose={() => setTocOpen(false)}
                onSelect={(href) => {
                  setTocOpen(false);
                  void navigateToHref(href);
                }}
              />
            )}
          </div>
        ) : (
        <div className="flex flex-col gap-1">
          {/* The READING bar. The percent readout below was already gated
              on > 0, but this track was drawn unconditionally — so a book
              with no figure still showed an empty bar, which is the "0% on
              reading bars" half of Thomas's ask. No figure, no bar. */}
          {Math.round(progressPercent * 100) > 0 && (
            <div className="pv-progress-track h-1 w-full overflow-hidden rounded">
              <div
                className="pv-progress h-full"
                style={{ width: `${Math.round(progressPercent * 100)}%` }}
              />
            </div>
          )}
          {/* Time remaining (left) and progress percent (right). Both are
              now omitted entirely when there's no real figure, rather than
              shown as a fabricated one:
                - estimateRemaining returns null until the book's true word
                  count is known (it used to fall back to 80,000 words and
                  render an identical "5 hrs 20 mins" for every book)
                - the percent was wrapped in Math.max(1, …), so a book at
                  0% displayed "1%". Thomas: "Only show reading % if non
                  zero." */}
          <div className="pv-fg-soft flex items-center justify-between text-xs leading-tight">
            <span className="truncate">
              {(() => {
                const est = estimateRemaining({
                  progress: progressPercent,
                  wordCount: bookWordCount,
                  charCount: bookCharCount,
                  ttsRate: tts.rate,
                });
                if (!est) return null;
                const secs = playing ? est.listenSeconds : est.readSeconds;
                if (secs <= 0) return null;
                return (
                  <>
                    {formatTimeLeftLong(secs)}{" "}
                    {playing ? "left to hear" : "left in book"}
                  </>
                );
              })()}
            </span>
            {Math.round(progressPercent * 100) > 0 && (
              <span className="shrink-0 pl-2 tabular-nums">
                {Math.round(progressPercent * 100)}%
              </span>
            )}
          </div>
        </div>
        )}

        {/* Tile row — ALWAYS rendered, panel open or not. It is the bottom
            of the assembly; the panel grows the bar upward above it. */}
        <div className="flex items-center justify-between gap-1.5 pt-1">
            {/* 2026-10-06, Thomas, verbatim: "move buttons around on bar>.
                Home bookmarks PLAY search, book/high SETTINGS" and "make
                PLAY/PAUSE larger/bolder, maybe with ( ) around it to
                hilight it".
                
                Order is now exactly his: Home, Bookmark-this-page, PLAY,
                Search, Marks, Settings. Play sits dead centre of six
                tiles, which is both what he asked for and the easiest
                place to hit without looking.

                Play is emphasised with a RING rather than literal
                parentheses. Brackets are two thin glyphs that have to
                survive font hinting and e-ink dithering at small sizes;
                a stroked circle is one solid shape that stays legible at
                16 grey levels, and it reads as a button rather than as
                punctuation next to the icon. Same idea, more robust on
                this panel.

                Every tile keeps flex-1 and py-4, so all six touch targets
                stay identical and generous — only the paint differs. */}
            <IconBtn
              label="Home"
              icon=""
              onClick={() => navigate("/library")}
            >
              <IconHome />
            </IconBtn>
            {/* Marks — bookmarks AND highlights, list plus the add/remove
                action. The separate bookmark tile that used to sit here is
                gone: five tiles, and this panel now carries both halves of
                the job (see toggleBookmarkOnThisPage below). */}
            <IconBtn
              label="Bookmarks and highlights"
              icon=""
              onClick={() => openPanel(marksOpen ? null : "marks")}
              active={marksOpen}
            >
              <IconMarksList />
            </IconBtn>
            {/* PLAY — centre tile, emphasised. Gated on
                canReadAloud(provider), not isTTSSupported(): server TTS
                needs no Web Speech API, and gating on it hid the button
                entirely on webviews without SpeechSynthesis. */}
            {canReadAloud(tts.provider) && (
              <IconBtn
                label={!playing ? "Read aloud" : paused ? "Resume" : "Pause"}
                icon=""
                emphasis
                onClick={!playing ? onPlay : paused ? onResume : onPause}
              >
                <PlayPauseGlyph showPause={playing && !paused} size={30} />
              </IconBtn>
            )}
            <IconBtn
              label="Search"
              icon=""
              onClick={() => openPanel(searchOpen ? null : "search")}
              active={searchOpen}
            >
              <IconSearch />
            </IconBtn>
            <IconBtn
              label="Settings"
              icon=""
              onClick={() => openPanel(settingsOpen ? null : "settings")}
              active={settingsOpen}
            >
              <IconSettings />
            </IconBtn>
        </div>
      </div>

      {/* (The play button when barHidden lives INSIDE the bottom strip
          now — see the barHidden branch above. No floating button.) */}

      {/* Selection menu: pops over the EPUB iframe when the user makes a
          text selection. Tap a color to highlight, dismiss by tapping
          outside or another selection. */}
      {/* SelectionMenu popover retired 2026-06-26. wireSelectionMenu now
          auto-saves the highlight with the last-used color on selection
          release — no popover, no second tap. The component is kept in
          the file (defined below) in case we need a re-color affordance
          later, but it's not rendered. */}

      {/* TOCPanel / SettingsPanel / SearchPanel / MarksPanel are no longer
          rendered here as floating overlays. They render INSIDE the bottom
          bar's panel slot (see "PANEL SLOT" above), which is what makes
          them displace the progress row and match the bar's style. */}
      {notesOpen && (
        <NotesPanel
          bookId={bookId}
          bookTitle={bookTitle || "Book"}
          onClose={() => setNotesOpen(false)}
          onOpenInsights={() => { setNotesOpen(false); setInsightsOpen(true); }}
          onJumpTo={(cfi) => {
            setNotesOpen(false);
            renditionRef.current?.display(cfi);
          }}
        />
      )}
      {insightsOpen && (
        <BookInsights
          book={bookRef.current}
          bookId={bookId}
          bookTitle={bookTitle || "Book"}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          bookAuthor={(bookRef.current as any)?.packaging?.metadata?.creator}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          language={(bookRef.current as any)?.packaging?.metadata?.language}
          onClose={() => setInsightsOpen(false)}
        />
      )}
      {reader.translateOn && bookId && translatePage.text.length > 0 && (
        <TranslatePanel
          bookId={bookId}
          pageText={translatePage.text}
          pageParas={translatePage.paras}
          pageCfi={translatePage.cfi}
          speechCharIndex={playing ? txSpeechIndex : null}
          navRef={txNavRef}
          fontFamily={reader.fontFamily}
          fontSize={reader.fontSize}
          lineHeight={reader.lineHeight}
          theme={reader.theme}
          lang={reader.translateLang}
          margin={reader.margin}
          aheadStatus={aheadStatus}
          // Reserve room at the bottom for the reader's own button bar
          // (5 tiles, ≈84 px including safe-area padding). The overlay
          // covers ONLY the reading column; the bar stays visible so
          // Play / Settings / Search remain reachable.
          bottomBarHeight={84}
          onTranslated={(cfi, text) => {
            setCurrentTranslation({ cfi, text });
          }}
        />
      )}
      {/* Held back while the voice is running. The recap is worth having —
          66 of them exist and surfacing them as a reading diary is on the
          roadmap — so this defers rather than discards: the text stays in
          state and the modal appears once playback stops. */}
      {recapText && !(playing && !paused) && (
        <RecapModal
          text={recapText}
          onClose={() => setRecapText(null)}
        />
      )}
      {/* Same rule for the "Summarising…" pill. */}
      {recapBusy && !(playing && !paused) && (
        <div className="pv-fg-soft fixed left-1/2 top-3 z-40 -translate-x-1/2 rounded-full border border-white/20 bg-black/70 px-3 py-1 text-xs">
          Summarising your session…
        </div>
      )}
    </div>
  );
}

/** Session-end recap modal. Small card with the Gemma summary + Copy /
 *  Share buttons. Matches the dark palette (standing design rule). */
function RecapModal({
  text,
  onClose,
}: {
  text: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked in some browsers */ }
  };
  const doShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ text, title: "PageVoice recap" });
      } else {
        await doCopy();
      }
    } catch { /* user cancelled */ }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div
        className="pv-surface pv-border pv-fg max-w-md rounded-lg border p-5 shadow-2xl"
        style={{ background: "#141414", color: "#d8d8d8" }}
      >
        <div className="mb-3 text-sm font-semibold" style={{ color: "#f7931a" }}>
          Session recap
        </div>
        <div className="mb-4 text-sm leading-relaxed">{text}</div>
        <div className="flex justify-end gap-2 text-sm">
          <button
            onClick={doCopy}
            className="pv-border rounded border px-3 py-1 hover:opacity-90"
          >
            {copied ? "Copied" : "Copy"}
          </button>
          <button
            onClick={doShare}
            className="pv-border rounded border px-3 py-1 hover:opacity-90"
          >
            Share
          </button>
          <button
            onClick={onClose}
            className="rounded px-3 py-1 hover:opacity-90"
            style={{ background: "#f7931a", color: "#0a0a0a" }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Visible page-flip chip — a small floating button at the side of the
 * viewport, like the FAB. Both pointerup and click are wired up because iOS
 * Safari sometimes drops the synthetic click after the user has focused the
 * EPUB iframe. Rendered via createPortal to document.body so re-renders of
 * the parent ReaderView (e.g. on every progress update) don't bounce the
 * button DOM and lose iOS's pointer attachment.
 *
 * epub.js's own rendition.on("click") also handles taps inside the iframe
 * content (left third = prev, right third = next), so navigation never
 * depends solely on the chip.
 */
/**
 * Page-flip tap zone — a THIN invisible strip down the left or right edge
 * of the reading area. Covers from below the top toolbar to above the
 * bottom bar so it never intercepts taps on the controls. The strip is
 * intentionally narrow (~14% of viewport, capped at 80px) so the middle
 * 70%+ of the page stays free for text selection / highlighting. The
 * top toolbar and bottom strip handle themselves; everything inside the
 * reading area is either prev-page (left half) or next-page (right half).
 */
/** Caret glyphs for the bottom-bar show/hide controls. Drawn as SVG with a
 *  thick stroke rather than a text character: a "⌃" renders at wildly
 *  different weights across fonts and all but vanishes on a greyscale
 *  e-ink panel. 32px with a 3px stroke stays legible at 16 grey levels. */
/** Marks icon — a bookmark ribbon over list lines. Thick strokes so it
 *  reads at 16 grey levels on the BOOX. */
function IconMarksList() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6h8M4 11h8M4 16h5" />
      <path d="M16 3h5v12l-2.5-2L16 15z" />
    </svg>
  );
}

/** Bookmark ribbon for the top-right toggle. Filled when the page is
 *  bookmarked, outline when not — so the state is carried by the SHAPE as
 *  well as by the button's background fill, and survives a greyscale panel
 *  even if the accent colour reads as a mid-tone. */
function IconBookmarkRibbon({ filled }: { filled: boolean }) {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3h12v18l-6-5-6 5z" />
    </svg>
  );
}

function ChevronUpGlyph({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="5 15 12 8 19 15" />
    </svg>
  );
}

function ChevronDownGlyph({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="5 9 12 16 19 9" />
    </svg>
  );
}

function PageFlipZone({
  side,
  onFlip,
  bottomInset,
}: {
  side: "left" | "right";
  onFlip: () => void;
  /** CSS length for the strip's bottom edge. Passed in because the amount
   *  of reserved space at the bottom now depends on whether the bar
   *  overlay is open (2026-10-06): with the bar closed the only thing down
   *  there is the 56px reveal caret, so the strip can run much further
   *  down and Thomas keeps nearly the whole page edge as a tap target.
   *  Hard-coding 104px for both states would have cost him a dead strip
   *  at the bottom of every full-screen page. */
  bottomInset: string;
}) {
  const lastFiredRef = useRef(0);
  // ── SWIPE-vs-TAP ON THE STRIP (2026-10-06) ────────────────────────────
  // The strips are fixed z-30 overlays 64px wide at each edge, and they
  // fired on pointerup with no movement test. A touch implicitly captures
  // the pointer to the element it started on, so a SWIPE that began inside
  // a strip released its pointerup on that strip and turned the page in the
  // STRIP's direction — regardless of which way the finger actually went.
  // Swipe left-to-right starting at the left edge, meaning "previous", and
  // you got "previous" by luck; start a right-to-left "next" swipe at the
  // left edge and you got "previous" instead. Intermittent and direction-
  // dependent, which is exactly "seems off".
  //
  // So: remember where the pointer went down, and if it moved more than
  // EDGE_TAP_SLOP_PX, this was a drag — stand aside and let the swipe
  // handler own it. A real tap moves a pixel or two and still fires, so the
  // thing Thomas has fallen back on does not change.
  const downRef = useRef<{ x: number; y: number } | null>(null);
  const onDown = (e: React.PointerEvent) => {
    downRef.current = { x: e.clientX, y: e.clientY };
    draggedRef.current = false;
  };
  /** Set when a release is judged a drag. Read by the click handler too:
   *  React fires pointerup THEN click for the same gesture, and the 200ms
   *  de-dupe below only runs on a fire that actually happened — so if
   *  pointerup bailed, click would sail straight through and reinstate the
   *  bug. This flag is what stops that. Cleared on the next pointerdown. */
  const draggedRef = useRef(false);
  const movedTooFar = (e: React.SyntheticEvent): boolean => {
    if (draggedRef.current) return true;
    const d = downRef.current;
    // Deliberately NOT clearing downRef here — click needs to see the same
    // start point pointerup did. pointerdown is what resets it.
    if (!d) return false;
    const pe = e as React.PointerEvent;
    if (typeof pe.clientX !== "number") return false;
    const moved =
      Math.abs(pe.clientX - d.x) > EDGE_TAP_SLOP_PX ||
      Math.abs(pe.clientY - d.y) > EDGE_TAP_SLOP_PX;
    if (moved) draggedRef.current = true;
    return moved;
  };
  const fire = (source: string) => (e: React.SyntheticEvent) => {
    if (movedTooFar(e)) {
      pvDebug(`edge-btn ${side} (${source}) ignored — drag, not tap`);
      return;
    }
    // Critical: do NOT flip the page if there's an active text selection.
    // Without this check, the user finishes a drag-select inside the iframe
    // but their finger lifts over the edge strip — the strip swallows the
    // touchend, fires a page-flip, and the highlight color popover never
    // gets to appear. Check selection on BOTH the parent doc and the
    // iframe doc; either having text selected means the user is selecting,
    // not flipping.
    try {
      const parentSel = window.getSelection?.()?.toString().trim() ?? "";
      const iframe = document.querySelector("iframe");
      const ifSel =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (iframe as any)?.contentWindow?.getSelection?.()?.toString().trim() ?? "";
      if (parentSel.length > 1 || ifSel.length > 1) {
        // Active selection — treat this gesture as part of the selection
        // workflow, not a page flip. Don't even preventDefault, in case
        // the click is what's about to focus the SelectionMenu.
        return;
      }
    } catch {
      /* selection probing is best-effort */
    }
    e.preventDefault?.();
    const now = performance.now();
    if (now - lastFiredRef.current < 200) return; // de-dupe pointerup+click
    lastFiredRef.current = now;
    pvDebug(`edge-btn ${side} (${source})`);
    try {
      onFlip();
    } catch (err) {
      console.warn(`[pagevoice] page flip (${source}) failed`, err);
    }
  };
  const sideClass = side === "left" ? "left-0" : "right-0";
  const zone = (
    <button
      type="button"
      aria-label={side === "left" ? "Previous page" : "Next page"}
      // 2026-10-06: non-focusable. The BOOX's external page keys were
      // focusing these strips and the browser painted a focus ring — a hard
      // black rectangle down the edge of the page on e-ink, which is the
      // "box appear on left or right" Thomas reported. Nothing should tab
      // to a page-turn strip: they are pointer/key targets, not controls a
      // keyboard user navigates between, and the keys are handled globally.
      tabIndex={-1}
      onPointerDown={onDown}
      onPointerUp={fire("pointerup")}
      onClick={fire("click")}
      // pv-edge-nav: marks this as a page-turn strip, NOT a control.
      // index.css uses it to keep the e-ink pressed-state invert off
      // these strips — see the 2026-10-05 BOOX note there.
      className={`pv-edge-nav fixed z-30 cursor-pointer bg-transparent ${sideClass}`}
      style={{
        touchAction: "manipulation",
        // Thin strip — 14vw wide, capped 56–80px — so the middle of the
        // reading area is free for text selection / highlighting. The
        // vertical range stops below the top bar (with safe-area-inset
        // for the iOS notch) and above the bottom bar plus its safe-area
        // inset, so neither row of controls can be intercepted by an
        // accidental edge tap.
        // A comfortable edge tap target for page turns, while still leaving
        // the center of the page free for the in-iframe gesture machine
        // (long-press highlight + finger-following swipe).
        // 2026-09-29, "Left right buttons slightly bigger": 52px -> 64px
        // (11vw -> 14vw). Deliberately a small step — at 375px that is
        // 52 -> 52.5px actual, i.e. the vw cap is what binds on a phone, so
        // the bump is mostly felt on wider screens while the phone target
        // grows only slightly. Still leaves the middle ~66% of a 375px
        // viewport free for text selection, and the vertical range is
        // unchanged so it cannot reach the header or the bottom bar.
        width: "min(64px, 14vw)",
        top: "calc(env(safe-area-inset-top, 0px) + 52px)",
        bottom: bottomInset,
        // No visible chrome at all — fully transparent. EPUB content shows
        // through. The browser tap-highlight is suppressed so the strip
        // doesn't flash blue on tap.
        WebkitTapHighlightColor: "transparent",
      }}
    />
  );
  return typeof document !== "undefined"
    ? createPortal(zone, document.body)
    : zone;
}

/**
 * Compact icon-only button used in the bottom bar. All buttons in the row
 * share flex:1 so they tile to even widths. Variants:
 *   primary  — filled with accent colour, used for Read aloud / Pause
 *   active   — filled with accent, used for Highlight-mode toggle and
 *              the open-state of Search
 *   disabled — dimmed, click suppressed (used by Stop while not playing)
 */
// ─── Bottom-bar monochrome icon set ────────────────────────────────────
// Inline SVGs that inherit color from `currentColor`. Replaces the
// previous emoji glyphs (🏠 🔎 🌐) which rendered in full color on iOS
// and competed with the reading content for attention. Design brief
// 2026-06-27: monochrome, easy to ignore, still recognizable. Stroke
// width 1.7 gives definition without shouting; 22px sits next to the
// existing 22-px bookmark + play/pause SVGs at the same visual weight.

const ICON_PROPS = {
  width: 28,
  height: 28,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function IconHome() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}

function IconContents() {
  return (
    <svg {...ICON_PROPS}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="11" cy="11" r="6" />
      <line x1="20" y1="20" x2="15.6" y2="15.6" />
    </svg>
  );
}

function IconTranslate() {
  // Globe outline — glyph chosen 2026-06-27. Monochrome
  // line art at 22 px reads as a clean "world / language" symbol
  // without the color-emoji noise the 🌐 codepoint used to introduce.
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 3 4 6 4 9s-1.5 6-4 9c-2.5-3-4-6-4-9s1.5-6 4-9z" />
    </svg>
  );
}

function IconSettings() {
  // Gear — central settings drawer. Replaces the standalone Fonts/Aa
  // tile and absorbs Theme, Search, and Chapters into a single
  // multi-section drawer (2026-06-27).
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

function IconFonts() {
  // Two-letter Aa as monochrome SVG so it sits alongside the others
  // without needing to swap text + SVG nodes per button.
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <text
        x="2"
        y="17"
        fontFamily='"Iowan Old Style", Georgia, serif'
        fontSize="13"
        fontWeight="700"
      >
        A
      </text>
      <text
        x="12"
        y="17"
        fontFamily='"Iowan Old Style", Georgia, serif'
        fontSize="10"
        fontWeight="500"
      >
        a
      </text>
    </svg>
  );
}

function IconBtn({
  label,
  icon,
  onClick,
  active,
  ariaPressed,
  disabled,
  emphasis,
  children,
}: {
  label: string;
  icon: string;
  onClick: () => void;
  active?: boolean;
  ariaPressed?: boolean;
  disabled?: boolean;
  /** Primary control of the bar — currently only Play/Pause. Paints a ring
   *  and full-strength foreground so it is unmistakable at a glance on a
   *  greyscale panel. Does NOT change the tile's size: every tile keeps
   *  flex-1 and py-4, so the touch targets stay identical and the bar stays
   *  as large as Thomas has twice asked it to be. */
  emphasis?: boolean;
  /** When provided, rendered instead of the `icon` string. Lets the bottom
   *  bar host non-text glyphs (mirrored ➜ for Back, the SVG play/pause
   *  triangle) without growing a parallel button component. */
  children?: React.ReactNode;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      aria-pressed={ariaPressed}
      title={label}
      disabled={disabled}
      // ACTIVE TILE = ACCENT (2026-10-06). Thomas: the chapters/bookmarks
      // and search buttons "Should be orange like settings". The orange is
      // --pv-accent (#f7931a on the dark theme) and .pv-accent-bg paints it
      // as a FILL with bg-coloured text, which is what Settings already
      // uses for its selected chips — so this reuses the existing value
      // rather than introducing a second one.
      //
      // It also reads correctly on his panel without depending on hue: the
      // e-ink themes remap --pv-accent to pure #000000 / #ffffff, so
      // accent-bg becomes a solid black-or-white block with inverted text.
      // Orange on a colour screen, maximum-contrast fill on e-ink, one
      // class. The previous active state was `text-white`, a colour change
      // only, which is the thing that would have been near-invisible.
      className={`pv-chrome-btn flex flex-1 items-center justify-center py-4 text-3xl leading-none transition-colors active:scale-90 ${
        emphasis
          ? "bg-transparent"
          : active
            ? "pv-accent-bg rounded-lg"
            : "bg-transparent text-neutral-500"
      } ${disabled ? "opacity-40" : ""}`}
      style={
        emphasis
          ? { color: "var(--pv-fg)" }
          : undefined
      }
    >
      {emphasis ? (
        // The ring. A stroked circle sized to the glyph, drawn in the
        // foreground colour — one solid shape rather than two thin bracket
        // glyphs, which is what survives dithering at 16 grey levels. The
        // tile around it is unchanged, so this is purely paint.
        <span
          className="flex items-center justify-center rounded-full"
          style={{
            width: "52px",
            height: "52px",
            border: "3px solid var(--pv-fg)",
            color: "var(--pv-fg)",
          }}
        >
          {children ?? <span aria-hidden>{icon}</span>}
        </span>
      ) : (
        children ?? <span aria-hidden>{icon}</span>
      )}
    </button>
  );
}

/**
 * Compact selection menu. Shows the user's CURRENT highlight colour as one
 * big swatch — one tap highlights with that colour. A small ▾ next to it
 * expands the full palette; picking a new colour both highlights and
 * remembers that colour as the new current. Same close ✕.
 *
 * Smaller surface than the old all-5-circles row → less visual noise over
 * the selected text, fewer mis-taps, more screen real estate for what the
 * user is actually reading.
 */
const LAST_COLOR_KEY = "pv-last-highlight-color";

/** Long-form remaining-time label for the reader footer, e.g.
 *  "5 hrs 24 mins" / "24 mins" / "< 1 min". Paired with "left in book". */
function formatTimeLeftLong(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const hu = h === 1 ? "hr" : "hrs";
  const mu = m === 1 ? "min" : "mins";
  if (h > 0) return `${h} ${hu} ${m} ${mu}`;
  if (m > 0) return `${m} ${mu}`;
  return "< 1 min";
}

function readLastColor(): HighlightColor {
  if (typeof window === "undefined") return "peach";
  const v = window.localStorage.getItem(LAST_COLOR_KEY) as HighlightColor | null;
  return v && HIGHLIGHT_COLORS.some((c) => c.id === v) ? v : "peach";
}

/**
 * Theme picker tile. Sits in the bottom bar where Play used to live —
 * these were swapped on 2026-06-14. Shows the current theme's
 * background colour as a small filled circle inside the tile. Tap to
 * open a popover that drops UP from the bar with the 4 themes; pick one
 * to apply + close.
 */
function ThemeBtn() {
  const theme = useReaderStore((s) => s.reader.theme);
  const setTheme = useReaderStore((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  const THEMES: { id: typeof theme; label: string; bg: string; fg: string }[] =
    [
      { id: "paper", label: "Day", bg: "#fdfcf8", fg: "#1b1712" },
      { id: "white", label: "White", bg: "#ffffff", fg: "#1a1a1a" },
      { id: "sepia", label: "Sepia", bg: "#f4ecd8", fg: "#5b4636" },
      { id: "gray", label: "Gray", bg: "#cfd2cd", fg: "#1a1a1a" },
      { id: "dark", label: "Dark", bg: "#0e0e10", fg: "#e8e6e1" },
    ];
  const current = THEMES.find((t) => t.id === theme) ?? THEMES[0];
  return (
    <div className="relative flex flex-1">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={`Theme — ${current.label}. Tap to change.`}
        title={`Theme: ${current.label}`}
        className="flex flex-1 items-center justify-center bg-transparent py-2.5 active:scale-90"
      >
        <span
          className="h-5 w-5 rounded-full border border-neutral-600"
          style={{ background: current.bg }}
          aria-hidden
        />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="pv-surface pv-border absolute bottom-full left-0 z-50 mb-2 flex items-center gap-2 rounded-full border px-2 py-1.5 shadow-2xl">
            {THEMES.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setTheme(t.id);
                  setOpen(false);
                }}
                title={t.label}
                aria-label={t.label}
                className={`h-7 w-7 rounded-full border active:scale-90 ${
                  t.id === theme
                    ? "ring-2 ring-offset-1 ring-white/80 ring-offset-transparent"
                    : "border-white/30"
                }`}
                style={{ background: t.bg, color: t.fg }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Highlighter tile on the bottom bar.
 *
 * One neutral pencil icon — no rainbow swatch in the bar, that read 'weird'.
 * Tap with text actively selected → applies the saved default colour
 * (yellow) to the selection right away. Subsequent picks of a different
 * colour come from the floating SelectionMenu only.
 *
 * The critical reliability fix: we capture the iframe selection range
 * AT THE MOMENT the tile is tapped, before any focus change can invalidate
 * it. The old picker computed the CFI inside an async chain that fired
 * after the popover opened, by which time some browsers had cleared the
 * range. Capture-on-tap → highlight applies every single time.
 */
/**
 * Bookmark tile on the bottom bar.
 *
 * Acts as a toggle: filled, accent-coloured 🔖 when the visible page is
 * already bookmarked; outlined / muted bookmark icon when it isn't. One
 * tap adds, one tap removes. No duplicates pile up.
 */
function BookmarkBtn({
  isOn,
  onToggle,
}: {
  isOn: boolean;
  onToggle: () => Promise<void>;
}) {
  const lastFiredRef = useRef(0);

  const fire = async (e: React.SyntheticEvent) => {
    const now = performance.now();
    // Dedupe touchstart+pointerdown that iOS fires for the same tap.
    if (now - lastFiredRef.current < 400) {
      e.preventDefault?.();
      return;
    }
    lastFiredRef.current = now;
    e.preventDefault?.();
    await onToggle();
  };

  return (
    <button
      onPointerDown={fire}
      onTouchStart={fire}
      aria-pressed={isOn}
      aria-label={isOn ? "Remove bookmark from this page" : "Bookmark this page"}
      title={isOn ? "Remove bookmark" : "Bookmark this page"}
      className={`flex flex-1 items-center justify-center bg-transparent py-2.5 text-2xl leading-none active:scale-90 ${
        isOn ? "" : "text-neutral-500"
      }`}
      style={{ color: isOn ? "var(--pv-accent, #d4af37)" : undefined }}
    >
      <span aria-hidden>
        {/* Outline bookmark when off, filled bookmark when on. The two
            SVG glyphs match in size so the toggle visually feels like
            a single button switching state, not two different icons. */}
        {isOn ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden>
            <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
          </svg>
        )}
      </span>
    </button>
  );
}

function HighlightBtn({
  captureSelection,
  applyHighlight,
}: {
  captureSelection: () => { cfi: string; text: string } | null;
  applyHighlight: (
    color: HighlightColor,
    capture: { cfi: string; text: string } | null
  ) => Promise<void>;
}) {
  const lastFiredRef = useRef(0);
  const fire = (e: React.SyntheticEvent) => {
    const now = performance.now();
    // Dedupe touchstart+mousedown that iOS fires for the same tap.
    if (now - lastFiredRef.current < 400) {
      e.preventDefault?.();
      return;
    }
    const capture = captureSelection();
    if (!capture) return;
    lastFiredRef.current = now;
    // preventDefault on pointerdown stops the button from focusing,
    // which in turn keeps the iframe's selection alive long enough
    // for applyHighlight to finish reading it.
    e.preventDefault?.();
    void applyHighlight(readLastColor(), capture);
  };
  return (
    <button
      onPointerDown={fire}
      onTouchStart={fire}
      aria-label="Highlight selection"
      title="Highlight selected text"
      className="flex flex-1 items-center justify-center bg-transparent py-2.5 text-2xl leading-none text-neutral-500 active:scale-90"
    >
      <span aria-hidden>✎</span>
    </button>
  );
}

function SelectionMenu({
  x,
  y,
  onHighlight,
  onDismiss,
}: {
  x: number;
  y: number;
  onHighlight: (c: HighlightColor) => void;
  onDismiss: () => void;
}) {
  const [current, setCurrent] = useState<HighlightColor>(() => readLastColor());
  const [expanded, setExpanded] = useState(false);
  const currentMeta =
    HIGHLIGHT_COLORS.find((c) => c.id === current) ?? HIGHLIGHT_COLORS[0];

  const apply = (color: HighlightColor) => {
    setCurrent(color);
    try {
      window.localStorage.setItem(LAST_COLOR_KEY, color);
    } catch {
      /* ignore */
    }
    onHighlight(color);
  };

  // Width depends on whether the palette is expanded.
  const W = expanded ? 220 : 100;
  const H = 44;
  const cx = Math.max(W / 2 + 8, Math.min(window.innerWidth - W / 2 - 8, x));
  const cy = Math.max(8, y - H - 12);

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        onClick={onDismiss}
        aria-hidden
      />
      <div
        className="pv-surface pv-border pv-fg fixed z-50 flex items-center gap-1 rounded-full border px-2 py-1 shadow-2xl"
        style={{ left: cx - W / 2, top: cy, width: W }}
        onClick={(e) => e.stopPropagation()}
      >
        {!expanded ? (
          <>
            <button
              aria-label={`Highlight ${currentMeta.label}`}
              title={`Highlight ${currentMeta.label}`}
              onClick={() => apply(current)}
              className="h-8 w-8 rounded-full border border-white/30 active:scale-90"
              style={{ background: currentMeta.hex }}
            />
            <button
              aria-label="Change highlight colour"
              title="Change colour"
              onClick={() => setExpanded(true)}
              className="pv-fg-soft px-1 text-sm leading-none"
            >
              ▾
            </button>
            <button
              aria-label="Cancel"
              onClick={onDismiss}
              className="pv-fg-soft ml-auto px-2 text-xs"
            >
              ✕
            </button>
          </>
        ) : (
          <>
            {HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.id}
                aria-label={`Highlight ${c.label}`}
                title={`Highlight ${c.label}`}
                onClick={() => apply(c.id)}
                className={`h-7 w-7 rounded-full border active:scale-90 ${
                  c.id === current
                    ? "ring-2 ring-offset-1 ring-white/80 ring-offset-transparent"
                    : "border-white/30"
                }`}
                style={{ background: c.hex }}
              />
            ))}
            <button
              aria-label="Cancel"
              onClick={onDismiss}
              className="pv-fg-soft px-2 text-xs"
            >
              ✕
            </button>
          </>
        )}
      </div>
    </>
  );
}

function tocHrefLabels(items: NavItem[]): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (list: NavItem[]) => {
    for (const it of list) {
      const norm = (it.href || "").split("#")[0];
      if (norm) out.set(norm, it.label.trim());
      if (it.subitems) walk(it.subitems);
    }
  };
  walk(items);
  return out;
}

function findInToc(items: NavItem[], href: string): NavItem | undefined {
  for (const it of items) {
    if (sameHref(it.href, href)) return it;
    if (it.subitems) {
      const child = findInToc(it.subitems, href);
      if (child) return child;
    }
  }
  return undefined;
}
function sameHref(a: string, b: string) {
  const norm = (s: string) => s.split("#")[0];
  return norm(a) === norm(b);
}
